import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { StateService } from '@uirouter/angular';
import * as _ from 'lodash';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { AlertAndNotificationsService } from 'src/app/core/services/alert-and-notifications.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { CompanyService } from 'src/app/core/services/company.service';
import { CompanyPageChangeCompanyStateModalService } from 'src/app/core/services/company-page-change-company-state-modal.service';
import { CompanyPageChooseJobBoardCredentialTypeModalService } from 'src/app/core/services/company-page-choose-job-board-credential-type-modal.service';
import { CompanyPageDeleteAssessmentCredentialModalService } from 'src/app/core/services/company-page-delete-assessment-credential-modal.service';
import { CompanyPageDeleteCompanyContactModalService } from 'src/app/core/services/company-page-delete-company-contact-modal.service';
import { CompanyPageDisassociateVendorsModalService } from 'src/app/core/services/company-page-disassociate-vendors-modal.service';
import { CompanyPageModifyPlanModalService } from 'src/app/core/services/company-page-modify-plan-modal.service';
import { CompanyPageWorkflowStepWarningModalService } from 'src/app/core/services/company-page-workflow-step-warning-modal.service';
import { DateTimeUtilityService } from 'src/app/core/services/date-time-utility.service';
import { GenericService } from 'src/app/core/services/generic.service';
import { GeocodingService } from 'src/app/core/services/geocoding.service';
import { PlanService } from 'src/app/core/services/plan.service';
import { PhoneNumberFormatPipe } from 'src/app/core/pipes/phone-number-format.pipe';
import { FourDotFiveDateFormatPipe } from 'src/app/core/pipes/four-dot-five-date-format.pipe';
import { ResumeService } from 'src/app/core/services/resume.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { UibModalService } from 'src/app/core/services/uibModalService';
import { UserRoleService } from 'src/app/core/services/user-role.service';
import { UserService } from 'src/app/core/services/user.service';
import { MESSAGECONSTANTS } from 'src/app/core/utils/constants';
import { RunCandidateScore } from 'src/app/core/models/runCandidateScore';

declare var $: any;

// Company-management API URL constants, inlined from the legacy
// company-page.module.js's `COMPANYMANAGEMENTCONSTANTS` AngularJS `.constant()`
// (22b-4d step 10) - that legacy constant is only reachable from AngularJS DI,
// so the same URL strings are duplicated here as a plain TS const rather than
// re-wired through the upgrade/downgrade bridge for a value that never
// changes at runtime. The legacy module's copy is removed in step 11 once
// nothing else references it.
const COMPANYMANAGEMENTCONSTANTS = {
    CONTROLLER: {
        ASSIGN_CLIENT_TO_STAFFING_COMPANY: 'api/company/assignclientorbu',
        GET_INDUSTRY_SUBLIST: 'api/company/getindustrysublist/',
        GET_INDUSTRY_GROUPS: 'api/company/getindustrylist',
        GET_4DOT5_ADMINS: 'api/company/get4dot5admins',
        GET_ENTITY_SCOPES: 'api/company/getentityscope',
        GET_COMPANY_TYPE_DETAILS: 'api/company/getcompanytype',
        GET_ASSESSMENT_WORK_FLOW: 'api/licensepref/getlicensepreferences/',
        GET_ASSESSMENT_WORK_FLOW_FOR_CLIENT_BU: 'api/licensepref/getclientorbulicensepreferences/',
        SET_LICENCE_PREFERENCES: 'api/licensepref/setlicensepreferences',
        SET_LICENCE_PREFERENCES_FOR_CLIENT_BU: 'api/licensepref/setclientorbulicensepreferences',
        GET_COMPANY: 'api/company/getcompanydetails',
        SAVE_COMPANY: 'api/company/savecompany',
        UPDATE_COMPANY_FOR_FOURDOTFIVE_ADMIN: 'api/user/updatecompanyfor4dot5admin',
        CHANGE_COMPANYSTATE: 'api/company/changecompanystate',
        GET_ALL_SOURCE_TYPES: 'api/resume/resumesourcetypes'
    }
};

@Component({
    selector: 'app-company-page',
    templateUrl: './company-page.component.html',
    providers: [PhoneNumberFormatPipe, FourDotFiveDateFormatPipe]
})
export class CompanyPageComponent implements OnInit, OnDestroy {

    // The legacy controller (6,181 lines) assigns ~200 ad-hoc view-state properties across
    // dozens of methods (`this.originalStepOne = ...`, `this.geoLocations = ...`, etc.) without a
    // single up-front declared property list, unlike the smaller sibling pages. Individually
    // declaring every one here (as upload-req-page.component.ts does for its own, shorter list)
    // would be pure transcription with no behavioral value for a like-for-like port, so this
    // root component instead declares an index signature and lets each method keep assigning
    // properties exactly as the legacy body did - see current-feature.md step 10 notes.
    [key: string]: any;

    @Input() $transition$: any;

    MESSAGECONSTANTS: any = MESSAGECONSTANTS;
    COMPANYMANAGEMENTCONSTANTS: any = COMPANYMANAGEMENTCONSTANTS;
    // Legacy body reads/writes `this.StorageService` (capitalized, matching the legacy AngularJS
    // service token) - kept as an instance property assigned from the injected singleton below,
    // rather than renaming every one of its many call sites in the ported body.
    StorageService: StorageService;
    // Bound by the "Employee Benefits" textarea/voice-input/formalize-comment in the template
    // (legacy years lived on `this.$scope.skillsBenefits` via component-scope `ng-model`, since
    // it was unprefixed by `$ctrl.` in the AngularJS template - ported to a plain component
    // property + native [(ngModel)] binding, see company-page.component.html).
    skillsBenefits: string;
    private destroyed = false;
    // Wizard nav state - which step-pane is currently shown. The Fuel-UX `$('#myWizard').wizard()`
    // jQuery plugin (still initialized in _initializeWizard(), unchanged from legacy) manages its
    // own "active" class/selection on the DOM independently of Angular, exactly as it did inside
    // the legacy AngularJS template - kept as-is since it's plain DOM/jQuery, not AngularJS-specific.

    constructor(
        public $state: StateService,
        private changeDetectorRef: ChangeDetectorRef,
        private genericService: GenericService,
        private geocodingService: GeocodingService,
        private storageServiceInjected: StorageService,
        private userService: UserService,
        private companyService: CompanyService,
        private planService: PlanService,
        private dateTimeUtilityService: DateTimeUtilityService,
        private userRoleService: UserRoleService,
        private alertsAndNotificationsService: AlertAndNotificationsService,
        private uibModalService: UibModalService,
        private resumeService: ResumeService,
        public authService: AuthService,
        private phoneNumberFormatPipe: PhoneNumberFormatPipe,
        private fourDotFiveDateFormatPipe: FourDotFiveDateFormatPipe,
        private companyPageModifyPlanModalService: CompanyPageModifyPlanModalService,
        private companyPageChooseJobBoardCredentialTypeModalService: CompanyPageChooseJobBoardCredentialTypeModalService,
        private companyPageDeleteAssessmentCredentialModalService: CompanyPageDeleteAssessmentCredentialModalService,
        private companyPageDisassociateVendorsModalService: CompanyPageDisassociateVendorsModalService,
        private companyPageDeleteCompanyContactModalService: CompanyPageDeleteCompanyContactModalService,
        private companyPageChangeCompanyStateModalService: CompanyPageChangeCompanyStateModalService,
        private companyPageWorkflowStepWarningModalService: CompanyPageWorkflowStepWarningModalService
    ) {
        // `StorageService` is also used as a static-style key/value accessor throughout the
        // ported legacy body (`this.StorageService.get(...)`/`.set(...)`/`.remove(...)`) exactly
        // as the legacy controller used it - keep the same `this.StorageService` property name
        // (capitalized, matching the legacy token) bound to the injected singleton instance.
        this.StorageService = this.storageServiceInjected;
        // Cached once here (rather than re-reading `authService.getUserDetails()`, which
        // re-parses `userDetails` from localStorage on every call) since this component mutates
        // it in place across several statements before persisting it back via
        // `this.StorageService.set('userDetails', ...)` - matching the old `$rootScope.userDetails`
        // singleton-reference behavior those call sites depend on.
        this.userDetails = this.authService.getUserDetails();
    }

    ngOnDestroy() {
        this.destroyed = true;
    }

    // jQuery and HTTP callbacks (e.g. the Google geocode lookup) can resolve after the user
    // navigates away or logs out, when the view is already destroyed.
    private detectChangesIfAlive() {
        if (!this.destroyed) {
            this.changeDetectorRef.detectChanges();
        }
    }

    ngOnInit() {
        this.$stateParams = this.$transition$.params();
        this._initializeVariables();
        this._initializeWizard()
        this._initializeCompany();
        this.getCompanyTypes();
    }

    _initializeVariables() {
        this.isTransitionAbortRequired = true;
        this.loggedInUserDetails = this.StorageService.get('userDetails');
        this.newlyCreatedCompanyId = null;
        this.fourDotFiveCompanyAdmin;
        this.assignClient2StaffingCompany;  //NP
        this.industrySublist = [];          //NP
        this.count = 0;
        this.confirmationAtStep;
        this.valid = false;
        this.showConfirmationPopup = false;
        this.updatedAssessementFlow;
        this.companyStepOneSaved = false;
        this.companyStepTwoSaved = false;
        this.companyStepThreeSaved = false;
        this.companyStepFourSaved = false;
        this.companyStepFiveSaved = false;
        this.stepOneChanged = false;
        this.stepTwoChanged = false;
        this.stepThreeChanged = false;
        this.stepFourChanged = false;
        this.stepFiveChanged = false;
        this.stepSixChanged = false;
        this.getDetailsOfCompany;
        this.fourDotFiveAdminObject = null;
        this.oldFourDotFiveAdminId = null;
        this.clientOrBuUpdateInProgress = false;
        this.clientOrBuCreationInProgress = false;
        this.vendorUpdateInProgress = false;
        this.resumePullCounts = [5, 10, 15, 20, 25];
        this.isCompanyDeleted = false;
        this.isActivePlanExists = true;
        this.isFuturePlanExists = false;
        this.isPlanChosen = false;
        this.futurePlanName = "";
        this.isAssociatedClientUpdateInProgress = false;
        this.isContactSelected = true;
        this.isStepReadOnlyForCompanyAdmin = true;
        this.accessTimeOptionsDisabled = false;
        this.disableActionButtons = false;
        this.hasUnsavedJobBoardCredentials = false;
        this.hasUnsavedAssessmentCredentials = false;

        this.industryListOfCompany = 0;
        this.oldCheckedAdmin = null;
        this.cancelClicked = false;
        this.newCompanyCreation = false;

        this.zipCodeCalling = false;
        this.zipCodeCallCompleted = false;
        this.saveButtonClicked = false;
        this.nextButtonClicked = false;
        this.nextButtonFlag = 0;
        this.saveButtonFlag = 0;
        this.addJobBoardByPasswordFlag = true;

        this.buttonWorkFlowText = 'Add';

        this.form = {};
        this.form.companyDetailsForm = { $setPristine: () => {} };
        this.form.companyContactPersonDetailsForm = { $setPristine: () => {} };
        this.form.companySecondaryContactPersonDetailsForm = { $setPristine: () => {} };
        this.form.contactForm = { $setPristine: () => {} };
        this.form.company4dot5AdminDetailsForm = { $setPristine: () => {} };
        this.form.addCredentialsForm = { $setPristine: () => {} };
        // Validity of company-page-step-license-scoring's own form (candidateSuspensionMonths),
        // combined into this.valid checks above (22b-4d step 6a).
        this.licenseScoringFormValid = true;
        // Validity of company-page-step-license-assessments' own form (the assessment
        // credentials CRUD table's required fields), combined the same way (22b-4d step 6a2).
        this.assessmentsFormValid = true;
        // Validity of company-page-step-license-jobboards' own form (daxtraDbName and the
        // job-board credentials CRUD table's required fields) - the legacy shared
        // `form.companyAssessmentFlowDetailsForm` this used to live on is retired now that
        // every field it validated has moved into a native sub-step (22b-4d step 6a3).
        this.jobBoardsFormValid = true;

        this.maskedMobilePhoneSecondarycursorPosition = true;

        //This is the UI object (Transient object)
        this.companyManagement = {};
        this.CompanyManagementReset = {};
        this.companyManagement.company = {};//Step 1 object
        // Seeded with one blank contact so Step 2 (Primary Contact) has a contactList[0]
        // to bind to as soon as it renders for a brand-new company - the *ngIf guarding
        // <app-company-page-step-contact> requires contactList[0] to exist, and unlike the
        // legacy AngularJS ng-model bindings, Angular's [(ngModel)] does not auto-vivify it.
        // Update/edit flows overwrite this with the real fetched contactList before Step 2 renders.
        this.companyManagement.contactList = [this._createBlankPrimaryContact()];//Step 2 object
        this.fourDotFiveAdminsList = [];//Step 3 object
        // ng-select (replacing the legacy isteven-multi-select directive, which tolerated an
        // undefined ngModel) requires this to be an array from first render (22b-4d step 4/10).
        this.selectedFourDotFiveAdmin = [];
        this.selectedPlan = {};//Step 4 object
        this.plans = [];
        this.futurePlan = {};
        this.AssessmentWorkFlow = [];//Step 5 object
        this.vendors = []; //Step 6 object
        this.phoneValidationStatuses = [];

        //This is the Saved object (Saved object),the object that will be sent while saving the step details.
        this.savedCompanyManagement = {};
        this.savedCompanyManagement.company = {};//Step 1 object
        this.savedCompanyManagement.contactList = [];//Step 2 object
        this.savedFourDotFiveAdmin = [];//Step 3 object
        this.savedCurrentActivePlan = {};//Step 4 object
        this.savedAssessmentWorkFlow = [];//Step 5 object

        this.savedCompanyManagementCopy = {};
        this.savedCompanyManagementCopy.company = {};
        this.savedCompanyManagementCopy.contactList = [];
        this.savedFourDotFiveAdminCopy = [];
        this.savedCurrentActivePlanCopy = {};
        this.savedAssessmentWorkFlowCopy = [];

        this.preferences = null;
        this.jobBoardCredentialListCopy = null;
        this.errorInSavingPrefsCheck = false;
        this.cancelNotClicked = true;
        this.invalidZipcode = false;

        this.licensePreferencesFetched = false;
        this.isCompanyDetailsSaveCalled = false; //flag used to make getworkflow settings api call
        this.planDetailsFetched = false;
        this.isTestLinkReadOnly = true;

        this.selectedIndustryGroup = null;
        this.laststep = false;
        this.TypeOfCompany = 'Company';
        this.showRemoveButton = false;
        if(!this.$stateParams.accessMode){
            this.accessMode = 'view';
        }
        this.IndustrySubList = [];
        this.SelectedIndustrySubList = [];
        this.typeaheadNotSelected = true;
        this.triggerCount = 0;
        this.delimitedCompanyOrClientName = null;
        this.companyTypes = [];
        this.lastStepCompleted = false;
        this.showCreateCompanyHeading = true;
        this.ClientOrBuUpdate = false;
        this.ClientOrBuCreate = false;
        this.IndustryGroupList = [];
        this.IndustrySubGroupList = [];
        this.dataLastLabel = 'Complete';
        this.selectedSubGroup = false;
        this.industriesInPanel = [];
        this.temp = [];

        this.primaryContactWTFormat = '+1 201-555-0123';
        this.primaryContactWTFormat1 = '+1 201-555-0123';
        this.primaryContactMNFormat = '+1 201-555-0123';
        this.secondaryContactWTFormat = '+1 201-555-0123';
        this.secondaryContactMNFormat = '+1 201-555-0123';
        this.primaryContactWTCountryCode = 'us';
        this.primaryContactWTCountryCode1 = 'us';
        this.primaryContactMNCountryCode = 'us';
        this.secondaryContactWTCountryCode = 'us';
        this.secondaryContactMNCountryCode = 'us';

        this.isValidPrimaryMobileEntered = false;
        this.isValidPrimaryWorkPhoneEntered = false;
        this.isValidSecondaryMobileEntered = false;
        this.isValidSecondaryWorkPhoneEntered = false;


        this.jobSourceType = [];

        this.isAddressValid = false;
        this.tempObj = null;
        this.errorOcurred = false;
        this.isCountyExists = false;

        this.JobBoardCredential = {};
        this.JobBoardCredential.sourceType;
        this.JobBoardCredential.userName;
        this.JobBoardCredential.key = "";
        this.selectedAccessTime = [];

        this.keyaccess;
        this.usernameaccess;
        this.buttonAssessment = "Add";
        this.selectedAssesmentIndex;
        this.assessmentTypeList = ["Codility", "Interview Mocha"];
        this.useAccountCredDetailsList = ["4dot5", "My Own"];
        this.useAccountCredDetailsList1 = ["4dot5", "My Own"];
        this.matchCountList = [1,2,3,5];
        this.accessMode = this.$stateParams.accessMode;
        this.readOnlyForClientOrBU = false;
        this.isUserSuperUserOr4dot5Admin = this.userRoleService.isLoggedInUserSuperUserOr4dot5Admin();
        // this.isUserAnAdmin = this.userRoleService.isLoggedInUserAnAdmin();
        this.isCompanyVendor = this.companyService.isCompanyVendor();
        this.isCompanyAdmin = this.userRoleService.isLoggedInUserCompanyAdmin();
    }

    _setMobileNumberSettings() {

        $("#maskedWorkPhone").keyup(() => {
            if((document.getElementById('maskedWorkPhone') as any).value.length > 0) {
                this.workTelephoneDirty = true;
            } else {
                this.workTelephoneDirty = false;
            }
        });

        $("#maskedWorkPhone1").keyup(() => {
            if((document.getElementById('maskedWorkPhone1') as any).value.length > 0) {
                this.mobileNumberDirty = true;
            } else {
                this.mobileNumberDirty = false;
            }
        });

        $("#maskedWorkPhoneSecondary").keyup(() => {
            if((document.getElementById('maskedWorkPhoneSecondary') as any).value.length > 0) {
                this.workTelephoneDirty = true;
            } else {
                this.workTelephoneDirty = false;
            }
        });

        $("#maskedMobilePhoneSecondary").keyup(() => {
            if((document.getElementById('maskedMobilePhoneSecondary') as any).value.length > 0) {
                this.mobileNumberDirty = true;
            } else {
                this.mobileNumberDirty = false;
            }
        });

        $("#maskedWorkPhone").on("countrychange", (e, countryData) => {
            this.primaryContactWTCountryCode = countryData.iso2;
            setTimeout(() => {
                if(this.primaryContactWTCountryCode !== undefined){
                    this.setPrimaryContactWTCountryCode();
                }
            }, 200);
        });

        $("#maskedWorkPhone1").on("countrychange", (e, countryData) => {
            this.primaryContactWTCountryCode1 = countryData.iso2;
            if(this.primaryContactWTCountryCode1 !== undefined){
                this.setPrimaryContactWTCountryCode1();
            }
        });

        $("#maskedMobilePhone").on("countrychange", (e, countryData) => {
            this.primaryContactMNCountryCode = countryData.iso2;
            setTimeout(() => {
                if(this.primaryContactMNCountryCode !== undefined){
                    this.setPrimaryContactMNCountryCode();
                }
            }, 200);
        });

        $("#maskedWorkPhoneSecondary").on("countrychange", (e, countryData) => {
            this.secondaryContactWTCountryCode = countryData.iso2;
            setTimeout(() => {
                if(this.secondaryContactWTCountryCode !== undefined){
                    this.setSecondaryContactWTCountryCode();
                }
            }, 200);
        });

        $("#maskedMobilePhoneSecondary").on("countrychange", (e, countryData) => {
            this.secondaryContactMNCountryCode = countryData.iso2;
            setTimeout(() => {
                if(this.secondaryContactMNCountryCode !== undefined){
                    this.setSecondaryContactMNCountryCode();
                }
            }, 200);
        });


        setTimeout(() => {
            if(this.form.companyContactPersonDetailsForm && typeof this.form.companyContactPersonDetailsForm.$setPristine === 'function') {
                this.form.companyContactPersonDetailsForm.$setPristine();
            }
        }, 5000);
    }

    _initializeWizard() {
        setTimeout(() => {
            {
                $('#myWizard').wizard()
                    .on('actionclicked.fu.wizard', function(event, data) {
                        var wizardoperations = localStorage.getItem('myStorage-wizardoperations');
                        if((wizardoperations === null) || (wizardoperations !== 'true')) {
                            event.preventDefault();
                        }
                    });

                // $('.custom-file-upload').bind("click", function () {
                //     $('#inputImage').trigger('click');
                // });
                $('#select-company-logo').bind("click", function() {
                    $('#company.headerLogo').trigger('click');
                });
                $('#select-company-small-logo').bind("click", function() {
                    $('#company.smallHeaderLogo').trigger('click');
                });
                $('#inputImage').change(function() {
                    var filepath = $('#inputImage').val();
                    var filename = filepath.split('\\').pop().split('/').pop(); //str.split('\\').pop().split('/').pop();
                    $('#companypicname').html(filename);
                });
            }
        }, 500);
    }

    _initializeCompany() {
        /**
         * Conditions to check whether its Client create or Client update.
         */
        if(this.StorageService.get('clientOrBuCreationInProgress') !== null) {
            //Condition is satified if Client Creation is in progress.
            if(this.userDetails.company.companyType == 'Corporation') {
                this.TypeOfCompany = 'BusinessUnit';
                //this.companyManagement.company.companyType = 'BusinessUnit';
            } else if(this.userDetails.company.companyType == 'StaffingCompany') {
                this.TypeOfCompany = 'Client';
                //this.companyManagement.company.companyType = 'Client';
            }
            this.ClientOrBuCreate = true;
            this.clientOrBuCreationInProgress = true;
            this.StorageService.remove('clientOrBuCreationInProgress');
            // INDUSTRY CODE                getIndustryGroups();
        } else if(!_.isNull(this.StorageService.get('clientOrBuToBeUpdated')) || !_.isNull(this.$stateParams.clientOrBuObject)) {
            //Condition is satified if Client Updation is in progress.
            if(this.userDetails.company.companyType == 'Corporation') {
                this.TypeOfCompany = 'BusinessUnit';
            } else if(this.userDetails.company.companyType == 'StaffingCompany') {
                this.TypeOfCompany = 'Client';
                this.isAssociatedClientUpdateInProgress = this.$stateParams.isAssociatedClient ? true : false;
            }
            this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_COMPANY + '/' + this.$stateParams.clientOrBuObject.company.id).subscribe((data) => {
                this.clientOrBuUpdateInProgress = true;
                this.ClientOrBuUpdate = true;
                if(this.isAssociatedClientUpdateInProgress) {
                    this._getSharedClientDescription(this.$stateParams.clientOrBuObject.company.id, (description) => {
                        let sharedClientObject = _.cloneDeep(this.$stateParams.clientOrBuObject);
                        sharedClientObject.company.description = description.clientDescription;
                        this.companyManagement = _.cloneDeep(sharedClientObject);
                        this._ensurePrimaryContact();
                        this.CompanyManagementReset = _.cloneDeep(this.companyManagement);
                        this._saveStepOne(data.body);
                    });
                } else {
                    this.companyManagement = _.cloneDeep(data.body);
                    this._ensurePrimaryContact();
                    this.CompanyManagementReset = _.cloneDeep(this.companyManagement);
                    this._saveStepOne(data.body);
                }

            });

            if(this.accessMode == 'view') {
            //This condition is satisfied when user doesnot have access to edit the company details and hence all fields are disabled.
            // this.companyDetailsUpdateModeByCompanyAdmin = true;
            this.readOnlyForClientOrBU = true;
            setTimeout(() => {
                $('.access').attr({ 'readonly': true, 'disabled': true });
                $('.hostaccess').attr({ 'readonly': true, 'disabled': true });
            }, 500);
            }
        }

        /**
         * Conditions to check for Vendor update.
         */

        else if(this.$stateParams.vendorId !== null) {
            this.vendorUpdateInProgress = true;
            if(this.userDetails.company.companyType == 'Corporation') {
                this.TypeOfCompany = 'Vendor';
            }
            this.companyService.getCompanyDetails(this.$stateParams.vendorId, (data) => {
                this.companyService.getVendorContacts(this.$stateParams.vendorId, this.userDetails.company.companyId, (vendorContacts) => {
                    data.contactList = _.cloneDeep(vendorContacts);
                    data.contactList.forEach((val, key) => {
                        val.primary = val.isPrimary;
                    });
                    this.companyManagement = _.cloneDeep(data);
                    this._ensurePrimaryContact();
                    this.CompanyManagementReset = _.cloneDeep(this.companyManagement);
                    this.savedCompanyManagement.company = _.cloneDeep(this.companyManagement.company);
                    this.savedCompanyManagement.contactList = _.cloneDeep(this.companyManagement.contactList);
                    this.savedCompanyManagement.companyState = _.cloneDeep(this.companyManagement.companyState);
                    this.editCompanyName = this.companyManagement.company.name.substring(0, 57);
                    this.editCompanyNameOnHover = this.companyManagement.company.name;
                    if(this.companyManagement.company.name.length > 57) {
                        this.editCompanyName = this.editCompanyName + '...';
                    }
                    this.getAddressDetails(this.companyManagement.company.zipcode, 'updateaddress');

                    this.originalStepOne = _.cloneDeep(this.companyManagement.company);
                    this.originalStepTwo = _.cloneDeep(this.companyManagement.contactList);
                    if(this.companyManagement.contactList.length > 1) {
                        this.showRemoveButton = true;
                        this.isValidPrimaryMobileEntered = true;
                        this.isValidPrimaryWorkPhoneEntered = true;
                        this.isValidSecondaryMobileEntered = true;
                        this.isValidSecondaryWorkPhoneEntered = true;
                        for(var i = 1; i < this.companyManagement.contactList.length; i++) {
                            this.phoneValidationStatuses.push({
                                emptyWorkNumber: false,
                                invalidWorkNumber: false,
                                isValidWorkNumber: true,
                                emptyMobileNumber: false,
                                invalidMobileNumber: false,
                                isValidMobileNumber: true
                            });
                        }
                    } else if(this.companyManagement.contactList.length == 1) {
                        this.isValidPrimaryMobileEntered = true;
                        this.isValidPrimaryWorkPhoneEntered = true;
                    }
                }, (error) => {
                    this.alertsAndNotificationsService.showBannerMessage(error, 'danger');
                });
            }, (error) => {
                //do nothing
            })
        }

        /**
         * This condition separates whether the module is loaded
         * on account of Company view/update or Host Company view/update.
         * @access for create, view and update
         */
        else if((this.StorageService.get('companyDetails') != null || this.userDetails.roleScreenRef.role.name == 'CorporateAdmin' || this.userDetails.roleScreenRef.role.name == 'StaffingAdmin') && !this.clientOrBuCreationInProgress && !this.clientOrBuUpdateInProgress && !this.vendorUpdateInProgress && this.StorageService.get('SelectedHostCompany') == null) {
            // This condition is satisfied when a company is selected from popup or Role is CorporateAdmin or role is StaffingAdmin
            // and
            // user not in creation/ updation of client and selected company is not host company.
            // view or update company access
            if(this.StorageService.get('companyDetails') != null) {
                if(!_.isUndefined(this.StorageService.get('companyDetails').companyId)) {
                    this.getDetailsOfCompany = this.StorageService.get('companyDetails').companyId;
                } else {
                    this.getDetailsOfCompany = this.StorageService.get('companyDetails');
                }
            } else {
                this.getDetailsOfCompany = this.userDetails.company.companyId;
            }

            this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_COMPANY + '/' + this.getDetailsOfCompany).subscribe((data) => {
                this.companyManagement = data.body;
                this._ensurePrimaryContact();
                this.CompanyManagementReset = _.cloneDeep(this.companyManagement);
                //Setting the phone number values to be true
                if(data.body.contactList.length == 1) {
                    this.isValidPrimaryMobileEntered = true;
                    this.isValidPrimaryWorkPhoneEntered = true;
                } else if(data.body.contactList.length > 1) {
                    this.isValidPrimaryMobileEntered = true;
                    this.isValidPrimaryWorkPhoneEntered = true;
                    this.isValidSecondaryMobileEntered = true;
                    this.isValidSecondaryWorkPhoneEntered = true;
                    for(var i = 1; i < data.body.contactList.length; i++) {
                        this.phoneValidationStatuses.push({
                            emptyWorkNumber: false,
                            invalidWorkNumber: false,
                            isValidWorkNumber: true,
                            emptyMobileNumber: false,
                            invalidMobileNumber: false,
                            isValidMobileNumber: true
                        });
                    }
                }

                this.getAddressDetails(data.body.company.zipcode, 'updateaddress');

                this.originalStepOne = _.cloneDeep(this.companyManagement.company);
                this.savedCompanyManagement.company = _.cloneDeep(this.companyManagement.company);
                this.originalStepTwo = _.cloneDeep(this.companyManagement.contactList);
                this.savedCompanyManagement.contactList = _.cloneDeep(this.companyManagement.contactList);
                this.originalStepThree = _.cloneDeep(this.companyManagement.fourDotFiveAdmin);
                this.savedFourDotFiveAdmin = _.cloneDeep(this.companyManagement.fourDotFiveAdmin);


                //  INDUSTRY CODE                 this.SelectedIndustrySubList = _.cloneDeep(this.companyManagement.company.industryList);

                if(this.companyManagement.fourDotFiveAdmin !== null) {
                    //This is satisfied when a company has 4dot5 admin assigned.
                    this.oldFourDotFiveAdminId = this.companyManagement.fourDotFiveAdmin.userId;
                    this.companyManagement.fourDotFiveAdmin.ticked = true;
                    this.originalStepThree.ticked = true;
                    this.fourDotFiveAdminsList = [];
                    this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_4DOT5_ADMINS).subscribe((data) => {
                        const admins = [];
                        if(this.userDetails.roleScreenRef.role.name == '4dot5SuperUser' || this.userDetails.roleScreenRef.role.name == '4dot5Admin') {
                            data.body.forEach((element) => {
                                if(element.userId == this.companyManagement.fourDotFiveAdmin.userId) {
                                    element.ticked = true;
                                } else {
                                    element.ticked = false;
                                }
                                admins.push(element);
                            }, this);
                        } else {
                            admins.push(this.companyManagement.fourDotFiveAdmin);
                        }
                        this.fourDotFiveAdminsList = admins;
                        this.get4Dot5AdminFullName();
                        this.getUserConfiguration();
                    }, (error) => {
                        if(this.authService.isOnline) {
                            this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                        }

                        //INDUSTRY CODE                            getIndustryGroups();
                    });
                } else if(this.isSuperUser(this.userDetails.roleScreenRef.role) && (this.companyManagement.fourDotFiveAdmin == null)) {
                    //This is satisfied when a company has is saved as a draft company and hence 4dot5 admin is still not assigned.
                    this.get4dot5admins();
                }
                if(data.body.contactList.length > 1) {
                    //This condition is satisfied when the selected company has Secondary Contact assigned and hence it has to be shown.
                    this.showRemoveButton = true;
                    //Setting phone number values to be true.
                    this.isValidSecondaryWorkPhoneEntered = true;
                    this.isValidSecondaryMobileEntered = true;
                }
            }, (error) => {
                if(this.authService.isOnline) {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                }
            });

            this.hostCompanySelected = false;
            this.companyDetailsUpdateMode = false;
            this.companyDetailsUpdateModeByCompanyAdmin = false;
            this.assessmentOrJobBoardCredentialsUpdated = false;
            this.showCreateCompanyHeading = false;

            if(this.userDetails.roleScreenRef.role.name == '4dot5SuperUser' || this.userDetails.roleScreenRef.role.name == '4dot5Admin') {
                //This condition is satisfied when user has access to edit the company details.
                this.companyDetailsUpdateMode = true;
                this.showCreateCompanyHeading = false;
                this.StorageService.remove('wizardoperations');
                setTimeout(() => {
                    $('.access').attr({ 'readonly': false, 'disabled': false });
                }, 500);
            } else {
                //This condition is satisfied when user doesnot have access to edit the company details and hence all fields are disabled.
                this.companyDetailsUpdateModeByCompanyAdmin = true;
                setTimeout(() => {
                    $('.access').attr({ 'readonly': true, 'disabled': true });
                    $('.hostaccess').attr({ 'readonly': true, 'disabled': true });
                }, 500);
            }
            this.accessMode = _.cloneDeep(this.$stateParams.accessMode);
        } else if(this.StorageService.get('SelectedHostCompany') != null) {
            //This condition is satisfied when selected company is a host company.
            this.companyDetailsUpdateMode = false;
            this.hostCompanySelected = true;
            this.TypeOfCompany = 'Host';
            this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_COMPANY + '/' + this.StorageService.get('SelectedHostCompany').companyId).subscribe((data) => {
                this.companyManagement = data.body;
                this._ensurePrimaryContact();
                this.CompanyManagementReset = _.cloneDeep(this.companyManagement);
                this.savedCompanyManagement.company = _.cloneDeep(this.companyManagement.company);
                this.savedCompanyManagement.contactList = _.cloneDeep(this.companyManagement.contactList);
                this.getAddressDetails(data.body.company.zipcode, 'updateaddress');
                this.originalStepOne = _.cloneDeep(this.companyManagement.company);

                // INDUSTRY CODE                 delete this.originalStepOne['industryGroup'];
                // INDUSTRY CODE               delete this.originalStepOne['industryList'];
                this.originalStepTwo = _.cloneDeep(this.companyManagement.contactList);
                this.originalStepFive = _.cloneDeep(this.companyManagement.licensePreferences);

                //Updating the SavedCompanyManagement object after getting company details.
                this.savedAssessmentWorkFlow = _.cloneDeep(this.companyManagement.licensePreferences);
                //  INDUSTRY CODE    this.SelectedIndustrySubList = _.cloneDeep(this.companyManagement.company.industryList);

                this.AssessmentWorkFlow = this.companyManagement.licensePreferences;
                this.accessMode = _.cloneDeep(this.$stateParams.accessMode);

                if(data.body.contactList.length > 1) {
                    //This condition is satisfied when the selected company has Secondary Contact assigned and hence it has to be shown.
                    this.showRemoveButton = true;
                }
                if(this.userDetails.roleScreenRef.role.name == '4dot5SuperUser' || this.userDetails.roleScreenRef.role.name == '4dot5Admin') {
                    this.companyDetailsUpdateMode = true;
                    this.showCreateCompanyHeading = false;
                    this.StorageService.remove('wizardoperations');
                    setTimeout(() => {
                        $('.access').attr({ 'readonly': true, 'disabled': true });
                        $('.hostaccess').attr({ 'readonly': true, 'disabled': true });
                    }, 500);
                } else {
                    setTimeout(() => {
                        $('.access').attr({ 'readonly': true, 'disabled': true });
                        $('.hostaccess').attr({ 'readonly': true, 'disabled': true });
                    }, 500);
                }
                // INDUSTRY CODE     getIndustryGroups();
            }, (error) => {
                if(this.authService.isOnline) {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                }
            });
        }
    }

    _saveStepOne(data) {
        this.savedCompanyManagement.company = _.cloneDeep(this.companyManagement.company);
        this.savedCompanyManagement.contactList = _.cloneDeep(this.companyManagement.contactList);
        //set phone validation statuses
        if(this.companyManagement.contactList.length == 0) {
            this.showRemoveButton = false;
        }
        for(var i = 0; i < this.companyManagement.contactList.length; i++) {
            if(i > 0) {
                var validationStatus = {
                    emptyWorkNumber: false,
                    invalidWorkNumber: false,
                    isValidWorkNumber: false,
                    emptyMobileNumber: false,
                    invalidMobileNumber: false,
                    isValidMobileNumber: false
                }
                this.phoneValidationStatuses.push(validationStatus);
            }
        }

        //As its a draft there should not be exception as duplicate client while saving.
        this.savedCompanyManagement.companyState = _.cloneDeep(this.companyManagement.companyState);
        this.editCompanyName = this.companyManagement.company.name.substring(0, 57);
        this.editCompanyNameOnHover = this.companyManagement.company.name;
        if(this.companyManagement.company.name.length > 57) {
            this.editCompanyName = this.editCompanyName + '...';
        }
        this.getAddressDetails(this.companyManagement.company.zipcode, 'updateaddress');
        this.StorageService.remove('clientOrBuToBeUpdated');

        this.originalStepOne = _.cloneDeep(this.companyManagement.company);
        this.originalStepTwo = _.cloneDeep(this.companyManagement.contactList);
        if(this.companyManagement.contactList.length > 1) {
            this.showRemoveButton = true;
            this.isValidPrimaryMobileEntered = true;
            this.isValidPrimaryWorkPhoneEntered = true;
            this.isValidSecondaryMobileEntered = true;
            this.isValidSecondaryWorkPhoneEntered = true;
            this.phoneValidationStatuses.forEach((status, key) => {
                status.isValidWorkNumber = true;
                status.isValidMobileNumber = true;
            });
        } else if(this.companyManagement.contactList.length == 1) {
            this.isValidPrimaryMobileEntered = true;
            this.isValidPrimaryWorkPhoneEntered = true;
        }
    }

    _getSharedClientDescription(clientId, successCallback) {
        const companyId = this.userDetails.company.companyId;
        this.companyService.getSharedClientDescription(companyId, clientId, (data) => {
            if(successCallback) {
                successCallback(data);
            }
        }, error => {
            this.alertsAndNotificationsService.showBannerMessage(error.message, "danger");
        });
    }

    _setSharedClientDescription(successCallback, errorCallback?) {
        let descriptionDetails = {
            companyId: this.userDetails.company.companyId, 
            clientId: this.companyManagement.company.id,
            note: this.companyManagement.company.description
        };
        this.companyService.setSharedClientDescription(descriptionDetails, data => {
            if(successCallback) {
                successCallback(data);
            }
        }, error =>{
            if(errorCallback) {
                errorCallback();
            }
        });
    }
    /**
     * This method works for triggerng button clicks on save button
     *  only for on blur validations
     */
    saveButtonTrigger() {
        var saveButtonClick = setTimeout(() => {
            $('.btn-save').triggerHandler('click');
            this.saveButtonClicked = false;
            this.nextButtonClicked = false;
            clearTimeout(saveButtonClick);
        }, 0);
    }

    stepMoved($index, jobType) {
        this.workflowStepsOrderToRender[jobType].splice($index, 1);
    }

    /**
     * This method works for triggerng button clicks on save button
     *  only for on blur validations
     */
    nextButtonTrigger() {
        var nextbtnclick = setTimeout(() => {
            $('.btn-next').triggerHandler('click');
            this.zipCodeCalling = false;
            this.zipCodeCallCompleted = false;
            this.nextButtonClicked = false;
            this.saveButtonClicked = false;
            this.triggerCount = 0;
            clearTimeout(nextbtnclick);
        }, 0);
    }

    onSlideClick(direction) {
        if(direction == 'right') {
            event.preventDefault();
            $('#content').animate({
                scrollLeft: "+=400px"
            }, "slow");
        }
        else {
            event.preventDefault();
            $('#content').animate({
                scrollLeft: "-=400px"
            }, "slow");
        }
    }

    isPrimaryContactWTValid(workPhoneValue) {
        if(workPhoneValue !== "") {
            if($("#maskedWorkPhone").intlTelInput("isValidNumber")) {
                this.isValidPrimaryWorkPhoneEntered = true;
                this.companyManagement.contactList[0].workPhone = this.phoneNumberFormatPipe.transform(workPhoneValue, this.primaryContactWTFormat);
            } else {
                this.emptyCompanyWorkNumber = false;
                this.invalidCompanyWorkNumber = true;
                this.isValidPrimaryWorkPhoneEntered = false;
                this.primaryWorkTelephoneValueTrim = (document.getElementById('maskedWorkPhone') as any).value;
                this.companyManagement.contactList[0].workPhone = this.primaryWorkTelephoneValueTrim.replace(/ |-/gm, '');
            }
        } else {
            this.emptyCompanyWorkNumber = true;
            this.invalidCompanyWorkNumber = false;
            this.isValidPrimaryWorkPhoneEntered = false;
        }
    }

    isPrimaryContactWTValid1(mobileNumberValue) {
        if(mobileNumberValue !== "") {
            this.form.companyContactPersonDetailsForm.maskedWorkPhone1.visited = true;
            if($("#maskedWorkPhone1").intlTelInput("isValidNumber")) {
                this.isValidPrimaryMobileEntered = true;
                this.companyManagement.contactList[0].mobilePhone = this.phoneNumberFormatPipe.transform(mobileNumberValue, this.primaryContactWTFormat1);
            } else {
                this.emptyCompanyMobileNumber = false;
                this.invalidCompanyMobileNumber = true;
                this.primaryMobileTelephoneValueTrim = (document.getElementById('maskedWorkPhone1') as any).value;
                this.companyManagement.contactList[0].mobilePhone = this.primaryMobileTelephoneValueTrim.replace(/ |-/gm, '');
                this.isValidPrimaryMobileEntered = false;
            }
        } else {
            this.emptyCompanyMobileNumber = true;
            this.invalidCompanyMobileNumber = false;
            this.isValidPrimaryMobileEntered = false;
        }
    }

    /**
     *  This is to handle long string
     */
    get4Dot5AdminFullName() {
        for(var p = 0; p < this.fourDotFiveAdminsList.length; p++) {
            if(this.fourDotFiveAdminsList[p].lastName != null) {
                var output: any = this.fourDotFiveAdminsList[p].firstName + " " + this.fourDotFiveAdminsList[p].lastName;
            } else {
                var output: any = this.fourDotFiveAdminsList[p].firstName;
            }
            if(output.length > 57) {
                this.fourDotFiveAdminsList[p]["fullName"] = output.substring(0, 57) + "...";
            } else {
                this.fourDotFiveAdminsList[p]["fullName"] = output;
            }
        }
        var tickedAdmin = this.fourDotFiveAdminsList.find((admin) => admin.ticked);
        this.selectedFourDotFiveAdmin = tickedAdmin ? [tickedAdmin] : [];
    }

    setPrimaryContactWTCountryCode() {
        $("#maskedWorkPhone").intlTelInput("setCountry", this.primaryContactWTCountryCode);
        this.primaryContactWTFormat = (document.getElementById("maskedWorkPhone") as any).placeholder;
        var maskValidator = [];

        for(var index = 0; index < this.primaryContactWTFormat.length; index++) {
            if(_.isNumber(this.primaryContactWTFormat[index])) {
            } else {
            }
        }
        setTimeout(() => {
            this.isPrimaryContactWTValid(this.companyManagement.contactList[0].workPhone);
        }, 500);

    }

    setPrimaryContactWTCountryCode1() {
        $("#maskedWorkPhone1").intlTelInput("setCountry", this.primaryContactWTCountryCode1);
        this.primaryContactWTFormat1 = (document.getElementById("maskedWorkPhone1") as any).placeholder;
        this.isPrimaryContactWTValid1(this.companyManagement.contactList[0].mobilePhone);
    }

    setPrimaryContactMNCountryCode() {
        $("#maskedMobilePhone").intlTelInput("setCountry", this.primaryContactMNCountryCode);
        this.primaryContactMNFormat = (document.getElementById("maskedMobilePhone") as any).placeholder;
        setTimeout(() => {
            this.isPrimaryContactMNValid();
        }, 500);
    }

    setPrimaryContactNExamples() {
        this.primaryContactWTFormat = (document.getElementById("maskedWorkPhone") as any).placeholder;
        this.primaryContactWTFormat1 = (document.getElementById("maskedWorkPhone1") as any).placeholder;
        this.primaryContactMNFormat = (document.getElementById("maskedMobilePhone") as any).placeholder;
    }

    isSecondaryContactWTValid(secondaryWorkNumberValue) {
        if(secondaryWorkNumberValue !== "") {
            this.form.companySecondaryContactPersonDetailsForm.maskedWorkPhoneSecondary.visited = true;
            if($("#maskedWorkPhoneSecondary").intlTelInput("isValidNumber")) {
                this.companyManagement.contactList[1].workPhone = this.phoneNumberFormatPipe.transform(secondaryWorkNumberValue, this.secondaryContactWTFormat);
                this.isValidSecondaryWorkPhoneEntered = true;
            } else {
                this.emptyCompanySecondaryWorkNumber = false;
                this.invalidCompanySecondaryWorkNumber = true;
                this.isValidSecondaryWorkPhoneEntered = false;
                this.secondaryWorkTelephoneValueTrim = (document.getElementById('maskedWorkPhoneSecondary') as any).value;
                this.companyManagement.contactList[1].workPhone = this.secondaryWorkTelephoneValueTrim.replace(/ |-/gm, '');
            }
        } else {
            this.emptyCompanySecondaryWorkNumber = true;
            this.invalidCompanySecondaryWorkNumber = false;
            this.isValidSecondaryWorkPhoneEntered = false;
        }
    }

    isContactWTValid(workNumber, index) {
        var selectorId = "#contactWorkPhone" + index;
        var id = "contactWorkPhone" + index;
        if(workNumber !== "") {
            if($(selectorId).intlTelInput("isValidNumber")) {
                this.companyManagement.contactList[index].workPhone = this.phoneNumberFormatPipe.transform(workNumber, this.secondaryContactWTFormat);
                this.phoneValidationStatuses[index - 1].isValidWorkNumber = true;
            } else {
                this.phoneValidationStatuses[index - 1].emptyWorkNumber = false;
                this.phoneValidationStatuses[index - 1].invalidWorkNumber = true;
                this.phoneValidationStatuses[index - 1].isValidWorkNumber = false;
                let contactWorkTelephoneValueTrim = (document.getElementById(id) as any).value;
                this.companyManagement.contactList[index].workPhone = contactWorkTelephoneValueTrim.replace(/ |-/gm, '');
            }
        } else {
            this.phoneValidationStatuses[index - 1].emptyWorkNumber = true;
            this.phoneValidationStatuses[index - 1].invalidWorkNumber = false;
            this.phoneValidationStatuses[index - 1].isValidWorkNumber = false;
        }
    }

    isSecondaryContactMNValid(secondaryMobileNumberValue) {
        if(secondaryMobileNumberValue !== "") {
            this.form.companySecondaryContactPersonDetailsForm.maskedMobilePhoneSecondary.visited = true;
            if($("#maskedMobilePhoneSecondary").intlTelInput("isValidNumber")) {
                this.isValidSecondaryMobileEntered = true;
                this.companyManagement.contactList[1].mobilePhone = this.phoneNumberFormatPipe.transform(secondaryMobileNumberValue, this.secondaryContactMNFormat);
            } else {
                this.emptyCompanySecondaryMobilePhone = false;
                this.invalidCompanySecondaryMobilePhone = true;
                this.isValidSecondaryMobileEntered = false;
                this.secondaryMobileTelephoneValueTrim = (document.getElementById('maskedMobilePhoneSecondary') as any).value;
                this.companyManagement.contactList[1].mobilePhone = this.secondaryMobileTelephoneValueTrim.replace(/ |-/gm, '');
            }
        } else {
            this.emptyCompanySecondaryMobilePhone = true;
            this.invalidCompanySecondaryMobilePhone = false;
            this.isValidSecondaryMobileEntered = false;
        }
    }

    isContactMNValid(mobileNumber, index) {
        if(mobileNumber !== "") {
            var selectorId = "#contactMobilePhone" + index;
            var id = "contactMobilePhone" + index;
            if($(selectorId).intlTelInput("isValidNumber")) {
                this.phoneValidationStatuses[index - 1].isValidMobileNumber = true;
                this.companyManagement.contactList[index].mobilePhone = this.phoneNumberFormatPipe.transform(mobileNumber, this.secondaryContactMNFormat);
            } else {
                this.phoneValidationStatuses[index - 1].emptyMobileNumber = false;
                this.phoneValidationStatuses[index - 1].invalidMobileNumber = true;
                this.phoneValidationStatuses[index - 1].isValidMobileNumber = false;
                let contactMobileTelephoneValueTrim = (document.getElementById(id) as any).value;
                this.companyManagement.contactList[index].mobilePhone = contactMobileTelephoneValueTrim.replace(/ |-/gm, '');
            }
        } else {
            this.phoneValidationStatuses[index - 1].emptyMobileNumber = true;
            this.phoneValidationStatuses[index - 1].invalidMobileNumber = false;
            this.phoneValidationStatuses[index - 1].isValidMobileNumber = false;
        }
    }

    setSecondaryContactWTCountryCode() {
        $("#maskedWorkPhoneSecondary").intlTelInput("setCountry", this.secondaryContactWTCountryCode);
        this.secondaryContactWTFormat = (document.getElementById("maskedWorkPhoneSecondary") as any).placeholder;
        setTimeout(() => {
            this.isSecondaryContactWTValid((document.getElementById("maskedWorkPhoneSecondary") as any).value);
        }, 500);

    }

    setSecondaryContactMNCountryCode() {
        $("#maskedMobilePhoneSecondary").intlTelInput("setCountry", this.secondaryContactMNCountryCode);
        this.secondaryContactMNFormat = (document.getElementById("maskedMobilePhoneSecondary") as any).placeholder;
        setTimeout(() => {
            this.isSecondaryContactMNValid((document.getElementById("maskedMobilePhoneSecondary") as any).value);
        }, 500);

    }

    setSecondaryContactNExamples() {
        this.secondaryContactWTFormat = (document.getElementById("maskedWorkPhoneSecondary") as any).placeholder;
        this.secondaryContactMNFormat = (document.getElementById("maskedMobilePhoneSecondary") as any).placeholder;
    }

    getCurrentStep() {
        return $('#myWizard').wizard('selectedItem').step;
    }

    isUserManagementTabActive() {
        var stepIndex = $('#myWizard').wizard('selectedItem').step;
        return stepIndex == 3;
    }

    single() {
    }

    cleanContactList() {
        // for(var i = 0; i < this.companyManagement.contactList.length; i++) {
        //     if(this.companyManagement.contactList[i].isHiringManager == true && this.companyManagement.contactList[i].firstName == undefined && this.companyManagement.contactList[i].workPhone == undefined && this.companyManagement.contactList[i].mobilePhone == undefined) {
        //         this.companyManagement.contactList.splice(i, 1);
        //     }
        // }

        for(var i = 0; i < this.companyManagement.contactList.length; i++) {
            if(this.companyManagement.contactList[i].primary !== undefined) {
                delete this.companyManagement.contactList[i].primary;
            }
        }

        for(var i = 0; i < this.savedCompanyManagement.contactList.length; i++) {
            if(this.savedCompanyManagement.contactList[i].isHiringManager == true && this.savedCompanyManagement.contactList[i].firstName == undefined && this.savedCompanyManagement.contactList[i].workPhone == undefined && this.savedCompanyManagement.contactList[i].mobilePhone == undefined) {
                this.savedCompanyManagement.contactList.splice(i, 1);
            }
        }

        for(var i = 0; i < this.savedCompanyManagement.contactList.length; i++) {
            if(this.savedCompanyManagement.contactList[i].primary !== undefined) {
                delete this.savedCompanyManagement.contactList[i].primary;
            }
        }

        if(this.originalStepTwo) {
            for(var i = 0; i < this.originalStepTwo.length; i++) {
                if(this.originalStepTwo[i].primary !== undefined) {
                    delete this.originalStepTwo[i].primary;
                }
            }
        }
    }
    /**
     * This method is called during company update,host company update, client update and if address is changed and cancel is clicked in step 1.
     * This would be sent over HTTPS if the page is served via HTTPS
     */
    getAddressDetails(zipcode, type, callback?) {
        if(!_.isUndefined(this.form.companyDetailsForm.zipcode) && this.form.companyDetailsForm.zipcode.$valid) {
            this.validzipcode = true;
        } else {
            this.validzipcode = false;
        }

        this.cities = [];
        this.states = [];
        this.counties = [];
        this.countries = [];
        var locations = [];

        if(type != 'copycomanyaddress') {
            var copyCompanyAddressCheckbox = (document.getElementById("copyCompanyAddress") as any);
            if(copyCompanyAddressCheckbox) {
                copyCompanyAddressCheckbox.checked = false;
            }
        }

        this.copyCompanyAddress = false;

        if(!_.isUndefined(zipcode) && !_.isEqual(zipcode, '')) {
            this.geocodingService.getByZipCode(zipcode).subscribe(
                (data) => {
                    var checkCountyExists = (locations, company) => {
                        for(var i = 0; i < locations.length; i++) {
                            if(_.isEqual(locations[i].city, company.city) &&
                                !_.isUndefined(locations[i].county) && (locations.county !== null) &&
                                !_.isUndefined(company.county) &&
                                (company.county !== null) && _.isEqual(locations[i].county, company.county)) {
                                return true;
                            }
                        }
                        return false;
                    };
                    if(data.status === 'OK') {
                        this.validzipcode = true;
                        for(var i = 0; i < data.results.length; i++) {
                            if(!_.isUndefined(data.results[i].postcode_localities) && (data.results[i].postcode_localities.length > 0)) {
                                for(var k = 0; k < data.results[i].postcode_localities.length; k++) {
                                    if((data.results[i].types[0] === 'postal_code')) {
                                        var location = this.geocodingService.parseAddress(data.results[i], data.results[i].postcode_localities[k]);
                                        locations.push(_.cloneDeep(location));
                                    } else {
                                    }
                                }
                            } else {
                                if((data.results[i].types[0] === 'postal_code')) {
                                    var location = this.geocodingService.parseAddress(data.results[i]);
                                    locations.push(_.cloneDeep(location));
                                } else {
                                }
                            }
                        }
                        this.temp = locations;
                        if(locations.length) {
                            this.temp.city = locations[0].city;
                            this.temp.addressState = locations[0].state;
                            this.temp.county = (_.isUndefined(locations[0].county)) ? null : locations[0].county;
                            if(!_.isUndefined(locations[0].county) && (locations[0].county !== null)) {
                                this.isCountyExists = true;
                            } else {
                                this.isCountyExists = false;
                            }
                            this.temp.country = locations[0].country;
                            this.temp.countryCode = locations[0].countryCode;
                            this.zipCodeCallCompleted = true;
                        }

                        if((type === 'createaddress' || type === 'copycomanyaddress') && !this.cancelClicked && locations.length) {
                            this.companyManagement.company.city = locations[0].city;
                            this.companyManagement.company.addressState = locations[0].state;
                            this.companyManagement.company.county = (_.isUndefined(locations[0].county)) ? null : locations[0].county;
                            if(!_.isUndefined(locations[0].county) && (locations[0].county !== null)) {
                                this.isCountyExists = true;
                            } else {
                                this.isCountyExists = false;
                            }
                            this.companyManagement.company.country = locations[0].country;
                            this.companyManagement.company.countryCode = locations[0].countryCode;
                            this.validzipcode = true;
                            this.zipCodeCallCompleted = true;
                        } else if(type === 'zipchanged' && !this.cancelClicked && locations.length) {
                            this.companyManagement.company.city = locations[0].city;
                            this.companyManagement.company.addressState = locations[0].state;
                            this.companyManagement.company.county = (_.isUndefined(locations[0].county)) ? null : locations[0].county;
                            if(!_.isUndefined(locations[0].county) && (locations[0].county !== null)) {
                                this.isCountyExists = true;
                            } else {
                                this.isCountyExists = false;
                            }
                            this.companyManagement.company.country = locations[0].country;
                            this.companyManagement.company.countryCode = locations[0].countryCode;
                            this.validzipcode = true;
                            this.zipCodeCallCompleted = true;
                        } else if(type === 'updateaddress' && !this.cancelClicked && locations.length) {
                            this.zipCodeCallCompleted = true;
                        } else if(this.cancelClicked) {
                            locations = _.cloneDeep(this.temp);
                            this.geoLocations = _.cloneDeep(this.temp);
                            this.geoLocationscopy = _.cloneDeep(this.copyOfGeoLocations);
                            this.companyManagement.company.city = this.originalStepOne.city;
                            this.companyManagement.company.addressState = this.originalStepOne.addressState;
                            this.companyManagement.company.county = this.originalStepOne.county;
                            if(checkCountyExists(_.cloneDeep(locations), _.cloneDeep(this.originalStepOne))) {
                                this.isCountyExists = true;
                            } else {
                                this.isCountyExists = false;
                            }
                            this.companyManagement.company.country = this.originalStepOne.country;
                            this.companyManagement.company.country = this.originalStepOne.countryCode;
                            this.validzipcode = true;
                            this.zipCodeCallCompleted = true;
                        } else if(locations.length) {
                            if(checkCountyExists(_.cloneDeep(locations), _.cloneDeep(this.originalStepOne))) {
                                this.isCountyExists = true;
                            } else {
                                this.isCountyExists = false;
                            }
                            if(!_.isUndefined(this.originalStepTwo[0]) && !_.isUndefined(this.originalStepTwo[0].workPhone)) {
                                this.companyManagement.contactList[0].workPhone = _.cloneDeep(this.originalStepTwo[0].workPhone);
                                this.companyManagement.contactList[0].mobilePhone = _.cloneDeep(this.originalStepTwo[0].mobilePhone);
                            }
                            if(!_.isUndefined(this.originalStepTwo[1]) && !_.isUndefined(this.originalStepTwo[1].workPhone)) {
                                this.companyManagement.contactList[1].workPhone = _.cloneDeep(this.originalStepTwo[1].workPhone);
                                this.companyManagement.contactList[1].mobilePhone = _.cloneDeep(this.originalStepTwo[1].mobilePhone);
                            }
                            this.zipCodeCallCompleted = true;
                        }
                    } else if(data.status === 'ZERO_RESULTS') {
                        this.validzipcode = false;
                        if(!this.cancelClicked) {
                            this.validzipcode = false;
                            if(!this.invalidZipcode) {
                                this.invalidZipcode = true;
                            }
                            locations = [];
                            this.companyManagement.company.city = '';
                            this.companyManagement.company.addressState = '';
                            this.companyManagement.company.county = '';
                            this.isCountyExists = false;
                            this.companyManagement.company.country = '';
                            this.companyManagement.company.countryCode = '';
                            this.zipCodeCallCompleted = true;
                        } else {
                            locations = _.cloneDeep(this.copyOfGeoLocations);
                            this.geoLocations = _.cloneDeep(this.copyOfGeoLocations);
                            this.geoLocationscopy = _.cloneDeep(this.copyOfGeoLocations);
                            this.companyManagement.company.city = this.originalStepOne.city;
                            this.companyManagement.company.addressState = this.originalStepOne.addressState;
                            this.companyManagement.company.county = this.originalStepOne.county;
                            if(checkCountyExists(_.cloneDeep(locations), _.cloneDeep(this.originalStepOne))) {
                                this.isCountyExists = true;
                            } else {
                                this.isCountyExists = false;
                            }
                            this.companyManagement.company.country = this.originalStepOne.country;
                            this.companyManagement.company.countryCode = this.originalStepOne.countryCode;
                            this.zipCodeCallCompleted = true;
                        }
                    }
                    this.geoLocations = locations;
                    this.geoLocationscopy = locations;
                    if(!this.geoLocations.length) {
                        this.validzipcode = false;
                        if(!this.invalidZipcode) {
                            this.invalidZipcode = true;
                        }
                        this.companyManagement.company.city = '';
                        this.companyManagement.company.addressState = '';
                        this.companyManagement.company.county = '';
                        this.isCountyExists = false;
                        this.companyManagement.company.country = '';
                        this.zipCodeCallCompleted = true;
                    }
                    this.detectChangesIfAlive();
                    if(typeof callback != "undefined") {
                        return callback(this.geoLocations);
                    }
                },
                (data) => {
                    this.invalidZipcode = true;
                    this.isCountyExists = false;
                    this.detectChangesIfAlive();
                    if(typeof callback != "undefined") {
                        return callback('success');
                    }
                }
            );
        } else {
            this.geoLocations = [];
            this.isCountyExists = false;
            if(typeof callback != "undefined") {
                return callback('success');
            }
        }
    }

    /**
     * Get Address details.
     */
    getZipCodeDetails(zipcode, type) {
        this.nextButtonClicked = false;
        this.saveButtonClicked = false;
        if(this.form.companyDetailsForm.zipcode.$valid) {
            if(!_.isUndefined(zipcode)) {
                this.cancelClicked = false;
            }
            this.validzipcode = true;
            this.zipCodeCalling = true;
            this.zipCodeCallCompleted = false;
            this.getAddressDetails(zipcode, type, (response) => {
                if(!_.isUndefined(this.savedCompanyManagement.company.county) && (this.savedCompanyManagement.company.county !== null)
                    && (this.companyManagement.company.zipcode === this.savedCompanyManagement.company.zipcode)) {
                    this.companyManagement.company.county = this.savedCompanyManagement.company.county;
                    // this.$scope.safeApply();
                }
                if(this.saveButtonClicked) {
                    this.saveButtonTrigger();
                } else if(this.nextButtonClicked) {
                    this.nextButtonTrigger();
                }
            });
        } else {
            this.cancelNotClicked = false;
            this.saveButtonClicked = false;
            this.nextButtonClicked = false;
            this.cities = [];
            this.states = [];
            this.counties = [];
            this.countries = [];
            this.geoLocations = [];
            this.geoLocationscopy = [];
            this.companyManagement.company.city = '';
            this.companyManagement.company.addressState = '';
            this.companyManagement.company.county = '';
            this.isCountyExists = false;
            this.companyManagement.company.country = '';
            this.companyManagement.company.countryCode = '';
        }
    }

    isMinimumLengthGreater(minValue, maxValue) {
        if(maxValue == 'unlimited') {
            return false;
        } else {
            return parseInt(minValue) > parseInt(maxValue);
        }
    }

    isPasswordRestrictionsValid() {
        return (!this.companyManagement.userConfiguration.passwordSettings[0].complexityRestrictionEnabled
            || (this.companyManagement.userConfiguration.passwordSettings[0].complexityRestrictionEnabled
                && $('#checkbox-group-user :checkbox:checked').length > 0))
            &&
            (!this.companyManagement.userConfiguration.passwordSettings[1].complexityRestrictionEnabled
                || (this.companyManagement.userConfiguration.passwordSettings[1].complexityRestrictionEnabled
                    && $('#checkbox-group-administrator :checkbox:checked').length > 0))
            &&
            !this.isMinimumLengthGreater(this.companyManagement.userConfiguration.passwordSettings[0].minimumLength, this.companyManagement.userConfiguration.passwordSettings[0].maximumLength)
            &&
            !this.isMinimumLengthGreater(this.companyManagement.userConfiguration.passwordSettings[1].minimumLength, this.companyManagement.userConfiguration.passwordSettings[1].maximumLength)
    }

    /**
     * This method is used to delete company details
     */
    deleteCompanyDetails() {
        this.getDeleteCompanyDetails();
    }

    getDeleteCompanyDetails() {
        this.companyService.getDeleteCompanyInfo(this.userDetails.company.companyId, (data) => {
            this.changeStateObject = {
                companyId: this.userDetails.company.companyId,
                companyName: this.userDetails.company.companyName,
                candidatesCount: data.numberOfCandiddates,
                requisitionsCount: data.numberOfRequisitions,
                usersCount: data.numberOfUsers,
                clientOrBuCount: data.numberOfClientOrBUs
            }
            this.launchCompanyStateChangeModal('Delete');
        }, (error) => {
            if(this.authService.isOnline) {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            }
        });
    }

    launchCompanyStateChangeModal(changeToState) {
        if(changeToState !== 'Delete') {
            this.changeStateObject = {
                companyId: this.userDetails.company.companyId,
                companyName: this.userDetails.company.companyName
            }
        }
        // Ported to the native BsModalService-based change-company-state
        // modal (22b-4d step 9) - see CompanyPageChangeCompanyStateModalService/
        // CompanyPageChangeCompanyStateModalComponent.
        this.companyPageChangeCompanyStateModalService.show({
            companyInfo: this.changeStateObject,
            changeToState: changeToState
        }, (callbackObject) => {
            if(callbackObject.callbackType == 'delete') {
                this.deleteCompanyDetailsLocally();
            } else if(callbackObject.callbackType == 'stateChange') {
                this.userDetails.company.companyState = changeToState;
                // Template Hold/Soft Hold/Release button [disabled] bindings, and the header
                // company-selector's {{companyStateLabel}}, read authService.userDetails.company.*,
                // which is a different object from this.userDetails (AuthService.getUserDetails()
                // re-parses localStorage into a fresh object on every call) - update it too, and
                // before setCompanyStateLabel() runs, since that reads authService.userDetails.company.companyState
                // to compute the label.
                this.authService.userDetails.company.companyState = changeToState;
                this.companyService.setCompanyStateLabel();
                this.StorageService.set('userDetails', _.cloneDeep(this.userDetails), { 'expires': new Date(Date.now() + this.authService.tokenvalidity) });
                this.getCompany(this.userDetails.company.companyId);
            }
        });
    }

    deleteCompanyDetailsLocally() {
        this.StorageService.remove('companyDetails');
        this.StorageService.remove('SelectedHostCompany');
        this.userDetails.company.companyId = null;
        this.userDetails.company.companyName = null;
        this.userDetails.company.companyType = null;
        this.userDetails.company.companySelected = false;
        this.companyService.companyDeletedShowPopup = true;
        this.StorageService.set('userDetails', _.cloneDeep(this.userDetails), { 'expires': new Date(Date.now() + this.authService.tokenvalidity) });
        this.isCompanyDeleted = true;
        this.$state.go('missioncontrol', null, { reload: true });
    }

    getCompany(companyId) {
        this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_COMPANY + '/' + companyId).subscribe((data) => {
            this.companyManagement = data.body;
            this.CompanyManagementReset = _.cloneDeep(this.companyManagement);
            //Setting the phone number values to be true
            if(data.body.contactList.length == 1) {
                this.isValidPrimaryMobileEntered = true;
                this.isValidPrimaryWorkPhoneEntered = true;
            } else if(data.body.contactList.length > 1) {
                this.isValidPrimaryMobileEntered = true;
                this.isValidPrimaryWorkPhoneEntered = true;
                this.isValidSecondaryMobileEntered = true;
                this.isValidSecondaryWorkPhoneEntered = true;
                this.phoneValidationStatuses.forEach((status, key) => {
                    status.isValidWorkNumber = true;
                    status.isValidMobileNumber = true;
                });
            }

            this.getAddressDetails(data.body.company.zipcode, 'updateaddress');

            this.originalStepOne = _.cloneDeep(this.companyManagement.company);
            this.savedCompanyManagement.company = _.cloneDeep(this.companyManagement.company);
            this.originalStepTwo = _.cloneDeep(this.companyManagement.contactList);
            this.savedCompanyManagement.contactList = _.cloneDeep(this.companyManagement.contactList);
            this.originalStepThree = _.cloneDeep(this.companyManagement.fourDotFiveAdmin);
            this.savedFourDotFiveAdmin = _.cloneDeep(this.companyManagement.fourDotFiveAdmin);

            if(this.companyManagement.fourDotFiveAdmin !== null) {
                //This is satisfied when a company has 4dot5 admin assigned.
                this.oldFourDotFiveAdminId = this.companyManagement.fourDotFiveAdmin.userId;
                this.companyManagement.fourDotFiveAdmin.ticked = true;
                this.originalStepThree.ticked = true;
                this.fourDotFiveAdminsList = [];
                this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_4DOT5_ADMINS).subscribe((data) => {
                    const admins = [];
                    if(this.userDetails.roleScreenRef.role.name == '4dot5SuperUser' || this.userDetails.roleScreenRef.role.name == '4dot5Admin') {
                        data.body.forEach((element) => {
                            if(element.userId == this.companyManagement.fourDotFiveAdmin.userId) {
                                element.ticked = true;
                            } else {
                                element.ticked = false;
                            }
                            admins.push(element);
                        }, this);
                    } else {
                        admins.push(this.companyManagement.fourDotFiveAdmin);
                    }
                    this.fourDotFiveAdminsList = admins;
                    this.get4Dot5AdminFullName();
                }, (error) => {
                    if(this.authService.isOnline) {
                        this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                    }
                });
            } else if(this.isSuperUser(this.userDetails.roleScreenRef.role) && (this.companyManagement.fourDotFiveAdmin == null)) {
                //This is satisfied when a company has is saved as a draft company and hence 4dot5 admin is still not assigned.
                this.get4dot5admins();
            }
            if(data.body.contactList.length > 1) {
                //This condition is satisfied when the selected company has Secondary Contact assigned and hence it has to be shown.
                this.showRemoveButton = true;
                //Setting phone number values to be true.
                this.isValidSecondaryWorkPhoneEntered = true;
                this.isValidSecondaryMobileEntered = true;
            }
        },
            (error) => {
                if(this.authService.isOnline) {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                }
            });
    }

    unCheckSelected4dot5Admin(selectedAdmin) {
        for(var i = 0; i < this.fourDotFiveAdminsList.length; i++) {
            if(this.fourDotFiveAdminsList[i].userId === selectedAdmin.userId) {
                this.fourDotFiveAdminsList[i].ticked = false;
            }
        }
    }

    getUserConfiguration(successCallback?) {
        if(this.companyManagement.userConfiguration == null) {
            this.companyService.getUserConfiguration((configurationResponse) => {
                this.companyManagement.userConfiguration = _.cloneDeep(configurationResponse);
                if(successCallback) {
                    successCallback();
                }
            }, (error) => {
                this.alertsAndNotificationsService.showBannerMessage(error.message, "danger");
            });
        } else {
            if(successCallback) {
                successCallback();
            }
        }
    }

    /**
     * This method is called to get list of 4dot5 Admins companyId
     */
    get4dot5admins() {
        this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_4DOT5_ADMINS).subscribe((data) => {
            this.fourDotFiveAdminsList = data.body;
            this.get4Dot5AdminFullName();
            this.getUserConfiguration();

            //INDUSTRY CODE         getIndustryGroups();
        }, (error) => {
            if(this.authService.isOnline) {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            }
            //INDUSTRY CODE         getIndustryGroups();
        });
    }

    getAllPlans() {
        this.planService.getAllPlans(this.companyManagement.company.companyType, (data) => {
            this.plans = data;
            var priority = 0;
            this.plans.forEach((value, key) => {
                priority++;
                value.enabled = false;
                value.planActionText = "Choose Plan";
                value.priority = value.plan.toLowerCase() == "vendor only" ? 0 : priority;
            });
            this.plansCopy = _.cloneDeep(this.plans);
        }, (error) => {
            if(this.authService.isOnline) {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            }
        });
    }

    setPlanPriorities(planType) {
        var priority = 0;
        //assign chosen plan
        if(planType == 'currentPlan') {
            this.plans.forEach((plan, key) => {
                priority++;
                if(plan.id == this.selectedPlan.planHeaderDto.planFeaturesCapabilityId) {
                    plan.enabled = true;
                    plan.priority = plan.plan.toLowerCase() == "vendor only" ? 0 : priority;
                    plan.planActionText = "Modify Plan";
                    this.savedCurrentActivePlanPriority = plan.priority;
                    this.savedCurrentActivePlanName = plan.plan;
                } else {
                    plan.enabled = false;
                    plan.priority = plan.plan.toLowerCase() == "vendor only" ? 0 : priority;
                    plan.planActionText = "Choose Plan";
                }
            });
        }
        else {
            this.plans.forEach((plan, key) => {
                priority++;
                if(plan.id == this.futurePlan.planHeaderDto.planFeaturesCapabilityId) {
                    this.savedFuturePlanPriority = priority;
                }
            });
        }
    }

    setFuturePlanName() {
        this.plans.forEach((plan, key) => {
            if(plan.id == this.futurePlan.planHeaderDto.planFeaturesCapabilityId) {
                this.futurePlanName = plan.plan;
            }
        });
    }

    /**
     * This method will have a track of which forward recipient is selected
     */
    fourDotFiveAdminChosen() {
        if(!_.isUndefined(this.companyManagement.fourDotFiveAdmin) && !_.isEqual(this.companyManagement.fourDotFiveAdmin, null) && !_.isUndefined(this.selectedFourDotFiveAdmin[0])) {
            if(_.isEqual(this.companyManagement.fourDotFiveAdmin.userId, this.selectedFourDotFiveAdmin[0].userId)) {
                this.unCheckSelected4dot5Admin(this.selectedFourDotFiveAdmin[0]);
                this.companyManagement.fourDotFiveAdmin = {};
                this.form.company4dot5AdminDetailsForm.$setDirty();
            } else {
                this.companyManagement.fourDotFiveAdmin = _.cloneDeep(this.selectedFourDotFiveAdmin[0]);
                this.form.company4dot5AdminDetailsForm.$setDirty();
            }
        } else {
            this.companyManagement.fourDotFiveAdmin = _.cloneDeep(this.selectedFourDotFiveAdmin[0]);
            this.CompanyManagementReset = _.cloneDeep(this.companyManagement);
            this.form.company4dot5AdminDetailsForm.$setDirty();
        }
    }

    updatedAssessementFlowChanges(updatedLicencePrefs) {
        for(var index = 0; index < updatedLicencePrefs.length; index++) {
            if(!(updatedLicencePrefs[index].licensePrefDetailsList[0].disabled == this.AssessmentWorkFlow[index].licensePrefDetailsList[0].disabled)) {
                this.AssessmentWorkFlow[index].disabled = updatedLicencePrefs[index].disabled;
            }
        }
    }

    /**
     * This method is called to get Assesement workflow steps.
     */
    getAssessmentWorkFlow(companyId, successCallback?) {
        this.laststep = true;
        this.isTestLinkReadOnly = true;
        if(!(!this.companyDetailsUpdateMode && !this.ClientOrBuUpdate) || (this.companyDetailsUpdateMode && !this.laststep) || (this.ClientOrBuUpdate && !this.laststep)) {
            this.dataLastLabel = 'Complete';
        }
        if(this.StorageService.get('companyDetails') != null && this.newlyCreatedCompanyId == null) {
            if(!_.isUndefined(this.StorageService.get('companyDetails').companyId)) {
                this.newlyCreatedCompanyId = this.StorageService.get('companyDetails').companyId;
            } else {
                this.newlyCreatedCompanyId = this.StorageService.get('companyDetails');
            }

        }
        if(this.isCompanyAdmin && this.TypeOfCompany == 'Company') {
            this.newlyCreatedCompanyId = this.userDetails.company.companyId;
        }
        if(this.clientOrBuCreationInProgress) {
            //This condition is satisfied when client or bu creation is in progress.
            this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_ASSESSMENT_WORK_FLOW_FOR_CLIENT_BU + '' + this.userDetails.company.companyId + '/' + this.newlyCreatedCompanyId).subscribe((data) => {
                if(!_.isNull(data.body.jobBoardCredentialList)) {
                    data.body.jobBoardCredentialList = this.massageJobBoardCredentials(data.body.jobBoardCredentialList);
                }
                this.originalStepFive = _.cloneDeep(data.body);
                this.preferences = _.cloneDeep(data.body);
                this.massageWorkflowStepsOrder();
                this.jobBoardCredentialListCopy = _.cloneDeep(this.preferences.jobBoardCredentialList);
                this.stepFiveChanged = !(_.isEqual(this.originalStepFive, this.preferences));
                this.licensePreferencesFetched = true;
                if(!this.errorInSavingPrefsCheck) {
                    this.StorageService.set('wizardoperations', true, null);
                    this.stepTrigger('nextbtnclick');
                }
                this.accessTimeOptionsDisabled = !(this.preferences.techAssessmentList.licensePrefDetailsList[0].selected || this.preferences.valueAssessmentList.licensePrefDetailsList[0].selected);
                //_setAccessDisabledForWorkflowSteps();
                if(successCallback) {
                    successCallback();
                }
            }, (error) => {
                if(this.authService.isOnline) {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                }
            });
        } else if(this.companyDetailsUpdateMode) {
            //This condition is satisfied when company update is in progress.
            this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_ASSESSMENT_WORK_FLOW + '' + this.userDetails.company.companyType + '/' + this.userDetails.company.companyId).subscribe((data) => {
                if(!_.isNull(data.body.jobBoardCredentialList)) {
                    data.body.jobBoardCredentialList = this.massageJobBoardCredentials(data.body.jobBoardCredentialList);
                }
                this.originalStepFive = _.cloneDeep(data.body);
                this.savedAssessmentWorkFlow = _.cloneDeep(data.body);
                this.preferences = _.cloneDeep(this.savedAssessmentWorkFlow);
                this.massageWorkflowStepsOrder();
                this.jobBoardCredentialListCopy = _.cloneDeep(this.preferences.jobBoardCredentialList);
                this.licensePreferencesFetched = true;
                this.accessTimeOptionsDisabled = !(this.preferences.techAssessmentList.licensePrefDetailsList[0].selected || this.preferences.valueAssessmentList.licensePrefDetailsList[0].selected);
                this.stepFiveChanged = !(_.isEqual(this.originalStepFive, this.preferences));
                if(!this.errorInSavingPrefsCheck) {
                    this.StorageService.set('wizardoperations', true, null);
                    this.stepTrigger('nextbtnclick');
                }
                if(successCallback) {
                    successCallback();
                }
                //_setAccessDisabledForWorkflowSteps();
            }, (error) => {
                if(this.authService.isOnline) {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                }
            });
        } else if(!this.clientOrBuUpdateInProgress) {
            if(this.newlyCreatedCompanyId == null) {
                this.newlyCreatedCompanyId == this.userDetails.company.companyId;
            }
            this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_ASSESSMENT_WORK_FLOW + '' + this.userDetails.company.companyType + '/' + this.userDetails.company.companyId).subscribe((data) => {
                if(!_.isNull(data.body.jobBoardCredentialList)) {
                    data.body.jobBoardCredentialList = this.massageJobBoardCredentials(data.body.jobBoardCredentialList);
                }
                this.originalStepFive = _.cloneDeep(data.body);
                this.savedAssessmentWorkFlow = _.cloneDeep(data.body);
                this.preferences = _.cloneDeep(this.savedAssessmentWorkFlow);
                this.accessTimeOptionsDisabled = !(this.preferences.techAssessmentList.licensePrefDetailsList[0].selected || this.preferences.valueAssessmentList.licensePrefDetailsList[0].selected);
                this.massageWorkflowStepsOrder();
                this.jobBoardCredentialListCopy = _.cloneDeep(this.preferences.jobBoardCredentialList);
                this.licensePreferencesFetched = true;
                this.stepFiveChanged = !(_.isEqual(this.originalStepFive, this.preferences));

                if(!this.errorInSavingPrefsCheck) {
                    this.StorageService.set('wizardoperations', true, null);
                    this.stepTrigger('nextbtnclick');
                }

                //Company admin can edit only company workflow steps credentials
                if(this.isCompanyAdmin && this.TypeOfCompany == 'Company') {
                    this.isStepReadOnlyForCompanyAdmin = false;
                } else {
                    this.isStepReadOnlyForCompanyAdmin = true;
                }
                if(successCallback) {
                    successCallback();
                }
                //_setAccessDisabledForWorkflowSteps();
            }, (error) => {
                if(this.authService.isOnline) {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                }
            });
        } else {
            if(this.clientOrBuUpdateInProgress) {
                //This is satisfied when client or bu update is in progress
                this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_ASSESSMENT_WORK_FLOW_FOR_CLIENT_BU + '' + this.userDetails.company.companyId + '/' + this.companyManagement.company.id).subscribe((data) => {
                    if(!_.isNull(data.body.jobBoardCredentialList)) {
                        data.body.jobBoardCredentialList = this.massageJobBoardCredentials(data.body.jobBoardCredentialList);
                    }
                    this.originalStepFive = _.cloneDeep(data.body);
                    this.preferences = _.cloneDeep(data.body);
                    this.massageWorkflowStepsOrder();
                    this.jobBoardCredentialListCopy = _.cloneDeep(this.preferences.jobBoardCredentialList);
                    this.licensePreferencesFetched = true;
                    this.stepFiveChanged = !(_.isEqual(this.originalStepFive, this.preferences));
                    this.accessTimeOptionsDisabled = !(this.preferences.techAssessmentList.licensePrefDetailsList[0].selected || this.preferences.valueAssessmentList.licensePrefDetailsList[0].selected);
                    this.StorageService.set('wizardoperations', true, null);
                    this.stepTrigger('nextbtnclick');
                    if(successCallback) {
                        successCallback();
                    }
                    //_setAccessDisabledForWorkflowSteps();
                }, (error) => {
                    if(this.authService.isOnline) {
                        this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                    }
                });
            } else {
                this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_ASSESSMENT_WORK_FLOW + '' + this.userDetails.company.companyType + '/' + companyId).subscribe((data) => {
                    if(!_.isNull(data.body.jobBoardCredentialList)) {
                        data.body.jobBoardCredentialList = this.massageJobBoardCredentials(data.body.jobBoardCredentialList);
                    }
                    this.originalStepFive = _.cloneDeep(data.body);
                    this.preferences = _.cloneDeep(data.body);
                    this.massageWorkflowStepsOrder();
                    this.jobBoardCredentialListCopy = _.cloneDeep(this.preferences.jobBoardCredentialList);
                    this.licensePreferencesFetched = true;
                    this.accessTimeOptionsDisabled = !(this.preferences.techAssessmentList.licensePrefDetailsList[0].selected || this.preferences.valueAssessmentList.licensePrefDetailsList[0].selected);
                    this.stepFiveChanged = !(_.isEqual(this.originalStepFive, this.preferences));
                    this.StorageService.set('wizardoperations', true, null);
                    this.stepTrigger('nextbtnclick');
                    if(successCallback) {
                        successCallback();
                    }
                    //_setAccessDisabledForWorkflowSteps();
                }, (error) => {
                    if(this.authService.isOnline) {
                        this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                    }
                });
            }

        }

        // Get all job source type

        this.genericService.getObjects('api/resume/resumesourcetypes').subscribe((data) => {
            // do not show other and referral source types
            data.body.forEach((val, key) => {
                if(val.sourceType != 'Other' && val.sourceType != 'Referral') {
                    this.jobSourceType.push(val);
                }
            });
        }, (error) => {

        });

        // function _setAccessDisabledForWorkflowSteps() {
        //     if(this.userDetails.roleScreenRef.role.name == 'CorporateAdmin' || this.userDetails.roleScreenRef.role.name == 'StaffingAdmin') {
        //         setTimeout(() =>  {
        //             $('.access').attr({ 'readonly': true, 'disabled': true });
        //         }, 300);
        //     }
        // }

    }

    /**
     * This method is called to get Current active plan details.
     */
    getCurrentPlanDetails(companyId, isMovetoNextStep) {
        this.planService.getCurrentPlan(companyId, (data) => {
            if(data !== "" && !_.isNull(data)) {
                this.selectedPlan = data;
                // this.selectedPlan.planHeaderDto.planStartDateDisplay = this.fourDotFiveDateFormatPipe.transform(this.selectedPlan.planHeaderDto.planStartDate);
                // this.selectedPlan.planHeaderDto.planEndDateDisplay = this.fourDotFiveDateFormatPipe.transform(this.selectedPlan.planHeaderDto.planEndDate);
                this.selectedPlan.planHeaderDto.unformattedStartDate = moment(this.selectedPlan.planHeaderDto.planStartDate).toDate();
                this.selectedPlan.planHeaderDto.unformattedEndDate = moment(this.selectedPlan.planHeaderDto.planEndDate).toDate();
                this.selectedPlan.planHeaderDto.planStartDateDisplay = moment(this.selectedPlan.planHeaderDto.unformattedStartDate).tz(this.selectedPlan.planHeaderDto.timeZoneName).format("MMM DD, YYYY");
                this.selectedPlan.planHeaderDto.planEndDateDisplay = moment(this.selectedPlan.planHeaderDto.unformattedEndDate).tz(this.selectedPlan.planHeaderDto.timeZoneName).format("MMM DD, YYYY");
                this.originalStepFour = _.cloneDeep(this.selectedPlan);
                this.savedCurrentActivePlan = _.cloneDeep(this.selectedPlan);
                this.planDetailsFetched = true;
                this.setPlanPriorities('currentPlan');
                // setPlanPriorities mutates plan entries in place (plan.enabled/planActionText)
                // without reassigning `this.plans` - the modify-plan modal's save callback runs
                // outside the zone tick that originally rendered app-company-page-step-plan's
                // [plans] input, so its *ngFor (and the button color/text it drives) can miss the
                // mutation unless the array reference itself changes here.
                this.plans = [...this.plans];
                this.isActivePlanExists = true;
                this.isPlanChosen = true;
                this.userDetails.plan.planName = this.selectedPlan.planHeaderDto.planName;
                this.userDetails.plan.planType = this.selectedPlan.planHeaderDto.planType;
                this.userDetails.plan.startDate = moment(this.selectedPlan.planHeaderDto.planStartDate);
                this.userDetails.plan.endDate = moment(this.selectedPlan.planHeaderDto.planEndDate);
                this.userDetails.plan.message = this.planService.getPlanExpiryMessage(this.userDetails.plan.planType, this.userDetails.plan.endDate, true);
                this.StorageService.set('userDetails', _.cloneDeep(this.userDetails), { 'expires': new Date(Date.now() + this.authService.tokenvalidity) });
                this.isCompanyVendor = this.companyService.isCompanyVendor();
            } else {
                this.isActivePlanExists = false;
                this.getExpiredPlanDetails(companyId, isMovetoNextStep);
            }
            this.getFuturePlanDetails(companyId, isMovetoNextStep);
            if(isMovetoNextStep) {
                this.StorageService.set('wizardoperations', true, null);
                this.stepTrigger('nextbtnclick');
            }
            this.detectChangesIfAlive();
        },
            (error) => {
                this.isActivePlanExists = false;
                if(isMovetoNextStep) {
                    this.StorageService.set('wizardoperations', true, null);
                    this.stepTrigger('nextbtnclick');
                }
            });
    }

    getExpiredPlanDetails(companyId, isMovetoNextStep) {
        this.planService.getExpiredPlan(companyId, (data) => {
            if(data !== "" && !_.isNull(data)) {
                this.expiredPlan = data;
                // this.expiredPlan.planHeaderDto.planStartDateDisplay = this.fourDotFiveDateFormatPipe.transform(this.expiredPlan.planHeaderDto.planStartDate);
                // this.expiredPlan.planHeaderDto.planEndDateDisplay = this.fourDotFiveDateFormatPipe.transform(this.expiredPlan.planHeaderDto.planEndDate);
                this.expiredPlan.planHeaderDto.unformattedStartDate = moment(this.expiredPlan.planHeaderDto.planStartDate).toDate();
                this.expiredPlan.planHeaderDto.unformattedEndDate = moment(this.expiredPlan.planHeaderDto.planEndDate).toDate();
                this.expiredPlan.planHeaderDto.planStartDateDisplay = moment(this.expiredPlan.planHeaderDto.unformattedStartDate).tz(this.expiredPlan.planHeaderDto.timeZoneName).format("MMM DD, YYYY");
                this.expiredPlan.planHeaderDto.planEndDateDisplay = moment(this.expiredPlan.planHeaderDto.unformattedEndDate).tz(this.expiredPlan.planHeaderDto.timeZoneName).format("MMM DD, YYYY");
                this.planDetailsFetched = true;
                this.isCompanyVendor = this.companyService.isCompanyVendor();
            }
            this.isActivePlanExists = false;
            this.isPlanChosen = false;
        },
            (error) => {
                this.isActivePlanExists = false;
                this.isPlanChosen = false;
                if(isMovetoNextStep) {
                    this.StorageService.set('wizardoperations', true, null);
                    this.stepTrigger('nextbtnclick');
                }
            });
    }

    getFuturePlanDetails(companyId, isMovetoNextStep) {
        this.planService.getFuturePlan(companyId, (data) => {
            this.futurePlan = data;
            if(data !== "" && !_.isNull(data)) {
                // this.futurePlan.planHeaderDto.planStartDateDisplay = this.fourDotFiveDateFormatPipe.transform(this.futurePlan.planHeaderDto.planStartDate);
                // this.futurePlan.planHeaderDto.planEndDateDisplay = this.fourDotFiveDateFormatPipe.transform(this.futurePlan.planHeaderDto.planEndDate);
                this.futurePlan.planHeaderDto.unformattedStartDate = moment(this.futurePlan.planHeaderDto.planStartDate).toDate();
                this.futurePlan.planHeaderDto.unformattedEndDate = moment(this.futurePlan.planHeaderDto.planEndDate).toDate();
                this.futurePlan.planHeaderDto.planStartDateDisplay = moment(this.futurePlan.planHeaderDto.unformattedStartDate).tz(this.futurePlan.planHeaderDto.timeZoneName).format("MMM DD, YYYY");
                this.futurePlan.planHeaderDto.planEndDateDisplay = moment(this.futurePlan.planHeaderDto.unformattedEndDate).tz(this.futurePlan.planHeaderDto.timeZoneName).format("MMM DD, YYYY");
                this.planDetailsFetched = true;
                this.setPlanPriorities('futurePlan');
                this.plans = [...this.plans];
                this.isFuturePlanExists = true;
                this.setFuturePlanName();
            } else {
                this.isFuturePlanExists = false;
            }
            this.disableActionButtons = false;
            this.detectChangesIfAlive();
        },
            (error) => {
                this.isFuturePlanExists = false;
                if(isMovetoNextStep) {
                    this.StorageService.set('wizardoperations', true, null);
                    this.disableActionButtons = false;
                    this.stepTrigger('nextbtnclick');
                }
            });
    }

    /**
     * This method is called to get list of Company Types.
     */
    getCompanyTypes() {
        let companyTypes = ['Corporation', 'StaffingCompany'];
        companyTypes.forEach((element) => {
            //Puts in a space before an uppercase letter                    this.companyTypes.push(element.replace(/([A-Z])/g, ' $1').trim());
            this.companyTypes.push(element);
        }, this);
        if(this.userDetails.company.companyId == null) {
            this.get4dot5admins();
            // this.getAllPlans();
        } else {
            // this.getAllPlans();
        }
    }

    editTestLink() {
        this.isTestLinkReadOnly = false;
    }

    massageJobBoardCredentials(jobBoardCredentialList) {
        jobBoardCredentialList.forEach((jobBoardCredential, key) => {
            jobBoardCredential.editable = true;
        });
        return jobBoardCredentialList;
    }

    /**
     * This function is called to the list on industry group list
     */
    /*   INDUSTRY CODE         function getIndustryGroups() {
                    var clientIdToGetIndustryList = 0;
                    if (this.ClientOrBuUpdate) {
                        clientIdToGetIndustryList = this.companyManagement.company.id;
                        this.industryListOfCompany = this.userDetails.company.companyId;
                    } else if (this.userDetails.company.companyId != null) {
                        this.industryListOfCompany = this.userDetails.company.companyId;
                    }
                    if (this.TypeOfCompany == 'BusinessUnit' || this.TypeOfCompany == 'Client' || this.ClientOrBuUpdate) {
                        this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_INDUSTRY_GROUPS + '/' + this.industryListOfCompany + '/' + clientIdToGetIndustryList).subscribe(function (data) {
                            this.IndustryGroupList = [];
                            industryMasterList = data.body;
                            data.body.forEach(function (element) {
                                this.IndustryGroupList.push(element.name);
                                if (element.selected) {
                                    this.industryGroup = element.name;
                                    this.IndustrySubList = element.industryDtos;
                                }
                                element.industryDtos.forEach(function (elem) {
                                    if (elem.selected == true) {
                                        this.industriesInPanel.push(elem);
                                        elem.selected = false;
                                    }
                                }, this);
                            }, this);
                            this.originalIndustriesPanel = _.cloneDeep(this.industriesInPanel);
                        }, function (error) {
                        if (error.status === 500) {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                }
                        });
                    } else {//Get industries during staffing company update and create.
                        this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_INDUSTRY_GROUPS + '/' + this.industryListOfCompany).subscribe(function (data) {
                            this.IndustryGroupList = [];
                            industryMasterList = data.body;
                            this.companyManagement.company.industryList = [];
                            data.body.forEach(function (element) {
                                this.IndustryGroupList.push(element.name);
                                if (element.selected) {
                                    this.industryGroup = element.name;
                                    this.IndustrySubList = element.industryDtos;
                                }
                                element.industryDtos.forEach(function (elem) {
                                    if (elem.selected == true) {
                                        this.industriesInPanel.push(elem);
                                        elem.selected = false;
                                    }
                                }, this);
                            }, this);
                            this.originalIndustriesPanel = _.cloneDeep(this.industriesInPanel);
                        }, function (error) {
                        if (error.status === 500) {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                }
                        });
                    }
                }*/

    /**
     * Change as per selection,get sublist of industries belonging to the group.
     */
    /*   INDUSTRY CODE         this.getIndustrySublist = function (industrygroup) {
                    if (this.userDetails.company.companyId != null && !this.ClientOrBuCreate) {
                        if (this.originalStepOne.industryGroup == industrygroup) {
                        }
                    }
                    if (!_.isEqual(this.selectedIndustryGroup, industrygroup)) {
                        industryMasterList.forEach(function (element) {
                            if (element.name == industrygroup) {
                                element.selected = true;
                                this.IndustrySubList = element.industryDtos;
                                this.selectedSubGroup = true;
                            } else {
                                if (_.isEqual(element.name, this.selectedIndustryGroup)) {
                                    element.selected = false;
                                    element.industryDtos.forEach(function (industryDto) {
                                        if (industryDto.selected === true) {
                                            industryDto.selected = false;
                                        }
                                    }, this);
                                }
                            }
                        }, this);
                    }

                    if (this.ClientOrBuCreate) {
                        industryMasterList.forEach(function (element) {
                            if (_.isEqual(element.name, industrygroup)) {
                                element.selected = true;
                                this.IndustrySubList = element.industryDtos;
                                this.IndustrySubList.forEach(function (elem) {
                                    elem.selected = false;
                                }, this);
                                this.selectedSubGroup = true;
                            }
                        }, this);
                    }

                    this.selectedIndustryGroup = _.cloneDeep(industrygroup);
                }*/


    /**
     * This method works for triggerng
     * button clicks and resetting timeout
     * and remaining variables to normal
     * @access for create, view and update
     */
    stepTrigger(target) {
        if(target === 'nextbtnclick') {
            var nextbtnclick = setTimeout(() => {
                $('.btn-next').triggerHandler('click');
                this.StorageService.remove('wizardoperations');

                this.triggerCount = 0;
                this.formsubmitted = false;
                clearTimeout(nextbtnclick);
            }, 0);
        } else if(target === 'cancelflow') {
            var steponeclick = setTimeout(() => {
                $('#1').trigger('click');
                this.triggerCount = 0;
                this.formsubmitted = false;
                this.StorageService.remove('wizardoperations');
                clearTimeout(steponeclick);
            }, 500);
        }
    }

    /**
     * Function to save Step 1 Company Details.
     */
    saveCompanyDetails() {
        if(this.StorageService.get('clientOrBuCreationInProgress') !== null) {
            if(this.userDetails.company.companyType == 'Corporation') {
                this.TypeOfCompany = 'BusinessUnit';
                this.companyManagement.company.companyType = 'BusinessUnit';
            } else if(this.userDetails.company.companyType == 'StaffingCompany') {
                this.TypeOfCompany = 'Client';
                this.companyManagement.company.companyType = 'Client';
            }
        }
        if(this.$stateParams.vendorId !== null) {
            if(this.userDetails.company.companyType == 'Corporation') {
                this.TypeOfCompany = 'Vendor';
            }
        }

        if((this.StorageService.get('companyDetails') == null || this.companyDetailsUpdateMode || this.clientOrBuCreationInProgress || this.clientOrBuUpdateInProgress || this.vendorUpdateInProgress) && this.stepOneChanged) {
            //Building the SavedCompanyManagement object before saving.
            this.savedCompanyManagementCopy.company = _.cloneDeep(this.savedCompanyManagement.company);
            this.savedCompanyManagement.company = _.cloneDeep(this.companyManagement.company);
            this.limitCharactersInName();
            this.disableActionButtons = true;
            this.genericService.addObject(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.SAVE_COMPANY + '/' + this.loggedInUserDetails.company.companyId, this.savedCompanyManagement).subscribe((data) => {
                this.disableActionButtons = false;
                this.originalStepOne = _.cloneDeep(data.body.company);
                this.nextButtonFlag++;
                this.isCompanyDetailsSaveCalled = true;
                this.savedCompanyManagement.company = _.cloneDeep(data.body.company);
                this.companyManagement.company = data.body.company;
                // INDUSTRY CODE                      this.originalIndustriesPanel = _.cloneDeep(this.originalStepOne.industryList);
                // INDUSTRY CODE                     delete this.originalStepOne['industryGroup'];
                // INDUSTRY CODE                   delete this.originalStepOne['industryList'];

                this.companyManagement.company.name = data.body.company.name;
                this.companyManagement.company.id = data.body.company.id;
                this.companyManagement.companyState = data.body.companyState;
                this.savedCompanyManagement.company.id = data.body.company.id;
                this.savedCompanyManagement.companyState = data.body.companyState;
                this.newlyCreatedCompanyId = data.body.company.id;
                this.companyService.newlyCreatedCompanyId = this.newlyCreatedCompanyId;

                if(this.TypeOfCompany != 'Client' && this.TypeOfCompany != 'BusinessUnit') {
                    this.userDetails.company.companyId = this.newlyCreatedCompanyId;
                    this.userDetails.company.companyName = data.body.company.name;
                    this.userDetails.company.companyType = data.body.company.companyType;
                    this.userDetails.company.companyState = data.body.companyState;
                    this.companyService.setCompanyStateLabel();
                    this.userDetails.company.companySelected = true;
                    this.StorageService.set('userDetails', _.cloneDeep(this.userDetails), { 'expires': new Date(Date.now() + this.authService.tokenvalidity) });
                    this.disableActionButtons = false;
                }
                if(this.TypeOfCompany == 'Client' || this.TypeOfCompany == 'BusinessUnit') {
                    this.getManagers(() => {
                        this.disableActionButtons = false;
                    });
                    this.assignClientToStaffingCompany('clickonclientnext');
                }
                if(this.clientOrBuUpdateInProgress) {
                    this.editCompanyName = data.body.company.name.substring(0, 57);
                    this.editCompanyNameOnHover = data.body.company.name;
                    if(data.body.company.name.length > 57) {
                        this.editCompanyName = this.editCompanyName + '...';
                    }
                }
                if(!(this.TypeOfCompany == 'Client') && !(this.TypeOfCompany == 'BusinessUnit')) {
                    this.companyStepOneSaved = true;
                    this.StorageService.set('wizardoperations', true, null);
                    this.stepTrigger('nextbtnclick');
                }

            }, (error) => {
                this.triggerCount = 0;
                this.disableActionButtons = false;
                if(error.statusCode === "409") {
                    this.errorOcurred = true;
                    if(!_.isUndefined(this.companyManagement.companyState)) {
                        if(error.developerMsg === "101") {
                            this.companyManagement.company.companyShortName = this.originalStepOne.companyShortName;
                        } else if(error.developerMsg === "102") {
                            this.companyManagement.company.name = this.originalStepOne.name;
                        } else if(error.developerMsg === "100") {
                            this.companyManagement.company.companyShortName = this.originalStepOne.companyShortName;
                            this.companyManagement.company.name = this.originalStepOne.name;
                        } else {
                            this.companyManagement.company.companyShortName = this.originalStepOne.companyShortName;
                            this.companyManagement.company.name = this.originalStepOne.name;
                        }
                    } else {
                        if(error.developerMsg === "101") {
                            this.companyManagement.company.companyShortName = '';
                        } else if(error.developerMsg === "102") {
                            this.companyManagement.company.name = '';
                        } else if(error.developerMsg === "100") {
                            this.companyManagement.company.companyShortName = '';
                            this.companyManagement.company.name = '';
                        }
                    }
                }
                if(this.authService.isOnline) {
                    this.alertsAndNotificationsService.showBannerMessage(error.error.message, 'danger');
                }
                this.savedCompanyManagement.company = _.cloneDeep(this.savedCompanyManagementCopy.company);
            });
        } else if(!this.stepOneChanged) {
            this.companyStepOneSaved = true;
            this.getManagers(() => {
                this.disableActionButtons = false;
            });
            this.StorageService.set('wizardoperations', true, null);
            this.triggerCount = 0;
        }
    }

    /**
     * Function to save Step 2 details(Company Contact Person Details)
     */
    saveContactDetails() {
        if(this.newlyCreatedCompanyId == null && (this.clientOrBuUpdateInProgress || this.vendorUpdateInProgress)) {
            this.newlyCreatedCompanyId = this.companyManagement.company.id;
        }
        if((this.StorageService.get('companyDetails') == null || this.companyDetailsUpdateMode || this.clientOrBuCreationInProgress || this.clientOrBuUpdateInProgress || this.vendorUpdateInProgress) && this.stepTwoChanged) {
            //Building the SavedCompanyManagement object before saving.
            this.savedCompanyManagementCopy.contactList = _.cloneDeep(this.savedCompanyManagement.contactList);
            this.savedCompanyManagement.contactList = _.cloneDeep(this.companyManagement.contactList);
            this.limitCharactersInName();
            if(!this.vendorUpdateInProgress && !this.isAssociatedClientUpdateInProgress) {
                if(this.clientOrBuCreationInProgress) {
                    this.savedCompanyManagement.contactList.forEach((contact, key) => {
                        if(contact.userId == undefined) {
                            contact.userId = null;
                        }
                    });
                }
                this.disableActionButtons = true;
                this.genericService.addObject(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.SAVE_COMPANY + '/' + this.userDetails.company.companyId, this.savedCompanyManagement).subscribe((data) => {
                    this.nextButtonFlag++;
                    this.originalStepTwo = _.cloneDeep(data.body.contactList);
                    this.savedCompanyManagement.contactList = _.cloneDeep(data.body.contactList);
                    this.companyManagement.contactList = _.cloneDeep(data.body.contactList);
                    if(this.vendorUpdateInProgress) {
                        this.laststep = true;
                        this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.VENDOR.UPDATESUCCESS, 'success');
                        this.$state.go('missioncontrol.vendors', null, { reload: true });
                    }
                    if(this.clientOrBuUpdateInProgress) {
                        this.laststep = true;
                        // this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.UPDATESUCCESS, 'success');
                    }
                    if(this.clientOrBuCreationInProgress) {
                        this.laststep = true;
                        // this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CLIENT+" '<b><i class='wrap-word'>"+this.savedCompanyManagement.company.name+"</i></b>' "+this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CREATEDRAFTSUCCESS, 'success')
                    }
                    if(this.userDetails.company.companyType == 'Host') {
                        this.laststep = true;
                        this.dataLastLabel = 'Complete';
                    }
                    if(this.TypeOfCompany == 'Client' || this.TypeOfCompany == 'BusinessUnit' || this.clientOrBuUpdateInProgress || this.clientOrBuCreationInProgress || this.StorageService.get('SelectedHostCompany') != null) {
                        if(!this.licensePreferencesFetched || this.isCompanyDetailsSaveCalled) {
                            this.getAssessmentWorkFlow(this.userDetails.company.companyId, () => {
                                this.disableActionButtons = false;
                            });
                        } else {
                            this.disableActionButtons = false;
                            this.StorageService.set('wizardoperations', true, null);
                            this.stepTrigger('nextbtnclick');
                        }
                    } else {
                        if(data.body.userConfiguration == null) {
                            this.getUserConfiguration(() => {
                                this.companyStepTwoSaved = true;
                                this.StorageService.set('wizardoperations', true, null);
                                this.disableActionButtons = false;
                                this.stepTrigger('nextbtnclick');
                            });
                        } else {
                            this.companyStepTwoSaved = true;
                            this.StorageService.set('wizardoperations', true, null);
                            this.disableActionButtons = false;
                            this.stepTrigger('nextbtnclick');
                        }
                    }
                }, (error) => {
                    if(this.authService.isOnline) {
                        this.alertsAndNotificationsService.showBannerMessage(error.error.message, 'danger');
                    }
                    this.disableActionButtons = false;
                    this.savedCompanyManagement.contactList = _.cloneDeep(this.savedCompanyManagementCopy.contactList);
                });
            } else if(this.isAssociatedClientUpdateInProgress) {
                var clientObject: any = {};
                clientObject.corporateCompanyId = this.newlyCreatedCompanyId;
                clientObject.vendorId = this.userDetails.company.companyId;
                clientObject.corporateCompanyContactList = _.cloneDeep(this.getParsedContacts(this.savedCompanyManagement.contactList));
                this.companyService.updateClientCorporateContactDetails(clientObject, (data) => {
                    data.contactList = _.cloneDeep(data.corporateCompanyContactList);
                    this.nextButtonFlag++;
                    this.originalStepTwo = _.cloneDeep(data.contactList);
                    this.savedCompanyManagement.contactList = _.cloneDeep(data.contactList);
                    this.companyManagement.contactList = _.cloneDeep(data.contactList);
                    if(!this.licensePreferencesFetched || this.isCompanyDetailsSaveCalled) {
                        this.getAssessmentWorkFlow(this.userDetails.company.companyId);
                    } else {
                        this.StorageService.set('wizardoperations', true, null);
                        this.stepTrigger('nextbtnclick');
                    }
                }, (error) => {
                    if(this.authService.isOnline) {
                        this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                    }
                    this.savedCompanyManagement.contactList = _.cloneDeep(this.savedCompanyManagementCopy.contactList);
                });
            } else {
                var vendorObject: any = {};
                vendorObject.corporateCompanyId = this.userDetails.company.companyId;
                vendorObject.vendorId = this.newlyCreatedCompanyId;
                vendorObject.contactList = _.cloneDeep(this.getParsedContacts(this.savedCompanyManagement.contactList));
                this.companyService.saveCorporateVendor(vendorObject, (data) => {
                    data.contactList = _.cloneDeep(this.getParsedCompanyContacts(data.contactList));
                    this.nextButtonFlag++;
                    this.originalStepTwo = _.cloneDeep(data.contactList);
                    this.savedCompanyManagement.contactList = _.cloneDeep(data.contactList);
                    this.companyManagement.contactList = _.cloneDeep(data.contactList);
                    if(this.vendorUpdateInProgress) {
                        this.laststep = true;
                        this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.VENDOR.UPDATESUCCESS, 'success');
                        this.$state.go('missioncontrol.vendors', null, { reload: true });
                    }
                }, (error) => {
                    if(this.authService.isOnline) {
                        this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                    }
                    this.savedCompanyManagement.contactList = _.cloneDeep(this.savedCompanyManagementCopy.contactList);
                });
            }

        } else if(!this.stepTwoChanged) {
            if(this.StorageService.get('SelectedHostCompany') != null) {
                this.laststep = true;
                this.dataLastLabel = 'Complete';
            }
            if(this.TypeOfCompany == 'Client' || this.TypeOfCompany == 'BusinessUnit' || this.userDetails.company.companyType == 'Host') {
                this.laststep = true;
                this.disableActionButtons = true;
                this.getAssessmentWorkFlow(this.userDetails.company.companyId, () => {
                    this.disableActionButtons = false;
                });
            } else if(this.TypeOfCompany == 'Vendor') {
                this.alertsAndNotificationsService.showBannerMessage('Nothing to be updated!', 'info');
                this.$state.go('missioncontrol.vendors', null, { reload: true });
            } else {
                this.companyStepTwoSaved = true;
                this.triggerCount = 0;
                this.StorageService.set('wizardoperations', true, null);
            }
        }
    }

    /**
     * Function to save Step 3 details(Company 4Dot5 Admin Details)
     */
    saveFourDotFiveAdminDetails() {
        if((this.StorageService.get('companyDetails') == null || this.companyDetailsUpdateMode) && this.stepThreeChanged) {

            //Building the SavedCompanyManagement object before saving.
            this.savedFourDotFiveAdminCopy = _.cloneDeep(this.savedFourDotFiveAdmin);
            this.savedFourDotFiveAdmin = _.cloneDeep(this.selectedFourDotFiveAdmin);
            this.limitCharactersInName();
            this.disableActionButtons = true;
            this.genericService.addObject(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.UPDATE_COMPANY_FOR_FOURDOTFIVE_ADMIN + '/' + this.savedFourDotFiveAdmin[0].userId + '/' + this.userDetails.company.companyId + '?oldfourdotfiveadmin=' + this.oldFourDotFiveAdminId, this.savedFourDotFiveAdmin).subscribe((data) => {
                this.originalStepThree = _.cloneDeep(data.body.fourDotFiveAdmin);
                this.savedFourDotFiveAdmin = _.cloneDeep(data.body.fourDotFiveAdmin);

                if(this.companyDetailsUpdateMode == true) {
                    this.companyManagement.fourDotFiveAdmin = _.cloneDeep(data.body.fourDotFiveAdmin);
                    this.CompanyManagementReset.fourDotFiveAdmin = _.cloneDeep(data.body.fourDotFiveAdmin);
                    // this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.UPDATESUCCESS, 'success')
                } else {
                    //this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.CREATEDRAFTSUCCESS, 'success');
                }
                this.userConfigurationCopy = _.cloneDeep(this.companyManagement.userConfiguration);
                this._saveUserConfiguration((data) => {
                    this.nextButtonFlag++;
                    this.companyManagement.userConfiguration = _.cloneDeep(data);
                    this.CompanyManagementReset.userConfiguration = _.cloneDeep(data);
                    this.companyStepThreeSaved = true;
                    this.getCurrentPlanDetails(this.loggedInUserDetails.company.companyId, true);
                    this.laststep = true;
                }, (error) => {
                    if(this.authService.isOnline) {
                        this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                    }
                    this.disableActionButtons = false;
                });

            }, (error) => {
                if(this.authService.isOnline) {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                }
                this.disableActionButtons = false;
                this.savedFourDotFiveAdmin = _.cloneDeep(this.savedFourDotFiveAdminCopy);
            });
        } else if(!this.stepThreeChanged) {
            this.laststep = true;
            if(!this.licensePreferencesFetched || this.isCompanyDetailsSaveCalled) {
                this.getCurrentPlanDetails(this.loggedInUserDetails.company.companyId, true);
            } else {
                this.StorageService.set('wizardoperations', true, null);
                this.triggerCount = 0;
            }
        }
    }

    _saveUserConfiguration(successCallback, errorCallback) {
        this.companyService.saveUserConfiguration(this.companyManagement.userConfiguration, this.userDetails.company.companyId, (data) => {
            if(successCallback) {
                successCallback(data);
            }
        }, (error) => {
            if(errorCallback) {
                errorCallback(error);
            }
        });
    }

    /**
     * Function to save Step 4 details(Company Plan)
     */
    savePlanDetails() {
        if((this.StorageService.get('companyDetails') == null || this.companyDetailsUpdateMode) && this.stepFourChanged) {

            this.savedCurrentActivePlanCopy = _.cloneDeep(this.selectedPlan);
            this.savedCurrentActivePlan = _.cloneDeep(this.selectedPlan);
            this.disableActionButtons = true;
            this.planService.savePlan(this.selectedPlan, (data) => {
                this.nextButtonFlag++;
                // data.planHeaderDto.planStartDateDisplay = this.fourDotFiveDateFormatPipe.transform(data.planHeaderDto.planStartDate);
                // data.planHeaderDto.planEndDateDisplay = this.fourDotFiveDateFormatPipe.transform(data.planHeaderDto.planEndDate);
                data.planHeaderDto.unformattedStartDate = moment(data.planHeaderDto.planStartDate).toDate();
                data.planHeaderDto.unformattedEndDate = moment(data.planHeaderDto.planEndDate).toDate();
                data.planHeaderDto.planStartDateDisplay = moment(data.planHeaderDto.unformattedStartDate).tz(data.planHeaderDto.timeZoneName).format("MMM DD, YYYY");
                data.planHeaderDto.planEndDateDisplay = moment(data.planHeaderDto.unformattedEndDate).tz(data.planHeaderDto.timeZoneName).format("MMM DD, YYYY");
                this.originalStepFour = _.cloneDeep(data);
                this.savedCurrentActivePlan = _.cloneDeep(data);
                this.selectedPlan = _.cloneDeep(data);
                this.isActivePlanExists = true;
                this.companyStepFourSaved = true;
                this.getAssessmentWorkFlow(this.loggedInUserDetails.company.companyId, () => {
                    this.disableActionButtons = false;
                });
                this.laststep = true;
            }, (error) => {
                if(this.authService.isOnline) {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                }
                this.disableActionButtons = false;
                // this.savedFourDotFiveAdmin = _.cloneDeep(this.savedFourDotFiveAdminCopy);
            });
        } else if(!this.stepFourChanged) {
            this.laststep = true;
            if(!this.licensePreferencesFetched || this.isCompanyDetailsSaveCalled) {
                this.disableActionButtons = true;
                this.getAssessmentWorkFlow(this.loggedInUserDetails.company.companyId, () => {
                    this.disableActionButtons = false;
                });
            } else {
                this.StorageService.set('wizardoperations', true, null);
                this.stepTrigger('nextbtnclick');
                //Company admin can edit only company workflow steps credentials
                if(this.isCompanyAdmin && this.TypeOfCompany == 'Company') {
                    this.isStepReadOnlyForCompanyAdmin = false;
                } else {
                    this.isStepReadOnlyForCompanyAdmin = true;
                }
            }
        }
    }

    /**
     * Function to limit characters in name.
     */
    limitCharactersInName() {
        this.delimitedCompanyOrClientName = this.savedCompanyManagement.company.name;
        //                if (this.savedCompanyManagement.company.name.length > 15) {
        //                    this.delimitedCompanyOrClientName = this.delimitedCompanyOrClientName + '....';
        //                }
    }

    getParsedCompanyContacts(contactList) {
        var parsedContactList = [];
        contactList.forEach((contact, key) => {
            var parsedContact: any = {};
            parsedContact.email = contact.email;
            parsedContact.firstName = contact.firstname;
            parsedContact.lastName = contact.lastname;
            parsedContact.im = contact.im;
            parsedContact.isPrimary = contact.isPrimary;
            parsedContact.mobilePhone = contact.mobilephone;
            parsedContact.workPhone = contact.workphone;
            parsedContactList.push(parsedContact);
        });
        return parsedContactList;
    }

    getParsedContacts(contactList) {
        var parsedContactList = [];
        contactList.forEach((contact, key) => {
            var parsedContact: any = {};
            parsedContact.email = contact.email;
            parsedContact.firstname = contact.firstName;
            parsedContact.lastname = contact.lastName;
            parsedContact.im = contact.im;
            parsedContact.isPrimary = contact.isPrimary;
            parsedContact.mobilephone = contact.mobilePhone;
            parsedContact.workphone = contact.workPhone;
            parsedContactList.push(parsedContact);
        });
        return parsedContactList;
    }

    getManagers(successCallback) {
        this.userService.getPossibleManagersForHr(this.userDetails.company.companyId, (data) => {
            this.managers = data;
            if(successCallback) {
                successCallback();
            }
        }, (error) => {
            //do nothing
        });
    }

    /**
     *
     */
    assignClientToStaffingCompany(clickfromclientsaveornext) {
        //This condition satisfy when a company from popup is selected and user tries to edit a client/bu for selected company
        if(this.clientOrBuUpdateInProgress) {
            this.newlyCreatedCompanyId = this.companyManagement.company.id;
        }
        if(this.StorageService.get('companyDetails') != null) {
            if(!_.isUndefined(this.StorageService.get('companyDetails').companyId)) {
                this.assignClient2StaffingCompany = this.StorageService.get('companyDetails').companyId;
            } else {
                this.assignClient2StaffingCompany = this.StorageService.get('companyDetails');
            }

        } else {
            this.assignClient2StaffingCompany = this.loggedInUserDetails.company.companyId;
        }
        this.genericService.addObject(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.ASSIGN_CLIENT_TO_STAFFING_COMPANY + '/' + this.assignClient2StaffingCompany + '/' + this.newlyCreatedCompanyId, null).subscribe((data) => {
            this.nextButtonFlag++;
            if(this.clientOrBuUpdateInProgress) {
                //  this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.UPDATESUCCESS, 'success')
            } else if(this.clientOrBuCreationInProgress && clickfromclientsaveornext == 'clickonclientsave') {
                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CLIENT + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CREATEDRAFTSUCCESS, 'success')
            }
            this.companyStepOneSaved = true;
            if(!(clickfromclientsaveornext == 'clickonclientsave')) {
                this.StorageService.set('wizardoperations', true, null);
                this.stepTrigger('nextbtnclick');
            }
        }, (error) => {
            if((error.status === 500) && this.authService.isOnline) {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            } else {
                if(this.clientOrBuUpdateInProgress) {
                    // this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.UPDATESUCCESS, 'success')
                } else if(this.clientOrBuCreationInProgress && clickfromclientsaveornext == 'clickonclientsave') {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CLIENT + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CREATEDRAFTSUCCESS, 'success')
                }
                this.companyStepOneSaved = true;
                if(!(clickfromclientsaveornext == 'clickonclientsave')) {
                    this.StorageService.set('wizardoperations', true, null);
                    this.stepTrigger('nextbtnclick');
                }
            }
        });
    }

    /**
     * Change state of Staffing company to 'Active'.
     */
    changeCompanyState(successCallback?, errorCallback?) {
        this.genericService.addObject(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.CHANGE_COMPANYSTATE + '/' + this.newlyCreatedCompanyId, this.companyManagement).subscribe((data) => {
            this.nextButtonFlag++;
            this.userDetails.company.companyState = data.body.companyState;
            this.companyManagement.companyState = data.body.companyState;
            this.companyService.setCompanyStateLabel();
            this.StorageService.set('userDetails', _.cloneDeep(this.userDetails), { 'expires': new Date(Date.now() + this.authService.tokenvalidity) });
            if(successCallback) {
                successCallback();
            }
        }, (error) => {
            if(this.authService.isOnline) {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            }
            if(errorCallback) {
                errorCallback(error);
            }
        });
    }

    /**
     * Function to save Step 5 details(Company assessement Flow Details)
     */
    saveAssessmentFlowDetails() {
        var clientOrCompanyLicensePrefsUrl;
        //This condition satisfy when a company from popup is selected and user tries to create a client/bu for selected company
        if(this.StorageService.get('companyDetails') != null && this.newlyCreatedCompanyId == null && !this.clientOrBuUpdateInProgress) {
            if(!_.isUndefined(this.StorageService.get('companyDetails').companyId)) {
                this.newlyCreatedCompanyId = this.StorageService.get('companyDetails').companyId;
            } else {
                this.newlyCreatedCompanyId = this.StorageService.get('companyDetails');
            }
        }

        if(this.isCompanyAdmin && this.TypeOfCompany == 'Company') {
            this.newlyCreatedCompanyId = this.userDetails.company.companyId;
        }
        //This condition satisfy when a company from popup is selected and user tries to edit a client/bu for selected company
        if(this.StorageService.get('companyDetails') == null && this.newlyCreatedCompanyId == null && this.companyManagement.company.companyType != 'Host') {
            if(this.StorageService.get('companyToBeUpdated') == null) {
                this.newlyCreatedCompanyId = this.userDetails.company.companyId;
            } else {
                this.newlyCreatedCompanyId = this.StorageService.get('companyToBeUpdated').company.id;
            }
        }

        //This condition will satisfy if editing host company
        if(this.companyManagement.company.companyType == 'Host') {
            this.newlyCreatedCompanyId = this.userDetails.company.companyId;
        }

        //This condition will satisfy if editing a client/bu
        if(this.clientOrBuUpdateInProgress) {
            this.newlyCreatedCompanyId = this.companyManagement.company.id;
        }


        if((this.StorageService.get('companyDetails') == null && !this.clientOrBuCreationInProgress && !this.clientOrBuUpdateInProgress)) {
            this.newCompanyCreation = true;
        }

        if((this.StorageService.get('companyDetails') == null || this.companyDetailsUpdateMode || this.companyDetailsUpdateModeByCompanyAdmin || this.clientOrBuCreationInProgress || this.clientOrBuUpdateInProgress || this.assessmentOrJobBoardCredentialsUpdated) && this.stepFiveChanged) {
            if(this.clientOrBuCreationInProgress || this.clientOrBuUpdateInProgress) {
                clientOrCompanyLicensePrefsUrl = this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.SET_LICENCE_PREFERENCES_FOR_CLIENT_BU + '/' + this.userDetails.company.companyId + '/' + this.newlyCreatedCompanyId;
            } else {
                clientOrCompanyLicensePrefsUrl = this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.SET_LICENCE_PREFERENCES + '/' + this.companyManagement.company.companyType + '/' + this.newlyCreatedCompanyId;
            }
            //Building the SavedCompanyManagement object before saving.
            this.savedAssessmentWorkFlowCopy = _.cloneDeep(this.savedAssessmentWorkFlow);
            this.savedAssessmentWorkFlow = _.cloneDeep(this.preferences);
            this.tempObj = _.cloneDeep(this.originalStepFive);
            this.limitCharactersInName();
            // this.disableActionButtons = true;
            this.genericService.addObject(clientOrCompanyLicensePrefsUrl, this.savedAssessmentWorkFlow).subscribe((data) => {
                //                        this.nextButtonFlag++;
                if(!_.isNull(data.body.licensePreferences.jobBoardCredentialList)) {
                    data.body.licensePreferences.jobBoardCredentialList = this.massageJobBoardCredentials(data.body.licensePreferences.jobBoardCredentialList);
                }
                this.companyManagement.licensePreferences = _.cloneDeep(data.body.licensePreferences);
                this.originalStepFive = _.cloneDeep(data.body.licensePreferences);
                this.savedAssessmentWorkFlow = _.cloneDeep(data.body.licensePreferences);
                this.massageWorkflowStepsOrder();
                this.isTestLinkReadOnly = true;

                if((this.companyDetailsUpdateMode || this.companyDetailsUpdateModeByCompanyAdmin) == true && this.stepFiveChanged) {
                    // if(this.userDetails.roleScreenRef.role.name != 'CorporateAdmin' && this.userDetails.roleScreenRef.role.name != 'StaffingAdmin') {
                    if(!_.isEqual(this.tempObj, this.savedAssessmentWorkFlow) || (this.nextButtonFlag > 0) || (this.saveButtonFlag > 0)) {
                        this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.UPDATESUCCESS, 'success');
                    } else {
                        this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.NOTHING_TO_UPDATED, 'success');
                    }
                    // }
                } else if(!this.clientOrBuUpdateInProgress && this.TypeOfCompany == 'Client') {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CLIENT + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CREATESUCCESS, 'success')
                } else if(this.TypeOfCompany == 'BusinessUnit') {
                    this.disableActionButtons = true;
                    this.companyService.getAllBuVendors(this.userDetails.company.companyId, this.newlyCreatedCompanyId, (data) => {
                        this.originalStepSix = _.cloneDeep(data);
                        this.savedVendors = _.cloneDeep(data);
                        this.vendors = _.cloneDeep(data);
                        this.disableActionButtons = false;
                        this.stepTrigger('nextbtnclick');

                    }, (error) => {
                        this.disableActionButtons = false;
                    });
                    // this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.BU.BU + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.BU.CREATESUCCESS, 'success')
                } else if(this.TypeOfCompany == 'Client' && this.isAssociatedClientUpdateInProgress) {
                    this.disableActionButtons = true;
                    this.companyService.getAllBusOfClient(this.userDetails.company.companyId, this.newlyCreatedCompanyId, this.userDetails.id, (data) => {
                        this.originalStepSeven = _.cloneDeep(data);
                        this.saveBusOfClient = _.cloneDeep(data);
                        this.busOfClient = _.cloneDeep(data);
                        this.disableActionButtons = false;
                        this.stepTrigger('nextbtnclick');

                    }, (error) => {
                        this.disableActionButtons = false;
                    });
                } else if(_.isEqual(this.userDetails.roleScreenRef.role.name, 'CorporateAdmin') || _.isEqual(this.userDetails.roleScreenRef.role.name, 'StaffingAdmin') && this.assessmentOrJobBoardCredentialsUpdated) {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.UPDATESUCCESS, 'success');
                } else if(this.StorageService.get('companyDetails') == null && !this.companyDetailsUpdateMode) {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.CREATESUCCESS, 'success')
                }
                if(this.clientOrBuUpdateInProgress && this.stepFiveChanged) {
                    if(!_.isEqual(this.tempObj, this.savedAssessmentWorkFlow) || (this.nextButtonFlag > 0) || (this.saveButtonFlag > 0)) {
                        if(this.TypeOfCompany == 'Client') {
                            this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CLIENT + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.UPDATESUCCESS, 'success')
                        }
                    } else {
                        if(this.TypeOfCompany == 'Client' && !this.isAssociatedClientUpdateInProgress) {
                            this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.NOTHING_TO_UPDATED, 'success');
                        }
                    }
                }


                this.companyStepFiveSaved = true;
                this.laststep = true;
                this.StorageService.set('wizardoperations', true, null);
                //Bu has addition step 'Associate Vendor'
                if(this.TypeOfCompany !== 'BusinessUnit' && !this.isAssociatedClientUpdateInProgress) {
                    this.stepTrigger('nextbtnclick');
                }

                const navigateAfterStepFive = () => {
                    if(this.TypeOfCompany == 'Client' && !this.isAssociatedClientUpdateInProgress) {
                        this.lastStepCompleted = true;
                        setTimeout(() => {
                            this.$state.go('missioncontrol.clientsOrBus', null, { reload: false });
                        }, 300);
                    } else if(this.companyManagement.company.companyType == 'Host') {
                        this.StorageService.set('companyDetails', this.newlyCreatedCompanyId, null);
                        this.lastStepCompleted = true;
                        // setTimeout(() => {
                        //     this.$state.go('missioncontrol.dashboardsuperusermodule', { companyObject: 'companyObject' }, { reload: false });
                        // }, 300);
                    } else if(this.TypeOfCompany == 'BusinessUnit' || this.isAssociatedClientUpdateInProgress) {
                        //do nothing
                    } else {
                        this.StorageService.set('companyDetails', this.newlyCreatedCompanyId, null);
                        this.lastStepCompleted = true;
                        setTimeout(() => {
                            if(!this.isCompanyVendor) {
                                this.$state.go('missioncontrol.dashboard', { companyObject: 'companyObject' }, { reload: false });
                            } else {
                                this.$state.go('missioncontrol.requisitions', { companyObject: 'companyObject' }, { reload: false });
                            }
                        }, 300);
                        // this.$state.go('missioncontrol.dashboard', { companyObject: 'companyObject' }, { reload: false });
                    }
                };

                if(this.companyManagement.companyState == 'Draft' || this.companyManagement.companyState == null) {
                    this.changeCompanyState(navigateAfterStepFive, navigateAfterStepFive);
                } else {
                    navigateAfterStepFive();
                }

            }, (error) => {
                if(this.authService.isOnline) {
                    if(error.error && error.error.message) {
                        let updatedMessage = error.error.message;
                        if(updatedMessage.includes('<br>')) {
                            updatedMessage = error.error.message.split('<br>')[0];
                        }
                        this.alertsAndNotificationsService.showBannerMessage(updatedMessage, 'danger');
                    } else {
                        this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                    }
                }

                if(error.statusCode == "409") {
                    this.getAssessmentWorkFlow(this.userDetails.company.companyId);
                } else {
                    this.savedAssessmentWorkFlow = _.cloneDeep(this.savedAssessmentWorkFlowCopy);
                }

                setTimeout(() => {
                    if(!this.isCompanyVendor) {
                        this.$state.go('missioncontrol.dashboard', { companyObject: 'companyObject' }, { reload: false });
                    } else {
                        this.$state.go('missioncontrol.requisitions', { companyObject: 'companyObject' }, { reload: false });
                    }
                }, 300);
            });
        } else if(!this.stepFiveChanged) {
            const proceedAfterStepFiveUnchanged = () => {
            this.companyStepFiveSaved = true;
            this.laststep = true;
            this.StorageService.set('wizardoperations', true, null);
            if(this.TypeOfCompany == 'Client') {
                this.lastStepCompleted = true;
                if(!this.ClientOrBuUpdate) {
                    if(this.TypeOfCompany == 'Client') {
                        this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CLIENT + " '<b><i class='wrap-word'>" + this.savedCompanyManagement.company.name + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CREATESUCCESS, 'success');
                    }
                    else {
                        this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.BU.BU + " '<b><i class='wrap-word'>" + this.savedCompanyManagement.company.name + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.BU.CREATESUCCESS, 'success');
                    }
                    setTimeout(() => {
                        this.$state.go('missioncontrol.clientsOrBus', null, { reload: false });
                    }, 300);
                } else {
                    if(this.isAssociatedClientUpdateInProgress) {
                            this.disableActionButtons = true;
                            this.companyService.getAllBusOfClient(this.userDetails.company.companyId, this.newlyCreatedCompanyId, this.userDetails.id, (data) => {
                                this.originalStepSeven = _.cloneDeep(data);
                                this.saveBusOfClient = _.cloneDeep(data);
                                this.busOfClient = _.cloneDeep(data);
                                this.disableActionButtons = false;
                                this.stepTrigger('nextbtnclick');
                            }, (error) => {
                                this.disableActionButtons = false;
                            });
                    }
                    else {
                        this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.NOTHING_TO_UPDATED, 'success');
                        setTimeout(() => {
                            this.$state.go('missioncontrol.clientsOrBus', null, { reload: false });
                        }, 300);
                    }
                }
            } else if(this.TypeOfCompany == 'BusinessUnit') {
                this.companyService.getAllBuVendors(this.userDetails.company.companyId, this.newlyCreatedCompanyId, (data) => {
                    this.originalStepSix = _.cloneDeep(data);
                    this.savedVendors = _.cloneDeep(data);
                    this.vendors = _.cloneDeep(data);
                    this.stepTrigger('nextbtnclick');
                }, (error) => {
                    //do nothing
                });
            }
            else if(this.companyManagement.company.companyType == 'Host') {
                if((this.nextButtonFlag > 0) || (this.saveButtonFlag > 0)) {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.UPDATESUCCESS, 'success');
                } else {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.NOTHING_TO_UPDATED, 'success')
                }
                this.StorageService.set('companyDetails', this.newlyCreatedCompanyId, null);
                this.lastStepCompleted = true;
                setTimeout(() => {
                    this.$state.go('missioncontrol.dashboardsuperusermodule', { companyObject: 'companyObject' }, { reload: false });
                }, 300);
            } else {
                this.StorageService.set('companyDetails', this.newlyCreatedCompanyId, null);
                this.lastStepCompleted = true;
                if(this.companyDetailsUpdateMode) {
                    //this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.UPDATESUCCESS, 'success')
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.NOTHING_TO_UPDATED, 'success');
                    setTimeout(() => {
                        if(!this.isCompanyVendor) {
                            this.$state.go('missioncontrol.dashboard', { companyObject: 'companyObject' }, { reload: false });
                        } else {
                            this.$state.go('missioncontrol.requisitions', { companyObject: 'companyObject' }, { reload: false });
                        }
                    }, 300);
                } else if(!!_.isUndefined(this.companyDetailsUpdateMode)) {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.CREATESUCCESS, 'success');
                    setTimeout(() => {
                        if(!this.isCompanyVendor) {
                            this.$state.go('missioncontrol.dashboard', { companyObject: 'companyObject' }, { reload: false });
                        } else {
                            this.$state.go('missioncontrol.requisitions', { companyObject: 'companyObject' }, { reload: false });
                        }
                    }, 300);
                }
            }
            };

            if(this.companyManagement.companyState == 'Draft' || this.companyManagement.companyState == null) {
                this.changeCompanyState(proceedAfterStepFiveUnchanged, proceedAfterStepFiveUnchanged);
            } else {
                proceedAfterStepFiveUnchanged();
            }
        } else {
            this.companyStepFiveSaved = true;
            this.laststep = true;
            this.StorageService.set('wizardoperations', true, null);
            if(!this.isCompanyVendor) {
                this.$state.go('missioncontrol.dashboard', { companyObject: 'companyObject' }, { reload: false });
            } else {
                this.$state.go('missioncontrol.requisitions', { companyObject: 'companyObject' }, { reload: false });
            }
        }

    }

    /**
     * Function to save Step 6 details(BU Vendors)
     */
    saveVendors() {
        //This condition satisfy when a company from popup is selected and user tries to create a client/bu for selected company
        if(this.StorageService.get('companyDetails') != null && this.newlyCreatedCompanyId == null && !this.clientOrBuUpdateInProgress) {
            if(!_.isUndefined(this.StorageService.get('companyDetails').companyId)) {
                this.newlyCreatedCompanyId = this.StorageService.get('companyDetails').companyId;
            } else {
                this.newlyCreatedCompanyId = this.StorageService.get('companyDetails');
            }
        }

        //This condition satisfy when a company from popup is selected and user tries to edit a client/bu for selected company
        if(this.StorageService.get('companyDetails') == null && this.newlyCreatedCompanyId == null && this.companyManagement.company.companyType != 'Host') {
            if(this.StorageService.get('companyToBeUpdated') == null) {
                this.newlyCreatedCompanyId = this.userDetails.company.companyId;
            } else {
                this.newlyCreatedCompanyId = this.StorageService.get('companyToBeUpdated').company.id;
            }
        }
        //This condition will satisfy if editing a client/bu
        if(this.clientOrBuUpdateInProgress) {
            this.newlyCreatedCompanyId = this.companyManagement.company.id;
        }


        if((this.StorageService.get('companyDetails') == null && !this.clientOrBuCreationInProgress && !this.clientOrBuUpdateInProgress)) {
            this.newCompanyCreation = true;
        }

        if(this.stepSixChanged) {
            this.tempObj = _.cloneDeep(this.originalStepSix);
            this.limitCharactersInName();
            this.modifiedVendors = this.getModifiedVendors();
            this.saveVendorsSaveClicked = false;
            if(this.modifiedVendors.disassociatedVendors.length > 0) {
                this.getBuVendorsRequisitionsCountAndLaunchDisassoicateModal();
            } else {
                this.saveVendorsOnComplete();
            }

        } else if(!this.stepSixChanged) {
            const proceedAfterStepSixUnchanged = () => {
                this.companyStepSixSaved = true;
                this.laststep = true;
                this.StorageService.set('wizardoperations', true, null);
                this.lastStepCompleted = true;
                if(!this.ClientOrBuUpdate) {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.BU.BU + " '<b><i class='wrap-word'>" + this.savedCompanyManagement.company.name + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.BU.CREATESUCCESS, 'success');
                    setTimeout(() => {
                        this.$state.go('missioncontrol.clientsOrBus', null, { reload: true });
                    }, 300);
                } else {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.NOTHING_TO_UPDATED, 'success');
                    setTimeout(() => {
                        this.$state.go('missioncontrol.clientsOrBus', null, { reload: true });
                    }, 300);
                }
            };
            if(this.companyManagement.companyState == 'Draft' || this.companyManagement.companyState == null) {
                this.changeCompanyState(proceedAfterStepSixUnchanged, proceedAfterStepSixUnchanged);
            } else {
                proceedAfterStepSixUnchanged();
            }
        } else {
            this.companyStepSixSaved = true;
            this.laststep = true;
            this.StorageService.set('wizardoperations', true, null);
            if(!this.isCompanyVendor) {
                this.$state.go('missioncontrol.dashboard', { companyObject: 'companyObject' }, { reload: false });
            } else {
                this.$state.go('missioncontrol.requisitions', { companyObject: 'companyObject' }, { reload: false });
            }
        }
    }

    saveVendorsOnComplete() {
        this.companyService.saveBuVendors(this.vendors, (data) => {
            this.vendors = _.cloneDeep(data);
            this.originalStepSix = _.cloneDeep(data);
            this.savedVendors = _.cloneDeep(data);

            if(!this.clientOrBuUpdateInProgress) {
                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.BU.BU + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.BU.CREATESUCCESS, 'success')
            }
            if(this.clientOrBuUpdateInProgress && this.stepSixChanged) {
                if(!_.isEqual(this.tempObj, this.savedVendors) || (this.nextButtonFlag > 0) || (this.saveButtonFlag > 0)) {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.BU.BU + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.BU.UPDATESUCCESS, 'success')
                } else {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.NOTHING_TO_UPDATED, 'success');
                }
            }

            this.companyStepSixSaved = true;
            this.laststep = true;
            this.StorageService.set('wizardoperations', true, null);
            this.stepTrigger('nextbtnclick');

            const navigateAfterVendorsSaved = () => {
                this.lastStepCompleted = true;
                setTimeout(() => {
                    this.$state.go('missioncontrol.clientsOrBus', null, { reload: true });
                }, 300);
            };
            if(this.companyManagement.companyState == 'Draft' || this.companyManagement.companyState == null) {
                this.changeCompanyState(navigateAfterVendorsSaved, navigateAfterVendorsSaved);
            } else {
                navigateAfterVendorsSaved();
            }
        }, (error) => {
            if(this.authService.isOnline) {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            }
        });
    }

    _accessTimeOptionErrorExists() {
        var accessTimeErrorExistsFlag = false;
        if((this.preferences.techAssessmentList.licensePrefDetailsList[0].enabled && this.preferences.techAssessmentList.licensePrefDetailsList[0].selected) || (this.preferences.valueAssessmentList.licensePrefDetailsList[0].enabled && this.preferences.valueAssessmentList.licensePrefDetailsList[0].selected)) {
            // if tech assessment or value assessment is enabled and selected
            var accessTimeHasBeenChosenFlag = false;
            this.preferences.licenseAssessmentPreferences.accessTimes.forEach((val, key) => {
                if(val.accessTimeValue) {
                    // if any accessTime option is chosen
                    accessTimeHasBeenChosenFlag = true;
                }
            });
            accessTimeErrorExistsFlag = !accessTimeHasBeenChosenFlag;
        }
        return accessTimeErrorExistsFlag;
    }

    saveStepsDataAfterTimeout(Company, ContactList, FourDotFiveAdmin) {
        setTimeout(() => {
            this.saveStepsData(Company, ContactList, FourDotFiveAdmin);
        }, 1000);
    }

    saveStepsData(Company, ContactList, FourDotFiveAdmin) {
        this.nextButtonClicked = false;
        this.saveButtonClicked = true;
        var currentStep = this.getCurrentStep();
        if(currentStep == 3 && (this.TypeOfCompany == 'Client' || this.TypeOfCompany == 'BusinessUnit' || this.StorageService.get('SelectedHostCompany') != null)) {
            currentStep = 5;
        }
        if(currentStep == 4 && this.TypeOfCompany == 'BusinessUnit') {
            currentStep = 6;
        }

        if(currentStep == 4 && this.TypeOfCompany == 'Client' && this.isAssociatedClientUpdateInProgress) {
            currentStep = 7;
        }

        this.valid = false;
        switch(currentStep) {
            case 1:
                this.stepOneChanged = !(_.isEqual(this.originalStepOne, this.companyManagement.company));//Checks if any changes.
                if(this.companyManagement.company.address2 != null) {
                    this.isAddressValid = !_.isEqual(this.companyManagement.company.address1.toUpperCase(), this.companyManagement.company.address2.toUpperCase()) && (this.companyManagement.company.address1 != this.companyManagement.company.address2);
                } else {
                    this.isAddressValid = this.companyManagement.company.address1 != this.companyManagement.company.address2;
                }
                this.valid = this.form.companyDetailsForm.$valid && this.isAddressValid && this.validzipcode && !_.isUndefined(this.validzipcode) && (!this.zipCodeCalling || (this.zipCodeCalling && this.zipCodeCallCompleted));//Checks validations.;
                if(this.stepOneChanged && this.valid) {
                    if(!!_.isUndefined(this.companyManagement.company.companyType)) {
                        if(this.userDetails.company.companyType == 'Corporation') {
                            this.companyManagement.company.companyType = 'BusinessUnit';
                        } else if(this.userDetails.company.companyType == 'StaffingCompany') {
                            this.companyManagement.company.companyType = 'Client';
                        }
                    }
                    //Building the SavedCompanyManagement object before saving.
                    this.savedCompanyManagementCopy.company = _.cloneDeep(this.savedCompanyManagement.company);
                    this.savedCompanyManagement.company = _.cloneDeep(this.companyManagement.company);
                    if(this.isAssociatedClientUpdateInProgress){
                        this._setSharedClientDescription(()=>{
                            this.originalStepOne = _.cloneDeep(this.companyManagement.company)
                            this.saveButtonFlag++;
                            this.isCompanyDetailsSaveCalled = true;
    
                            //Updating the SavedCompanyManagement object after saving.
                            this.savedCompanyManagement.company = _.cloneDeep(this.companyManagement.company);
                            this.limitCharactersInName();
                            this.saveButtonClicked = false;
                            this.companyStepOneSaved = true;
                            this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + " Description updated successfully", 'success')
                        });
                       
                    } else {
                        this.genericService.addObject(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.SAVE_COMPANY + '/' + this.loggedInUserDetails.company.companyId, this.savedCompanyManagement).subscribe((data) => {
                            this.originalStepOne = _.cloneDeep(data.body.company);
                            this.saveButtonFlag++;
                            this.isCompanyDetailsSaveCalled = true;
    
                            //Updating the SavedCompanyManagement object after saving.
                            this.savedCompanyManagement.company = _.cloneDeep(data.body.company);
                            this.limitCharactersInName();
                            this.saveButtonClicked = false;
    
                            this.companyManagement.company = data.body.company;
                            this.companyManagement.company.name = data.body.company.name;
                            this.companyManagement.companyState = data.body.companyState;
                            this.companyManagement.companyState = data.body.companyState;
                            this.savedCompanyManagement.company.id = data.body.company.id;
                            this.savedCompanyManagement.companyState = data.body.companyState;
                            this.newlyCreatedCompanyId = data.body.company.id;
                            if(this.TypeOfCompany != 'Client' && this.TypeOfCompany != 'BusinessUnit' && this.TypeOfCompany != 'Vendor') {
                                this.userDetails.company.companyId = data.body.company.id;
                                this.userDetails.company.companyName = data.body.company.name;
                                this.userDetails.company.companyType = data.body.company.companyType;
                                this.userDetails.company.companyState = data.body.companyState;
                                this.companyService.setCompanyStateLabel();
                                this.userDetails.company.companySelected = true;
                                this.StorageService.set('userDetails', _.cloneDeep(this.userDetails), { 'expires': new Date(Date.now() + this.authService.tokenvalidity) });
                            }
                            this.companyStepOneSaved = true;
                            if(((this.TypeOfCompany == 'Client' || this.TypeOfCompany == 'BusinessUnit') && this.clientOrBuUpdateInProgress) || ((this.TypeOfCompany == 'Vendor') && this.vendorUpdateInProgress)) {
                                this.editCompanyName = data.body.company.name.substring(0, 57);
                                this.editCompanyNameOnHover = data.body.company.name;
                                if(data.body.company.name.length > 57) {
                                    this.editCompanyName = this.editCompanyName + '...';
                                }
                                if(this.TypeOfCompany == 'BusinessUnit') {
                                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.BU.BU + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.UPDATESUCCESS, 'success')
                                }
                                else if(this.TypeOfCompany == 'Client') {
                                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CLIENT + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.UPDATESUCCESS, 'success')
                                }
                                else {
                                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.VENDOR.VENDOR + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.VENDOR.UPDATESUCCESS, 'success')
                                }
                            }
                            if((this.TypeOfCompany == 'Client' || this.TypeOfCompany == 'BusinessUnit') && this.clientOrBuCreationInProgress) {
                                this.assignClientToStaffingCompany('clickonclientsave');
                                //this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CREATESUCCESS, 'success')
                            }
    
                            if(!(this.companyDetailsUpdateMode) && this.TypeOfCompany == 'Company') {
                                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANYDRAFTSUCCESS, 'success')
                            }
                            if(this.companyDetailsUpdateMode) {
                                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.UPDATESUCCESS, 'success')
                            }
                        }, (error) => {
                            if(error.statusCode === "409") {
                                this.errorOcurred = true;
                                if(!_.isUndefined(this.companyManagement.companyState)) {
                                    if(error.developerMsg === "101") {
                                        this.companyManagement.company.companyShortName = this.originalStepOne.companyShortName;
                                    } else if(error.developerMsg === "102") {
                                        this.companyManagement.company.name = this.originalStepOne.name;
                                    } else if(error.developerMsg === "100") {
                                        this.companyManagement.company.companyShortName = this.originalStepOne.companyShortName;
                                        this.companyManagement.company.name = this.originalStepOne.name;
                                    } else {
                                        this.companyManagement.company.companyShortName = this.originalStepOne.companyShortName;
                                        this.companyManagement.company.name = this.originalStepOne.name;
                                    }
                                } else {
                                    if(error.developerMsg === "101") {
                                        this.companyManagement.company.companyShortName = '';
                                    } else if(error.developerMsg === "102") {
                                        this.companyManagement.company.name = '';
                                    } else if(error.developerMsg === "100") {
                                        this.companyManagement.company.companyShortName = '';
                                        this.companyManagement.company.name = '';
                                    }
                                }
                            }
                            if(this.authService.isOnline) {
                                this.alertsAndNotificationsService.showBannerMessage(error.error.message, 'danger');
                            }
                            this.savedCompanyManagement.company = _.cloneDeep(this.savedCompanyManagementCopy.company);
                        });
                    }
                    
                } else if(!this.valid) {
                    this.step1FormSubmitted = true;
                    this.triggerCount = 0;
                } else if(this.valid && (!this.stepOneChanged)) {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.NOTHING_TO_SAVE, 'success');
                    this.saveButtonClicked = false;
                }
                break;
            case 2:
                this.errorOcurred = false;
                this.cleanContactList();
                this.stepTwoChanged = !(_.isEqual(this.originalStepTwo, this.companyManagement.contactList));
                if(this.showRemoveButton) {
                    this.validateSecondaryDetails = true;
                    this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.form.companySecondaryContactPersonDetailsForm.$valid
                        && this.isValidPrimaryMobileEntered && this.isValidPrimaryWorkPhoneEntered && this.isValidSecondaryMobileEntered && this.isValidSecondaryWorkPhoneEntered && this.isValidPhoneNumbersEntered();
                } else {
                    this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.isValidPrimaryMobileEntered && this.isValidPrimaryWorkPhoneEntered;
                }
                if(this.stepTwoChanged && this.valid) {
                    if(this.companyManagement.contactList.length == 1) {
                        this.companyManagement.contactList[0].primary = false;
                    }
                    if(this.companyManagement.contactList.length > 1) {
                        this.companyManagement.contactList.forEach((contact, key) => {
                            contact.primary = false;
                        });
                    }

                    if(this.newlyCreatedCompanyId == null && (this.clientOrBuUpdateInProgress || this.vendorUpdateInProgress)) {
                        this.newlyCreatedCompanyId = this.companyManagement.company.id;
                    }

                    //Building the SavedCompanyManagement object before saving.
                    this.savedCompanyManagementCopy.contactList = _.cloneDeep(this.savedCompanyManagement.contactList);
                    this.savedCompanyManagement.contactList = _.cloneDeep(this.companyManagement.contactList);
                    this.limitCharactersInName();
                    if(!this.vendorUpdateInProgress && !this.isAssociatedClientUpdateInProgress) {
                        if(this.clientOrBuCreationInProgress) {
                            this.savedCompanyManagement.contactList.forEach((contact, key) => {
                                if(contact.userId == undefined) {
                                    contact.userId = null;
                                }
                            });
                        }
                        this.genericService.addObject(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.SAVE_COMPANY + '/' + this.userDetails.company.companyId, this.savedCompanyManagement).subscribe((data) => {
                            this.saveButtonFlag++;
                            this.originalStepTwo = _.cloneDeep(data.body.contactList);
                            this.saveButtonClicked = false;
                            //Updating the SavedCompanyManagement object after saving.
                            this.savedCompanyManagement.contactList = _.cloneDeep(data.body.contactList);

                            this.companyManagement.contactList = _.cloneDeep(data.body.contactList);
                            if(this.TypeOfCompany == 'Client' || this.TypeOfCompany == 'BusinessUnit') {
                                //this.getAssessmentWorkFlow(this.loggedInUserDetails.company.companyId);
                            }
                            if(this.TypeOfCompany == 'BusinessUnit' && this.clientOrBuUpdateInProgress) {
                                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.BU.BU + " '<b><i class='wrap-word'>" + this.savedCompanyManagement.company.name + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.BU.UPDATESUCCESS, 'success');
                            }
                            if(this.companyManagement.company.companyType == 'Client' && this.clientOrBuUpdateInProgress) {
                                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CLIENT + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.UPDATESUCCESS, 'success')
                            } else if(!this.clientOrBuCreationInProgress && this.companyDetailsUpdateMode) {
                                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.UPDATESUCCESS, 'success')
                            }
                            if((this.TypeOfCompany == 'Client' || this.TypeOfCompany == 'BusinessUnit') && this.clientOrBuCreationInProgress) {
                                if(this.TypeOfCompany == 'Client') {
                                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CLIENT + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CREATEDRAFTSUCCESS, 'success')
                                } else {
                                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.BU.BU + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.BU.CREATEDRAFTSUCCESS, 'success')
                                }
                            }
                            if(!(this.companyDetailsUpdateMode) && this.TypeOfCompany == 'Company') {
                                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANYDRAFTSUCCESS, 'success')
                            }
                            this.companyStepTwoSaved = true;
                        }, (error) => {
                            if(this.authService.isOnline) {
                                this.alertsAndNotificationsService.showBannerMessage(error.error.message, 'danger');
                            }
                            this.savedCompanyManagement.contactList = _.cloneDeep(this.savedCompanyManagementCopy.contactList);
                        });
                    } else if(this.isAssociatedClientUpdateInProgress) {
                        var clientObject: any = {};
                        clientObject.corporateCompanyId = this.newlyCreatedCompanyId;
                        clientObject.vendorId = this.userDetails.company.companyId;
                        clientObject.corporateCompanyContactList = _.cloneDeep(this.getParsedContacts(this.savedCompanyManagement.contactList));
                        this.companyService.updateClientCorporateContactDetails(clientObject, (data) => {
                            data.contactList = _.cloneDeep(data.corporateCompanyContactList);
                            this.saveButtonFlag++;
                            this.originalStepTwo = _.cloneDeep(data.contactList);
                            this.saveButtonClicked = false;
                            //Updating the SavedCompanyManagement object after saving.
                            this.savedCompanyManagement.contactList = _.cloneDeep(data.contactList);

                            this.companyManagement.contactList = _.cloneDeep(data.contactList);
                            this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CLIENT + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.UPDATESUCCESS, 'success')
                            this.companyStepTwoSaved = true;
                        }, (error) => {
                            if(this.authService.isOnline) {
                                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                            }
                            this.savedCompanyManagement.contactList = _.cloneDeep(this.savedCompanyManagementCopy.contactList);
                        });
                    } else {
                        var vendorObject: any = {};
                        vendorObject.corporateCompanyId = this.userDetails.company.companyId;
                        vendorObject.vendorId = this.newlyCreatedCompanyId;
                        vendorObject.contactList = _.cloneDeep(this.getParsedContacts(this.savedCompanyManagement.contactList));
                        this.companyService.saveCorporateVendor(vendorObject, (data) => {
                            data.contactList = _.cloneDeep(this.getParsedCompanyContacts(data.contactList));
                            this.saveButtonFlag++;
                            this.originalStepTwo = _.cloneDeep(data.contactList);
                            this.saveButtonClicked = false;
                            //Updating the SavedCompanyManagement object after saving.
                            this.savedCompanyManagement.contactList = _.cloneDeep(data.contactList);

                            this.companyManagement.contactList = _.cloneDeep(data.contactList);
                            this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.VENDOR.VENDOR + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.VENDOR.UPDATESUCCESS, 'success')
                            this.companyStepTwoSaved = true;
                        }, (error) => {
                            if(this.authService.isOnline) {
                                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                            }
                            this.savedCompanyManagement.contactList = _.cloneDeep(this.savedCompanyManagementCopy.contactList);
                        });
                    }
                } else if(!this.valid) {
                    this.step2FormSubmitted = true;
                    this.triggerCount = 0;
                } else if(this.valid && (!this.stepTwoChanged)) {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.NOTHING_TO_SAVE, 'success');
                    this.saveButtonClicked = false;
                }
                break;
            case 3:
                // this.oldCheckedAdmin = null;
                if(!_.isUndefined(this.originalStepThree) && this.originalStepThree != null && this.selectedFourDotFiveAdmin.length > 0) {
                    this.stepThreeChanged = !(_.isEqual(this.originalStepThree.userId, this.selectedFourDotFiveAdmin[0].userId)) || !_.isEqual(this.companyManagement.userConfiguration, this.CompanyManagementReset.userConfiguration);
                    this.oldFourDotFiveAdminId = this.originalStepThree.userId;
                } else if(!!_.isUndefined(this.originalStepThree) || (this.originalStepThree === null)) {
                    /**
                     * First condition is satisfied in create company.
                     * Second condition is satisfied when creating a company in
                     * Draft state( without selecting 4dot5admin) and coming to update for the next time later
                     */
                    this.stepThreeChanged = true;
                }

                if(this.showRemoveButton) {
                    this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.form.companySecondaryContactPersonDetailsForm.$valid && this.selectedFourDotFiveAdmin.length > 0;
                } else {
                    this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.selectedFourDotFiveAdmin.length > 0;
                }
                if(this.valid) {
                    this.valid = this.isPasswordRestrictionsValid();
                    if(!this.valid) {
                        this.complexityRestrictionChange('user');
                        this.complexityRestrictionChange('administrator');
                    }
                }
                if(this.stepThreeChanged && this.valid) {
                    if(!!_.isUndefined(this.fourDotFiveCompanyAdmin)) {
                        this.fourDotFiveCompanyAdmin = this.selectedFourDotFiveAdmin;
                    }

                    //Building the SavedCompanyManagement object before saving.
                    this.savedFourDotFiveAdminCopy = _.cloneDeep(this.savedFourDotFiveAdmin);
                    this.savedFourDotFiveAdmin = _.cloneDeep(this.selectedFourDotFiveAdmin);
                    this.limitCharactersInName();
                    this.genericService.addObject(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.UPDATE_COMPANY_FOR_FOURDOTFIVE_ADMIN + '/' + this.savedFourDotFiveAdmin[0].userId + '/' + this.userDetails.company.companyId + '?oldfourdotfiveadmin=' + this.oldFourDotFiveAdminId, this.savedFourDotFiveAdmin).subscribe((data) => {
                        this.originalStepThree = _.cloneDeep(data.body.fourDotFiveAdmin);
                        //Updating the SavedCompanyManagement object after saving.
                        this.savedFourDotFiveAdmin = _.cloneDeep(data.body.fourDotFiveAdmin);

                        this.companyManagement.fourDotFiveAdmin = _.cloneDeep(data.body.fourDotFiveAdmin);
                        this.CompanyManagementReset.fourDotFiveAdmin = _.cloneDeep(data.body.fourDotFiveAdmin);
                        //this.getAssessmentWorkFlow(this.loggedInUserDetails.company.companyId);
                        this._saveUserConfiguration((data) => {
                            this.saveButtonFlag++;
                            this.saveButtonClicked = false;
                            this.companyManagement.userConfiguration = _.cloneDeep(data);
                            this.CompanyManagementReset.userConfiguration = _.cloneDeep(data);
                            // if((this.StorageService.get('companyDetails') != null || this.companyDetailsUpdateMode)) {
                            this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.UPDATESUCCESS, 'success')
                            // } else if(!(this.companyDetailsUpdateMode)) {
                            //     this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANYDRAFTSUCCESS, 'success')
                            // }
                            this.companyStepThreeSaved = true;

                        }, (error) => {
                            if(this.authService.isOnline) {
                                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                            }
                            this.disableActionButtons = false;
                        });
                    }, (error) => {
                        if(this.authService.isOnline) {
                            this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                        }
                        this.savedFourDotFiveAdmin = _.cloneDeep(this.savedFourDotFiveAdminCopy);
                    });
                } else if(!this.valid) {
                    this.step3FormSubmitted = true;
                    this.triggerCount = 0;
                } else if(this.valid && (!this.stepThreeChanged)) {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.NOTHING_TO_SAVE, 'success');
                    this.saveButtonClicked = false;
                }
                break;
            case 4:
                // this.stepFiveChanged = _.isEqual(this.originalStepFive, this.companyManagement.licensePreferences);
                if(this.isPlanChosen && this.isActivePlanExists) {
                    this.stepFourChanged = !(_.isEqual(this.originalStepFour, this.selectedPlan));
                }
                if(this.isPlanChosen && this.stepFourChanged) {
                    this.planService.savePlan(this.selectedPlan, (data) => {
                        this.saveButtonFlag++;
                        // data.planHeaderDto.planStartDateDisplay = this.fourDotFiveDateFormatPipe.transform(data.planHeaderDto.planStartDate);
                        // data.planHeaderDto.planEndDateDisplay = this.fourDotFiveDateFormatPipe.transform(data.planHeaderDto.planEndDate);
                        data.planHeaderDto.unformattedStartDate = moment(data.planHeaderDto.planStartDate).toDate();
                        data.planHeaderDto.unformattedEndDate = moment(data.planHeaderDto.planEndDate).toDate();
                        data.planHeaderDto.planStartDateDisplay = moment(data.planHeaderDto.unformattedStartDate).tz(data.planHeaderDto.timeZoneName).format("MMM DD, YYYY");
                        data.planHeaderDto.planEndDateDisplay = moment(data.planHeaderDto.unformattedEndDate).tz(data.planHeaderDto.timeZoneName).format("MMM DD, YYYY");
                        this.originalStepFour = _.cloneDeep(data);
                        this.savedCurrentActivePlan = _.cloneDeep(data);
                        this.selectedPlan = _.cloneDeep(data);
                        this.isActivePlanExists = true;
                        this.alertsAndNotificationsService.showBannerMessage('Company plan details saved successfully', 'success');
                        this.companyStepFourSaved = true;
                        this.saveButtonClicked = false;
                    }, (error) => {
                        if(this.authService.isOnline) {
                            this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                        }
                    });
                } else if(!this.stepFourChanged) {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.NOTHING_TO_SAVE, 'success');
                    this.saveButtonClicked = false;
                } else if(!this.isPlanChosen) {
                    this.step4formsubmitted = true;
                    this.triggerCount = 0;
                }
                break;
            case 5:
                this._setHasUnsavedAssessmentCredentials();
                this._setHasUnsavedJobBoardCredentials();
                // this.stepFiveChanged = _.isEqual(this.originalStepFive, this.companyManagement.licensePreferences);
                this.parseWorkflowStepsOrderBeforeSave();
                this.stepFiveChanged = !(_.isEqual(this.preferences, this.originalStepFive));
                if(this.TypeOfCompany == 'Client' || this.StorageService.get('SelectedHostCompany') != null || this.TypeOfCompany == 'BusinessUnit') {
                    if(this.showRemoveButton) {
                        this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.form.companySecondaryContactPersonDetailsForm.$valid && this.licenseScoringFormValid && this.assessmentsFormValid && this.jobBoardsFormValid;
                    } else {
                        this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.licenseScoringFormValid && this.assessmentsFormValid && this.jobBoardsFormValid;
                    }
                } else {
                    if(this.showRemoveButton) {
                        this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.form.companySecondaryContactPersonDetailsForm.$valid && this.selectedFourDotFiveAdmin.length > 0 && this.licenseScoringFormValid && this.assessmentsFormValid && this.jobBoardsFormValid;
                    } else {
                        this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.selectedFourDotFiveAdmin.length > 0 && this.licenseScoringFormValid && this.assessmentsFormValid && this.jobBoardsFormValid;
                    }
                }
                var techOrValueAssessmentAccessTimesNotChosenFlag = this._accessTimeOptionErrorExists();
                if(this.StorageService.get('SelectedHostCompany') != null) {
                    this.newlyCreatedCompanyId = this.userDetails.company.companyId;
                }
                //This condition will satisfy if editing a client/bu
                if(this.clientOrBuUpdateInProgress) {
                    this.newlyCreatedCompanyId = this.companyManagement.company.id;
                }
                if(this.valid && this.stepFiveChanged && !techOrValueAssessmentAccessTimesNotChosenFlag && !this.hasUnsavedAssessmentCredentials && !this.hasUnsavedJobBoardCredentials) {
                    //Building the SavedCompanyManagement object before saving.

                    this.savedAssessmentWorkFlowCopy = _.cloneDeep(this.savedAssessmentWorkFlow);
                    this.savedAssessmentWorkFlow = _.cloneDeep(this.preferences);
                    this.limitCharactersInName();
                    var clientOrCompany;
                    if(this.TypeOfCompany == 'Client' || this.TypeOfCompany == 'BusinessUnit') {
                        clientOrCompany = this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.SET_LICENCE_PREFERENCES_FOR_CLIENT_BU + '/' + this.userDetails.company.companyId + '/' + this.newlyCreatedCompanyId;
                    } else {
                        clientOrCompany = this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.SET_LICENCE_PREFERENCES + '/' + this.userDetails.company.companyType + '/' + this.newlyCreatedCompanyId;
                    }
                    this.genericService.addObject(clientOrCompany, this.savedAssessmentWorkFlow).subscribe((data) => {
                        this.saveButtonFlag++;
                        if(!_.isNull(data.body.jobBoardCredentialList)) {
                            data.body.jobBoardCredentialList = this.massageJobBoardCredentials(data.body.licensePreferences.jobBoardCredentialList);
                        }
                        this.companyManagement.licensePreferences = _.cloneDeep(data.body.licensePreferences);
                        this.originalStepFive = _.cloneDeep(data.body.licensePreferences);
                        // update existing job credentials list
                        this.preferences.jobBoardCredentialList = data.body.licensePreferences.jobBoardCredentialList;
                        this.massageWorkflowStepsOrder();
                        // Updating SavedCompanyManagement object after saving.
                        this.savedAssessmentWorkFlow = _.cloneDeep(data.body.licensePreferences);
                        if(this.clientOrBuUpdateInProgress && !(_.isEqual(this.originalStepFive, this.AssessmentWorkFlow))) {
                            if(this.TypeOfCompany == 'Client') {
                                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CLIENT + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.UPDATESUCCESS, 'success');
                            } else {
                                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.BU.BU + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.BU.UPDATESUCCESS, 'success');
                            }
                            // this.$state.go('missioncontrol.clientsOrBus', null, { reload: false });
                        } else if((this.StorageService.get('companyDetails') != null || this.StorageService.get('SelectedHostCompany') != null) && this.companyDetailsUpdateMode) {
                            if(this.userDetails.roleScreenRef.role.name != 'CorporateAdmin' && this.userDetails.roleScreenRef.role.name != 'StaffingAdmin') {
                                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.UPDATESUCCESS, 'success');
                            }
                        } else if(this.clientOrBuCreationInProgress) {
                            if(this.TypeOfCompany == 'Client') {
                                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CLIENT + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.CREATEDRAFTSUCCESS, 'success');
                            } else {
                                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.BU.BU + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.BU.CREATEDRAFTSUCCESS, 'success');
                            }

                        } else if(this.isCompanyAdmin) {
                            this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.UPDATESUCCESS, 'success');
                        }
                        else if(!this.companyDetailsUpdateMode) {
                            this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANY + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.COMPANY.COMPANYDRAFTSUCCESS, 'success');
                        }
                        this.companyStepFiveSaved = true;
                        this.saveButtonClicked = false;
                        //No update of company to active state from draft state on click of save.
                        /*if (this.companyManagement.company.companyType != 'Host') {
                            this.genericService.addObject(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.CHANGE_COMPANYSTATE + '/' + this.newlyCreatedCompanyId, this.companyManagement).subscribe(function (data) {
                            }, function (error) {
                            });
                        }*/
                    }, (error) => {
                        if(this.authService.isOnline) {
                            if(error.error && error.error.message) {
                                let updatedMessage = error.error.message;
                                if(updatedMessage.includes('<br>')) {
                                    updatedMessage = error.error.message.split('<br>')[0];
                                }
                                this.alertsAndNotificationsService.showBannerMessage(updatedMessage, 'danger');
                            } else {
                                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                            }
                        }

                        if(error.statusCode == "409") {
                            this.errorInSavingPrefsCheck = true;
                            this.getAssessmentWorkFlow(this.userDetails.company.companyId)
                        } else {
                            this.savedAssessmentWorkFlow = _.cloneDeep(this.savedAssessmentWorkFlowCopy);
                        }
                    });
                } else if(this.valid && techOrValueAssessmentAccessTimesNotChosenFlag) {
                    this.alertsAndNotificationsService.showBannerMessage("Please enable Access Time option(s) to continue.", 'danger');
                    this.step5formsubmitted = true;
                } else if(this.valid && this.hasUnsavedAssessmentCredentials && this.hasUnsavedJobBoardCredentials) {
                    this.showUnsavedCredentialsWarning("Unsaved data will be lost. Assessments and Job Boards Credentials data have not been saved.");
                } else if(this.valid && this.hasUnsavedAssessmentCredentials) {
                    this.showUnsavedCredentialsWarning("Unsaved data will be lost. Assessments Credentials data has not been saved.");
                } else if(this.valid && this.hasUnsavedJobBoardCredentials) {
                    this.showUnsavedCredentialsWarning("Unsaved data will be lost. Job Boards Credentials data has not been saved.");
                } else if(this.valid && !this.stepFiveChanged) {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.NOTHING_TO_SAVE, 'success');
                    this.saveButtonClicked = false;
                    //this.$state.go('missioncontrol.clientsOrBus', null, { reload: false });
                } else if(!this.valid) {
                    this.step5formsubmitted = true;
                }
                this.isTestLinkReadOnly = true;
                break;
            case 6:
                this.stepSixChanged = !(_.isEqual(this.vendors, this.originalStepSix));
                if(this.showRemoveButton == 'Remove') {
                    this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.form.companySecondaryContactPersonDetailsForm.$valid;
                } else {
                    this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid;
                }

                //This condition will satisfy if editing a client/bu
                if(this.clientOrBuUpdateInProgress) {
                    this.newlyCreatedCompanyId = this.companyManagement.company.id;
                }
                if(this.valid && this.stepSixChanged) {
                    this.savedVendorsCopy = _.cloneDeep(this.savedVendors);
                    this.savedVendors = _.cloneDeep(this.vendors);
                    this.limitCharactersInName();
                    this.modifiedVendors = this.getModifiedVendors();
                    this.saveVendorsSaveClicked = true;
                    if(this.modifiedVendors.disassociatedVendors.length > 0) {
                        this.getBuVendorsRequisitionsCountAndLaunchDisassoicateModal();
                    } else {
                        this.saveVendorsOnSave();
                    }
                } else if(this.valid && !this.stepSixChanged) {
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.NOTHING_TO_SAVE, 'success');
                    this.saveButtonClicked = false;
                } else if(!this.valid) {
                    this.step6formsubmitted = true;
                }
                break;
            case 7:
                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.NOTHING_TO_SAVE, 'success');
                break;
        }
    }

    onEnableBrandingChange() {
        if(this.preferences.enableCompanyBranding) {
            this.preferences.frontPageLogoS3Url = this.savedAssessmentWorkFlow.frontPageLogoS3Url;
            this.preferences.addSmallHeaderToReport = this.savedAssessmentWorkFlow.addSmallHeaderToReport;
            this.onEnableSmallLogo();
        } else {
            this.preferences.frontPageLogoS3Url = null;
            this.preferences.addSmallHeaderToReport = false;
            this.onEnableSmallLogo();
        }
    }

    onEnableSmallLogo() {
        if(this.preferences.addSmallHeaderToReport) {
            this.preferences.smallHeaderLogoS3Url = this.savedAssessmentWorkFlow.smallHeaderLogoS3Url;
        } else {
            this.preferences.smallHeaderLogoS3Url = null;
        }
    }

    saveVendorsOnSave() {
        this.companyService.saveBuVendors(this.vendors, (data) => {
            this.saveButtonFlag++;
            this.vendors = _.cloneDeep(data);
            this.originalStepSix = _.cloneDeep(data);
            this.savedVendors = _.cloneDeep(data);
            if(this.clientOrBuUpdateInProgress && !(_.isEqual(this.originalStepSix, this.savedVendorsCopy))) {
                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.BU.BU + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.BU.UPDATESUCCESS, 'success');
            } else if(this.clientOrBuCreationInProgress) {
                this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.BU.BU + " '<b><i class='wrap-word'>" + this.delimitedCompanyOrClientName + "</i></b>' " + this.MESSAGECONSTANTS.NOTIFICATIONS.BU.CREATEDRAFTSUCCESS, 'success');
            }
            this.companyStepSixSaved = true;
            this.saveButtonClicked = false;

        }, (error) => {
            if(this.authService.isOnline) {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            }
        });
    }

    getModifiedVendors() {
        var modifiedVendors = {
            disassociatedVendors: [],
            associatedVendors: []
        };
        this.vendors.vendorDtoList.forEach((vendor, key) => {
            this.originalStepSix.vendorDtoList.forEach((savedVendor, savedVendorKey) => {
                if(vendor.vendorId == savedVendor.vendorId && !vendor.isSelected && savedVendor.isSelected) {
                    modifiedVendors.disassociatedVendors.push(vendor);
                } else if(vendor.vendorId == savedVendor.vendorId && vendor.isSelected && !savedVendor.isSelected) {
                    modifiedVendors.associatedVendors.push(vendor);
                }
            });
        });
        for(var i = 0; i < modifiedVendors.disassociatedVendors.length; i++) {
            modifiedVendors.disassociatedVendors[i].buId = this.vendors.buId;
            modifiedVendors.disassociatedVendors[i].corporateCompanyId = this.vendors.corporateCompanyId;
        }
        return modifiedVendors;
    }

    getBuVendorsRequisitionsCountAndLaunchDisassoicateModal() {
        this.getBuVendorsDetails(0);
    }

    getBuVendorsDetails(index) {
        this.companyService.getSharedReqAndBuCount(this.modifiedVendors.disassociatedVendors[index], (data) => {
            this.modifiedVendors.disassociatedVendors[index].requisitionCount = data.requisitionCount;
            if(index == this.modifiedVendors.disassociatedVendors.length - 1) {
                this.launchDisassociateVendorsModal(this.modifiedVendors);
            } else if(index < this.modifiedVendors.disassociatedVendors.length - 1) {
                this.getBuVendorsDetails(index + 1);
            }
        }, (error) => {
            this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
        });
    }

    launchDisassociateVendorsModal(modifiedVendors) {
        // Ported to the native BsModalService-based disassociate-vendors
        // modal (22b-4d step 7) - see CompanyPageDisassociateVendorsModalService/
        // CompanyPageDisassociateVendorsModalComponent, which embeds the
        // already-native <app-delete-entity entity="buVendorRelationship">
        // component.
        this.companyPageDisassociateVendorsModalService.show({
            buVendorsInfo: modifiedVendors
        }, callbackObject => {
            if(callbackObject.callbackType == 'cancel') {
                this.vendors = _.cloneDeep(this.originalStepSix);
            } else if(callbackObject.callbackType == 'disassociate') {
                this.vendors.note = callbackObject.entity.note;
                if(this.saveVendorsSaveClicked) {
                    this.saveVendorsOnSave();
                } else {
                    this.saveVendorsOnComplete();
                }
            }
        });
    }

    /**
     this.triggerCount = 0;
     * Toggle company address to user checkbox.
     */
    toggleCopyCompanyAddress() {
        this.copyCompanyAddress = (document.getElementById("copyCompanyAddress") as any).checked;
        var companyAddressZipcode;
        if(this.copyCompanyAddress) {
            this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_COMPANY + '/' + this.userDetails.company.companyId).subscribe((data) => {
                companyAddressZipcode = data.body.company.zipcode;
                this.companyManagement.company.address1 = data.body.company.address1;
                this.companyManagement.company.address2 = data.body.company.address2;
                this.companyManagement.company.zipcode = companyAddressZipcode;
                this.cancelClicked = false;
                this.getAddressDetails(companyAddressZipcode, 'copycomanyaddress', (response) => {
                    $('.companyaddresscopyenabled').attr({ 'readonly': true, 'disabled': true });
                });
            }, (error) => {
                if(this.authService.isOnline) {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                }
            });
        } else {
            this.copyCompanyAddress = false;
            $('.companyaddresscopyenabled').attr({ 'readonly': false, 'disabled': false });
            if(this.accessMode == this.MESSAGECONSTANTS.ACCESS_MODES.EDIT) {
                this.companyManagement.company.address1 = this.savedCompanyManagement.company.address1;
                this.companyManagement.company.address2 = this.savedCompanyManagement.company.address2;
                this.companyManagement.company.zipcode = this.savedCompanyManagement.company.zipcode;
                this.getAddressDetails(this.companyManagement.company.zipcode, 'zipchanged', (response) => {
                    if(!_.isUndefined(this.savedCompanyManagement.company.county) && (this.savedCompanyManagement.company.county !== null)
                        && (this.companyManagement.company.zipcode === this.savedCompanyManagement.company.zipcode)) {
                        this.companyManagement.company.county = this.savedCompanyManagement.company.county;
                        // this.$scope.safeApply();
                    }
                });
            } else {
                this.companyManagement.company.address1 = this.savedCompanyManagement.company.address1;
                this.companyManagement.company.address2 = this.savedCompanyManagement.company.address2;
                this.companyManagement.company.zipcode = this.savedCompanyManagement.company.zipcode;
                this.getAddressDetails(this.companyManagement.company.zipcode, 'zipchanged', (response) => {
                    if(!_.isUndefined(this.savedCompanyManagement.company.county) && (this.savedCompanyManagement.company.county !== null)
                        && (this.companyManagement.company.zipcode === this.savedCompanyManagement.company.zipcode)) {
                        this.companyManagement.company.county = this.savedCompanyManagement.company.county;
                        // this.$scope.safeApply();
                    }
                });
            }
        }
    }

    updatedLicensePrefsOnSave() {
        this.genericService.getObjects(this.COMPANYMANAGEMENTCONSTANTS.CONTROLLER.GET_ASSESSMENT_WORK_FLOW + '' + this.userDetails.company.companyType + '/' + this.userDetails.company.companyId).subscribe((data) => {
            //this.originalStepFive = _.cloneDeep(data.body);
            if(!_.isNull(data.body.jobBoardCredentialList)) {
                data.body.jobBoardCredentialList = this.massageJobBoardCredentials(data.body.jobBoardCredentialList);
            }
            this.savedAssessmentWorkFlow = _.cloneDeep(data.body);
            this.preferences = _.cloneDeep(this.savedAssessmentWorkFlow);
            this.massageWorkflowStepsOrder();
            this.licensePreferencesFetched = true;
            this.accessTimeOptionsDisabled = !(this.preferences.techAssessmentList.licensePrefDetailsList[0].selected || this.preferences.valueAssessmentList.licensePrefDetailsList[0].selected);
            this.stepFiveChanged = !(_.isEqual(this.originalStepFive, this.preferences));
        }, (error) => {
            if(this.authService.isOnline) {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            }
        });
    }

    manageStepDataAfterTimeout(Company, ContactList, FourDotFiveAdmin) {
        setTimeout(() => {
            this.manageStepData(Company, ContactList, FourDotFiveAdmin);
        }, 1000);
    }

    /** Function to control wizard flow with validations
     *
     */
    manageStepData(Company, ContactList, FourDotFiveAdmin) {
        //alert(this.triggerCount);

        if(this.triggerCount === 0) {
            this.triggerCount = _.cloneDeep(this.triggerCount + 1);
            this.formsubmitted = true;
            this.nextButtonClicked = true;
            this.saveButtonClicked = false;
            this.StorageService.remove('wizardoperations');
            this.valid = false;
            var currentStep = this.getCurrentStep();
            //alert(currentStep);
            if(currentStep == 3 && ((this.TypeOfCompany == 'Client') || this.StorageService.get('SelectedHostCompany') != null || this.TypeOfCompany == 'BusinessUnit')) {
                currentStep = 5;
            }
            if(currentStep == 4 && this.TypeOfCompany == 'BusinessUnit') {
                currentStep = 6;
            }
            if(currentStep == 4 && this.TypeOfCompany == 'Client' && this.isAssociatedClientUpdateInProgress) {
                currentStep = 7;
            }

            switch(currentStep) {
                case 1:
                    this.stepOneChanged = !(_.isEqual(this.originalStepOne, this.companyManagement.company));//Checks if any changes.
                    if(this.companyManagement.company.address1 !== null && this.companyManagement.company.address1 !== undefined) {
                        if(this.companyManagement.company.address2 != null) {
                            this.isAddressValid = !_.isEqual(this.companyManagement.company.address1.toUpperCase(), this.companyManagement.company.address2.toUpperCase()) && (this.companyManagement.company.address1 != this.companyManagement.company.address2);
                        } else {
                            this.isAddressValid = this.companyManagement.company.address1 != this.companyManagement.company.address2;
                        }
                    }
                    this.valid = this.form.companyDetailsForm.$valid && this.isAddressValid && this.validzipcode && !_.isUndefined(this.validzipcode) && (!this.zipCodeCalling || (this.zipCodeCalling && this.zipCodeCallCompleted));//Checks validations.
                    if(this.valid) {
                        if(this.isAssociatedClientUpdateInProgress) {
                            this.nextButtonClicked = false;
                            this.disableActionButtons = true;
                            if(this.stepOneChanged) {
                                this._setSharedClientDescription(() => {
                                    this.disableActionButtons = false;
                                    this.nextButtonFlag++;
                                    this.isCompanyDetailsSaveCalled = true;
                                    // this.triggerCount = 0;
                                    this.originalStepOne = _.cloneDeep(this.companyManagement.company);
                                    //Building the SavedCompanyManagement object before saving.
                                    this.savedCompanyManagementCopy.company = _.cloneDeep(this.savedCompanyManagement.company);
                                    this.savedCompanyManagement.company = _.cloneDeep(this.companyManagement.company);
                                    // this.stepTrigger('nextbtnclick');
                                    this.disableActionButtons = false;
                                    this.StorageService.set('wizardoperations', true, null);
                                    this.stepTrigger('nextbtnclick');
                                }, (error) => {
                                    this.triggerCount = 0;
                                    this.disableActionButtons = false;
                                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                                    this.savedCompanyManagement.company = _.cloneDeep(this.savedCompanyManagementCopy.company);
                                });
                            } else if(!this.stepOneChanged) {
                                this.step1FormSubmitted = true;
                                this.triggerCount = 0;
                                this.StorageService.set('wizardoperations', true, null);
                                this.disableActionButtons = false; 
                            }
                        } else {
                            if(!!_.isUndefined(this.companyManagement.company.companyType)) {
                                if(this.userDetails.company.companyType == 'Corporation') {
                                    this.companyManagement.company.companyType = 'BusinessUnit';
                                } else if(this.userDetails.company.companyType == 'StaffingCompany') {
                                    this.companyManagement.company.companyType = 'Client';
                                }
                            }
                            this.nextButtonClicked = false;
                            this.disableActionButtons = true;
                            this.saveCompanyDetails();
                        }
                    } else {
                        this.step1FormSubmitted = true;
                        this.triggerCount = 0;
                        this.disableActionButtons = false;
                    }
                    if(this.savedCompanyManagement.company.companyType === "Host") {
                        this.isValidPrimaryWorkPhoneEntered = true;
                        this.isValidPrimaryMobileEntered = true;
                        this.isValidSecondaryWorkPhoneEntered = true;
                        this.isValidSecondaryMobileEntered = true;
                        this.phoneValidationStatuses.forEach((status, key) => {
                            status.isValidWorkNumber = true;
                            status.isValidMobileNumber = true;
                        });
                    }
                    break;
                case 2:
                    if(this.TypeOfCompany == 'Company') {
                        this.getAllPlans();
                    }
                    this.cleanContactList();
                    this.errorOcurred = false;
                    this.stepTwoChanged = !(_.isEqual(this.originalStepTwo, this.companyManagement.contactList));

                    if(this.companyManagement.contactList.length == 1) {
                        this.companyManagement.contactList[0].primary = false;
                    }
                    if(this.showRemoveButton) {
                        this.companyManagement.contactList.forEach((contact, key) => {
                            contact.primary = false;
                        });
                        this.validateSecondaryDetails = true;
                        this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.form.companySecondaryContactPersonDetailsForm.$valid
                            && this.isValidPrimaryMobileEntered && this.isValidPrimaryWorkPhoneEntered && this.isValidSecondaryMobileEntered && this.isValidSecondaryWorkPhoneEntered && this.isValidPhoneNumbersEntered();
                    } else {
                        this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.isValidPrimaryMobileEntered && this.isValidPrimaryWorkPhoneEntered;
                    }
                    if(this.valid) {
                        this.nextButtonClicked = false;
                        this.saveContactDetails();
                    } else if(this.StorageService.get('companyDetails') != null && !(this.companyDetailsUpdateMode) && !this.clientOrBuCreationInProgress && !this.clientOrBuUpdateInProgress && !this.vendorUpdateInProgress) {
                        this.StorageService.set('wizardoperations', true, null);
                        if(this.clientOrBuUpdateInProgress) {
                            this.laststep = true;
                        }
                        this.nextButtonClicked = false;
                        this.triggerCount = 0;
                        this.disableActionButtons = false;
                    } else {
                        this.step2FormSubmitted = true;
                        this.triggerCount = 0;
                        this.disableActionButtons = false;
                    }
                    break;
                case 3:

                    this.oldCheckedAdmin = null;
                    if(!_.isUndefined(this.originalStepThree) && this.originalStepThree != null && this.selectedFourDotFiveAdmin.length > 0) {
                        this.stepThreeChanged = !_.isEqual(this.originalStepThree.userId, this.selectedFourDotFiveAdmin[0].userId) || !_.isEqual(this.companyManagement.userConfiguration, this.CompanyManagementReset.userConfiguration);
                        this.oldFourDotFiveAdminId = this.originalStepThree.userId;
                    } else if(!!_.isUndefined(this.originalStepThree) || (this.originalStepThree === null)) {
                        /**
                         * First condition is satisfied in create company.
                         * Second condition is satisfied when creating a company in
                         * Draft state( without selecting 4dot5admin) and coming to update for the next time later
                         */
                        this.stepThreeChanged = true;
                    }
                    if(this.showRemoveButton) {
                        this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.form.companySecondaryContactPersonDetailsForm.$valid && this.selectedFourDotFiveAdmin.length > 0;
                    } else {
                        this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.selectedFourDotFiveAdmin.length > 0;
                    }
                    if(this.valid) {
                        this.valid = this.isPasswordRestrictionsValid();
                        if(!this.valid) {
                            this.complexityRestrictionChange('user');
                            this.complexityRestrictionChange('administrator');
                        }
                    }

                    if(this.valid) {
                        this.nextButtonClicked = false;
                        this.saveFourDotFiveAdminDetails();
                    } else if(this.StorageService.get('companyDetails') != null && !(this.companyDetailsUpdateMode)) {
                        if(!this.licensePreferencesFetched || this.isCompanyDetailsSaveCalled) {
                            this.disableActionButtons = true;
                            this.getAssessmentWorkFlow(this.StorageService.get('companyDetails').companyId, () => {
                                this.disableActionButtons = false;
                            });
                        } else {
                            this.StorageService.set('wizardoperations', true, null);
                            this.stepTrigger('nextbtnclick');
                        }
                        this.laststep = true;
                        this.nextButtonClicked = false;
                        this.StorageService.set('wizardoperations', true, null);
                        this.triggerCount = 0;
                    } else {
                        this.step3FormSubmitted = true;
                        this.triggerCount = 0;
                    }
                    break;
                case 4:
                    this.stepFourNextClicked = true;

                    // if (this.isPlanChosen && this.isActivePlanExists) {
                    //     this.stepFourChanged = !(_.isEqual(this.originalStepFour, this.selectedPlan));
                    // }
                    // else if(this.isPlanChosen && !this.isActivePlanExists) {
                    //     this.stepFourChanged = true;
                    // }
                    if(this.isPlanChosen && this.isActivePlanExists) {
                        this.nextButtonClicked = false;
                        this.savePlanDetails();

                    }
                    else if(this.StorageService.get('companyDetails') != null && !(this.companyDetailsUpdateMode)) {
                        this.laststep = true;
                        this.StorageService.remove('wizardoperations');
                        this.triggerCount = 0;
                        this.nextButtonClicked = false;
                        this.laststep = true;
                    } else {
                        this.step4formsubmitted = true;
                        this.triggerCount = 0;
                    }

                    break;
                case 5:
                    this._setHasUnsavedAssessmentCredentials();
                    this._setHasUnsavedJobBoardCredentials();
                    this.parseWorkflowStepsOrderBeforeSave();
                    this.stepFiveChanged = !(_.isEqual(this.companyManagement.licensePreferences, this.preferences));
                    // this.stepFiveChanged = !(_.isEqual(this.originalStepFive, this.preferences));

                    if(this.TypeOfCompany == 'Client' || this.TypeOfCompany == 'BusinessUnit' || this.companyManagement.company.companyType == 'Host') {
                        if(this.showRemoveButton) {
                            this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.form.companySecondaryContactPersonDetailsForm.$valid && this.licenseScoringFormValid && this.assessmentsFormValid && this.jobBoardsFormValid;
                        } else {
                            this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.licenseScoringFormValid && this.assessmentsFormValid && this.jobBoardsFormValid;
                        }
                    } else {
                        if(this.showRemoveButton) {
                            this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.form.companySecondaryContactPersonDetailsForm.$valid && this.selectedFourDotFiveAdmin.length > 0 && this.licenseScoringFormValid && this.assessmentsFormValid && this.jobBoardsFormValid;
                        } else {
                            this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.selectedFourDotFiveAdmin.length > 0 && this.licenseScoringFormValid && this.assessmentsFormValid && this.jobBoardsFormValid;
                        }
                    }
                    var techOrValueAssessmentAccessTimesNotChosenFlag = this._accessTimeOptionErrorExists();
                    if(this.valid && !techOrValueAssessmentAccessTimesNotChosenFlag && !this.hasUnsavedAssessmentCredentials && !this.hasUnsavedJobBoardCredentials) {
                        this.nextButtonClicked = false;
                        this.saveAssessmentFlowDetails();
                    } else if(this.valid && techOrValueAssessmentAccessTimesNotChosenFlag) {
                        this.alertsAndNotificationsService.showBannerMessage("Please enable Access Time option(s) to continue.", 'danger');
                        this.triggerCount = 0;
                    } else if(this.valid && this.hasUnsavedAssessmentCredentials && this.hasUnsavedJobBoardCredentials) {
                        this.showUnsavedCredentialsWarning("Unsaved data will be lost. Assessments and Job Boards Credentials data have not been saved.");
                        this.triggerCount = 0;
                    } else if(this.valid && this.hasUnsavedAssessmentCredentials) {
                        this.showUnsavedCredentialsWarning("Unsaved data will be lost. Assessments Credentials data has not been saved.");
                        this.triggerCount = 0;
                    } else if(this.valid && (this.hasUnsavedJobBoardCredentials)) {
                        this.showUnsavedCredentialsWarning("Unsaved data will be lost. Job Boards Credentials data has not been saved.");
                        this.triggerCount = 0;
                    } else if(this.StorageService.get('companyDetails') != null && !(this.companyDetailsUpdateMode) && !this.clientOrBuCreationInProgress && !this.clientOrBuUpdateInProgress) {
                        this.laststep = true;
                        this.StorageService.remove('wizardoperations');
                        this.triggerCount = 0;
                        this.nextButtonClicked = false;
                        /*  setTimeout(function () {
                              this.$state.go('missioncontrol.dashboard', { companyObject: 'companyObject' }, { reload: false });
                          }, 4000);*/
                    } else {
                        this.step5formsubmitted = true;
                        this.triggerCount = 0;
                    }

                    break;
                case 6:
                    this.stepSixChanged = !(_.isEqual(this.vendors, this.originalStepSix));
                    if(this.TypeOfCompany == 'Client' || this.TypeOfCompany == 'BusinessUnit' || this.companyManagement.company.companyType == 'Host') {
                        if(this.showRemoveButton) {
                            this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid && this.form.companySecondaryContactPersonDetailsForm.$valid;
                        } else {
                            this.valid = this.form.companyDetailsForm.$valid && this.form.companyContactPersonDetailsForm.$valid;
                        }
                    }
                    if(this.valid) {
                        this.nextButtonClicked = false;
                        this.saveVendors();
                    } else if(this.StorageService.get('companyDetails') != null && !(this.companyDetailsUpdateMode) && !this.clientOrBuCreationInProgress && !this.clientOrBuUpdateInProgress) {
                        this.laststep = true;
                        this.StorageService.remove('wizardoperations');
                        this.triggerCount = 0;
                        this.nextButtonClicked = false;
                    } else {
                        this.step6formsubmitted = true;
                        this.triggerCount = 0;
                    }
                    break;
                case 7:
                    this.laststep = true;
                    this.StorageService.set('wizardoperations', true, null);
                    this.lastStepCompleted = true;
                    this.alertsAndNotificationsService.showBannerMessage(this.MESSAGECONSTANTS.NOTIFICATIONS.CLIENT.NOTHING_TO_UPDATED, 'success');
                    const navigateAfterStepSeven = () => {
                        setTimeout(() => {
                            this.$state.go('missioncontrol.clientsOrBus', null, { reload: true });
                        }, 300);
                    };
                    if(this.companyManagement.companyState == 'Draft' || this.companyManagement.companyState == null) {
                        this.changeCompanyState(navigateAfterStepSeven, navigateAfterStepSeven);
                    } else {
                        navigateAfterStepSeven();
                    }
                    break;
                default:
            }
        } else {
            this.triggerCount = 0;
        }
    }


    toggleAssessmentWorkFlowValues(index, workflow) {
        var showWarningFlag = false;
        if(!workflow.selected) {
            if(workflow.name == 'Auto Match') {
                this.preferences.matchCount = null;
            }
            if(workflow.candidatesPresentOnStep) {
                switch(workflow.name) {
                    case "Recruiter Screening":
                        showWarningFlag = true;
                        break;
                    case "Tech Assessment":
                        showWarningFlag = true;
                        break;
                    case "Value Assessment":
                        showWarningFlag = true;
                        break;
                    case "Phone Screen":
                        showWarningFlag = true;
                        break;
                    case "Interview":
                        showWarningFlag = true;
                        break;
                    case "Education Verification":
                        showWarningFlag = true;
                        break;
                    case "Experience Verification":
                        showWarningFlag = true;
                        break;
                }
                if(showWarningFlag) {
                    workflow.selected = true;
                    this.showCandidatesAvailableOnStepWarning(workflow.messageForCandidatePresent, workflow.name);
                }
            }
        } else {
            if(workflow.name == 'Auto Match') {
                this.preferences.matchCount = 2;
            }
        }
        this.massageWorkflowStepsOrder();
        this.accessTimeOptionsDisabled = !(this.preferences.techAssessmentList.licensePrefDetailsList[0].selected || this.preferences.valueAssessmentList.licensePrefDetailsList[0].selected);
        this.stepFiveChanged = !(_.isEqual(this.originalStepFive, this.preferences));
    }

    showCandidatesAvailableOnStepWarning(message, stepName) {
        let context = "";
        if(this.TypeOfCompany == "BusinessUnit" || this.TypeOfCompany == "Client") {
            context = "clientOrBu";
        } else if(this.userDetails.company.companyType == 'Corporation') {
            context = "corporation";
        } else {
            context = "staffingCompany";
        }
        // Ported to the native BsModalService-based workflow-step-warning
        // modal (22b-4d step 9) - see CompanyPageWorkflowStepWarningModalService/
        // CompanyPageWorkflowStepWarningModalComponent.
        this.companyPageWorkflowStepWarningModalService.show({
            companyId: this.userDetails.company.companyId,
            clientOrBuId: this.newlyCreatedCompanyId,
            context: context,
            stepName: stepName,
            message: message
        }, (callbackObject) => {
            //do nothing
        });
    }

    massageWorkflowStepsOrder() {
        this.workflowStepsOrder = _.cloneDeep(this.preferences.workflowStepsOrder);
        this.isAnyWorkflowStepsEnabled = false;
        //setting id for dnd list to work
        _.forEach(this.workflowStepsOrder, (steps, jobType) => {
            var stepsDetails = [];
            steps.forEach((step, index) => {
                var isStepEnabled = true;
                if(step == "Recruiter Screening") {
                    isStepEnabled = this.preferences.recruiterScreening.licensePrefDetailsList[0].selected && this.preferences.recruiterScreening.licensePrefDetailsList[0].enabled;
                } else if(step == "Tech Assessment") {
                    isStepEnabled = this.preferences.techAssessmentList.licensePrefDetailsList[0].selected && this.preferences.techAssessmentList.licensePrefDetailsList[0].enabled;
                } else if(step == "Value Assessment") {
                    isStepEnabled = this.preferences.valueAssessmentList.licensePrefDetailsList[0].selected && this.preferences.valueAssessmentList.licensePrefDetailsList[0].enabled;
                } else if(step == "Phone Screen") {
                    isStepEnabled = this.preferences.phoneScreenList.licensePrefDetailsList[0].selected && this.preferences.phoneScreenList.licensePrefDetailsList[0].enabled;
                } else if(step == "Interview") {
                    isStepEnabled = this.preferences.interviewList.licensePrefDetailsList[0].selected && this.preferences.interviewList.licensePrefDetailsList[0].enabled;
                }
                if(isStepEnabled) {
                    this.isAnyWorkflowStepsEnabled = true;
                }
                stepsDetails.push({ id: index, stepName: step, enabled: isStepEnabled, jobType: jobType });
            });
            this.workflowStepsOrder[jobType] = _.cloneDeep(stepsDetails);

        });
        this.workflowStepsOrderToRender = {};
        _.forEach(this.workflowStepsOrder, (steps, jobType) => {
            this.workflowStepsOrderToRender[jobType] = _.cloneDeep(
                steps.filter((step, index) => index !== 0 && index !== steps.length - 1 && step.enabled)
            );
        });
    }

    parseWorkflowStepsOrderBeforeSave() {
        _.forEach(this.workflowStepsOrder, (steps, jobType) => {
            var stepDetails = [];
            var renderIndex = 0;
            steps.forEach((step, index) => {
                if(index !== 0 && index !== steps.length - 1 && step.enabled) {
                    stepDetails.push(this.workflowStepsOrderToRender[jobType][renderIndex]);
                    renderIndex++;
                } else {
                    stepDetails.push(step);
                }
            });
            this.workflowStepsOrder[jobType] = _.cloneDeep(stepDetails);
        });
        var workflowStepsOrderCopy = _.cloneDeep(this.workflowStepsOrder);
        _.forEach(this.workflowStepsOrder, (steps, jobType) => {
            var stepsDetails = [];
            steps.forEach((step, index) => {
                stepsDetails.push(step.stepName);
            });
            workflowStepsOrderCopy[jobType] = _.cloneDeep(stepsDetails);
        });
        this.preferences.workflowStepsOrder = _.cloneDeep(workflowStepsOrderCopy);
    }


    /**
     * This function is called on click of 'Add Secondary Contact' button.
     * It allows the user to add secondary contact person for the company/client.
     */
    isSecondaryContactSelected(action) {
        if(action == 'add') {
            var validationStatus = {
                emptyWorkNumber: false,
                invalidWorkNumber: false,
                isValidWorkNumber: false,
                emptyMobileNumber: false,
                invalidMobileNumber: false,
                isValidMobileNumber: false
            }
            this.phoneValidationStatuses.push(validationStatus);
            // AngularJS's ngModel tolerated contactList[1] being undefined (lenient $parse),
            // but native Angular's [(ngModel)] dereferences it directly and throws. Explicitly
            // create the blank secondary contact so contactList[1] exists before the template renders.
            if(!this.companyManagement.contactList[1]) {
                this.companyManagement.contactList.push({
                    email: null,
                    firstName: null,
                    im: null,
                    isHiringManager: true,
                    isPrimary: false,
                    lastName: null,
                    managerId: null,
                    mobilePhone: null,
                    userId: null,
                    workPhone: null
                });
            }
            this.showRemoveButton = true;
        } else {
            if(this.companyManagement.contactList && this.companyManagement.contactList[1] && this.companyManagement.contactList[1].userId) {
                this.userService.getDeleteUserInfo(this.companyManagement.contactList[1].userId, (data) => {
                    if(data.requisitionDetails && data.requisitionDetails.length > 0) {
                        this.deleteContactUserInfoModal(this.companyManagement.contactList[1], data, (data) => {});
                    } else {
                        this._removeSecondaryContact()
                    }
                }, (error) => {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                });
            } else {
                this._removeSecondaryContact();
            }
        }
    }

    _removeSecondaryContact() {
        if(this.companyManagement.contactList.length > 1) {
            this.companyManagement.contactList.splice(1, 1);
            this.phoneValidationStatuses.splice(0, 1);
        }
        if(this.companyManagement.contactList.length > 1) {
            this.showRemoveButton = true;
        } else {
            this.showRemoveButton = false;
        }
        this.validateSecondaryDetails = false;
        this.form.companySecondaryContactPersonDetailsForm.$setPristine();
    }

    deleteContactUserInfoModal(userDetails, deleteUserInfo, successCallback) {
        // Ported to the native BsModalService-based delete-company-contact
        // modal (22b-4d step 8) - see CompanyPageDeleteCompanyContactModalService/
        // CompanyPageDeleteCompanyContactModalComponent.
        this.companyPageDeleteCompanyContactModalService.show({
            userDetails: userDetails,
            deleteContactUserInfo: deleteUserInfo
        }, callbackObject => {
            if(successCallback) {
                successCallback(callbackObject);
            }
        });
    }

    // Draft companies saved before Step 2 was ever filled in come back from the API with an
    // empty contactList. <app-company-page-step-contact> only renders when contactList[0]
    // exists (see company-page.component.html), so without this the Primary Contact step
    // shows completely blank with no way to add a contact. Re-seed the same blank primary
    // contact the constructor uses for brand-new companies.
    _ensurePrimaryContact() {
        if(!this.companyManagement.contactList || this.companyManagement.contactList.length === 0) {
            this.companyManagement.contactList = [this._createBlankPrimaryContact()];
        }
    }

    _createBlankPrimaryContact() {
        return {
            email: null,
            firstName: null,
            im: null,
            isHiringManager: true,
            isPrimary: true,
            primary: true,
            lastName: null,
            managerId: null,
            mobilePhone: null,
            userId: null,
            workPhone: null
        };
    }

    //add contacts
    addContact() {
        var contact = {
            email: null,
            firstName: null,
            im: null,
            isHiringManager: true,
            isPrimary: false,
            lastName: null,
            mobilePhone: null,
            userId: null,
            workPhone: null
        }
        this.companyManagement.contactList.push(contact);
        var validationStatus = {
            emptyWorkNumber: false,
            invalidWorkNumber: false,
            isValidWorkNumber: false,
            emptyMobileNumber: false,
            invalidMobileNumber: false,
            isValidMobileNumber: false
        }
        this.phoneValidationStatuses.push(validationStatus);
    }

    removeContact(index) {
        if(this.companyManagement.contactList && this.companyManagement.contactList[index] && this.companyManagement.contactList[index].userId) {
            this.userService.getDeleteUserInfo(this.companyManagement.contactList[index].userId, (data) => {
                if(data.requisitionDetails && data.requisitionDetails.length > 0) {
                    this.deleteContactUserInfoModal(this.companyManagement.contactList[index], data, (data) => {});
                } else {
                    this._removeContact(index);
                }
            }, (error) => {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            });
        } else {
            this._removeContact(index);
        }
    }

    _removeContact(index) {
        this.companyManagement.contactList.splice(index, 1);
        this.phoneValidationStatuses.splice(index - 1, 1);
        if(this.companyManagement.contactList.length > 1) {
            this.showRemoveButton = true;
        }
    }

    isValidPhoneNumbersEntered() {
        if(this.phoneValidationStatuses.length < 2) {
            return true;
        }
        for(var i = 1; i < this.phoneValidationStatuses.length; i++) {
            if(!this.phoneValidationStatuses[i].isValidMobileNumber || !this.phoneValidationStatuses[i].isValidWorkNumber) {
                return false;
            }
        }
        return true;
    }

    switchToContactOrHiringManager(type, contact) {
        if(type == 'contact' && !contact.userId) {
            contact.isHiringManager = false;
        } else if(type == 'contact') {
            this.userService.getDeleteUserInfo(contact.userId, (data) => {
                if(data.requisitionDetails && data.requisitionDetails.length > 0) {
                    contact.isHiringManager = true;
                    this.deleteContactUserInfoModal(contact, data, (data) => {});
                } else {
                    contact.isHiringManager = false;
                }
            }, (error) => {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            });
        }
    }

    _setFIlteredContacts(isCompany, primaryContact) {
        let filteredContacts = [];
        this.companyManagement.contactList.map(contact => {
            if (isCompany) {
                let savedContactsEmailList = this.savedCompanyManagement.contactList.map(contact => contact.email);
                if (savedContactsEmailList.includes(contact.email) && (contact.email != primaryContact.email)) {
                    filteredContacts.push(contact);
                }
            } else {
                if (contact.userId && contact.userId != primaryContact.userId) {
                    filteredContacts.push(contact);
                }
            }
        });
        return filteredContacts;
    }

    removePrimaryContact(primaryContact) {
        let filteredContacts = [];
        filteredContacts = this._setFIlteredContacts(this.TypeOfCompany == 'Company', primaryContact);
        let deleteContactInfo = {
            activityType: 'removePrimaryContact',
            activityTitle: 'Remove Primary Contact',
            activityMessage: '',
            contactsList: filteredContacts
        };

        if (filteredContacts.length > 0) {
            deleteContactInfo.activityMessage = "Choose a contact to replace current primary contact.";

            // Check if primary contact is associated with requistions
            if (this.TypeOfCompany == 'Company') {
                this._deletePrimaryContact(primaryContact, deleteContactInfo, this.TypeOfCompany == 'Company');
            } else {
                this.userService.getDeleteUserInfo(primaryContact.userId, (data) => {
                    if (data.requisitionDetails && data.requisitionDetails.length > 0) {
                        this.deleteContactUserInfoModal(primaryContact, data, (data) => { });
                    } else {
                        this._deletePrimaryContact(primaryContact, deleteContactInfo, this.TypeOfCompany == 'Company');
                    }
                }, (error) => {
                    this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
                });
            }
        } else {
            deleteContactInfo.activityMessage = "Please add a contact before deleting the primary contact";
            this.deleteContactUserInfoModal(primaryContact, deleteContactInfo, (data) => {
                // Do Nothing
            });
        }
    }

    _deletePrimaryContact(primaryContact, deleteContactInfo, isCompany) {
        this.deleteContactUserInfoModal(primaryContact, deleteContactInfo, (primaryContactObject) => {
            // Replace Primary contact with selected contact
            if (primaryContactObject && primaryContactObject.entity && primaryContactObject.entity.primaryContact) {
                let newPrimaryContact = primaryContactObject.entity.primaryContact;
                let index = 0;

                if (isCompany) {
                    this.companyManagement.contactList = this.companyManagement.contactList.filter(contact => contact.email != primaryContact.email);
                    index = this.companyManagement.contactList.findIndex(contact => contact.email === newPrimaryContact.email);
                } else {
                    this.companyManagement.contactList = this.companyManagement.contactList.filter(contact => contact.userId != primaryContact.userId);
                    index = this.companyManagement.contactList.findIndex(contact => contact.userId === newPrimaryContact.userId);
                }

                if (index > 0 && index < this.companyManagement.contactList.length) {
                    const [objectToMove] = this.companyManagement.contactList.splice(index, 1);
                    this.companyManagement.contactList.unshift(objectToMove);
                }

                // Remove if secondary contact form appear
                if (this.companyManagement.contactList.length == 1 && this.showRemoveButton) {
                    this._removeSecondaryContact();
                }
            }
        });
    }
    /**
     * This method will be called when user clicks on 'Prev' button of wizard
     */
    previousButtonClicked() {
        if(this.form && this.form.companyContactPersonDetailsForm) {
            this.form.companyContactPersonDetailsForm.$setPristine();
        }
        // this.disableActionButtons = false;
        this.step2FormSubmitted = false;
        this.saveButtonClicked = false;
        this.nextButtonClicked = false;
        //Company admin can edit only company workflow steps credentials
        if(this.isCompanyAdmin && this.TypeOfCompany == 'Company') {
            this.isStepReadOnlyForCompanyAdmin = true;
        }
        this.laststep = false;
        if(this.count === 0 && this.StorageService.get('companyDetails') == null && this.StorageService.get('SelectedHostCompany') == null && this.companyManagement.companyState != 'Draft') {
            this.count = this.count + 1;
            var action = setTimeout(() => {
                this.StorageService.set('wizardoperations', true, null);
                $('.btn-prev').triggerHandler('click');
                this.StorageService.remove('wizardoperations');
                this.count = 0;
                clearTimeout(action);
            }, 0);
        } else {
            if(this.StorageService.get('SelectedHostCompany') == null) {
                this.AssessmentWorkFlow = [];
            }
            if(this.companyDetailsUpdateMode || this.ClientOrBuCreate || this.ClientOrBuUpdate || this.StorageService.get('SelectedHostCompany') != null || this.companyManagement.companyState == 'Draft') {
                this.StorageService.set('wizardoperations', true, null);
                this.triggerCount = 0;
            } else if(_.isEqual(this.userDetails.roleScreenRef.role.name, 'StaffingAdmin') || _.isEqual(this.userDetails.roleScreenRef.role.name, 'CorporateAdmin')) {
                this.StorageService.set('wizardoperations', true, null);
                this.triggerCount = 0;
            }
        }
    }

    /**
     * Clear data on the form
     */
    cancel() {
        this.showConfirmationPopup = this.hasDataChanged();
        if(this.showConfirmationPopup) {
            this.alertsAndNotificationsService.showConfirm("Warning!", "Unsaved data on the page will be lost. Do you still want to continue?", "warning", true, () => {
                this.clearData();
                this.goToListingOrDashboardPage();
            }, () => {
                this.showConfirmationPopup = false;
            });
        } else {
            this.goToListingOrDashboardPage();
        }
    }

    goToListingOrDashboardPage() {
        if(this.TypeOfCompany == 'Client' || this.TypeOfCompany == 'BusinessUnit') {
            this.$state.go('missioncontrol.clientsOrBus', null, { reload: false });
        } else if(this.TypeOfCompany == 'Vendor') {
            this.$state.go('missioncontrol.vendors', null, { reload: false });
        } else if(this.companyManagement.company.companyType == 'Host') {
            // this.$state.go('missioncontrol.dashboardsuperusermodule', { companyObject: 'companyObject' }, { reload: false });
        } else if(this.userDetails.company.companyState !== 'Draft') {
            if(!this.isCompanyVendor) {
                this.$state.go('missioncontrol.dashboard', { companyObject: 'companyObject' }, { reload: false });
            } else {
                this.$state.go('missioncontrol.requisitions', { companyObject: 'companyObject' }, { reload: false });
            }
        }
    }

    cancelSteps() {
        this.cancel();
    }

    clearData() {

        this.cancelNotClicked = false;
        this.cancelClicked = false;
        this.copyOfGeoLocations = _.cloneDeep(this.geoLocations);
        this.copyOfGeoLocationsCopy = _.cloneDeep(this.geoLocationsCopy);
        var cancelCurrentStep = this.getCurrentStep();
        if(cancelCurrentStep == 3 && (this.TypeOfCompany == 'Client' || this.StorageService.get('SelectedHostCompany') != null || this.TypeOfCompany == 'BusinessUnit')) {
            cancelCurrentStep = 4;
        }
        // this.CompanyManagementReset.fourDotFiveAdmin = _.cloneDeep(this.companyManagement.fourDotFiveAdmin);
        this.showConfirmationPopup = false;

        switch(cancelCurrentStep) {
            case 1:
                if(!_.isUndefined(this.companyManagement.companyState)) {
                    this.cancelClicked = true;
                    if(!_.isEqual(this.companyManagement.company.zipcode, this.originalStepOne.zipcode)) {
                        this.getAddressDetails(this.originalStepOne.zipcode, 'zipchanged');
                    } else {
                        this.getAddressDetails(this.originalStepOne.zipcode, 'updateaddress');
                    }
                    this.step1FormSubmitted = false;
                    this.companyManagement.company = _.cloneDeep(this.originalStepOne);
                    this.savedCompanyManagement.company = _.cloneDeep(this.originalStepOne);
                    this.form.companyDetailsForm.$setPristine();
                } else {
                    this.form.companyDetailsForm.$setPristine();
                    this.geoLocations = [];
                    this.companyManagement.company = {};
                    this.cancelClicked = true;
                    this.getAddressDetails(this.companyManagement.company.zipcode, 'zipchanged');
                    this.step1FormSubmitted = false;
                }
                break;
            case 2:
                if(this.originalStepTwo != undefined && this.originalStepTwo.length != 0) {
                    this.companyManagement.contactList = _.cloneDeep(this.originalStepTwo);
                    this.isValidPrimaryMobileEntered = true;
                    this.isValidPrimaryWorkPhoneEntered = true;
                    if(this.originalStepTwo.length > 1) {
                        this.showRemoveButton = true;
                        this.isValidSecondaryMobileEntered = true;
                        this.isValidSecondaryWorkPhoneEntered = true;
                        this.phoneValidationStatuses.forEach((status, key) => {
                            status.isValidWorkNumber = true;
                            status.isValidMobileNumber = true;
                        });
                        setTimeout(() => {
                            this.form.companySecondaryContactPersonDetailsForm.$setPristine();
                        }, 1000);
                    } else {
                        this.invalidCompanySecondaryWorkNumber = false;
                        this.invalidCompanySecondaryMobilePhone = false;
                    }

                    setTimeout(() => {
                        if(this.form && this.form.companyContactPersonDetailsForm) {
                            this.form.companyContactPersonDetailsForm.$setPristine();
                        }
                    }, 1000);
                    this.step2FormSubmitted = false;
                } else {
                    this.companyManagement.contactList = [];
                    (document.getElementById('maskedWorkPhone') as any).value = '';
                    (document.getElementById('maskedWorkPhone1') as any).value = '';
                    if(this.showRemoveButton) {
                        this.invalidCompanySecondaryMobilePhone = false;
                        this.invalidCompanySecondaryWorkNumber = false;
                        (document.getElementById('maskedWorkPhoneSecondary') as any).value = '';
                        (document.getElementById('maskedMobilePhoneSecondary') as any).value = '';
                    }
                    this.step2FormSubmitted = false;
                    this.form.companyContactPersonDetailsForm.$setPristine();
                }
                break;
            case 3:
                if(this.originalStepThree != undefined) {
                    if(!(this.userDetails.roleScreenRef.role.name == '4dot5Admin')) {
                        this.fourDotFiveAdminsList.forEach((element) => {
                            if(element.userId != this.originalStepThree.userId) {
                                element.ticked = false;
                            } else {
                                element.ticked = true;
                            }
                        }, this);
                        if(this.oldCheckedAdmin != null) {
                            this.oldCheckedAdmin[0].ticked = false;
                        }
                        this.companyManagement = _.cloneDeep(this.CompanyManagementReset);
                    }
                } else {
                    this.companyManagement.fourDotFiveAdmin = {};
                    this.fourDotFiveAdminsList.forEach((element) => {
                        if(element.ticked) {
                            element.ticked = false;
                        }
                    }, this);
                    this.step3FormSubmitted = false;
                }
                this.form.company4dot5AdminDetailsForm.$setPristine();
                break;
            case 4:
                if(!this.ClientOrBuUpdate && !this.ClientOrBuCreate) {
                    if(this.userDetails.company.companyType != 'Host') {
                        this.selectedPlan = _.cloneDeep(this.originalStepFour);
                    }
                }
                this.step4formsubmitted = false;
                break;
            case 5:
                if(this.ClientOrBuCreate || this.ClientOrBuUpdate) {
                    setTimeout(() => {
                        this.AssessmentWorkFlow = [];
                        this.preferences = _.cloneDeep(this.originalStepFive);
                    }, 100);
                } else {
                    setTimeout(() => {
                        this.preferences = _.cloneDeep(this.originalStepFive);
                    }, 100);
                }
                this.step5formsubmitted = false;
                break;
        }
    }

    /**
     * Deselect industry subgroup on cancel
     */
    /*      INDUSTRY CODE        this.deselectIndustrySubGroup = function () {
                    //   var matching = false;
                    industryMasterList.forEach(function (element) {
                        if (element.selected == true) {
                            element.industryDtos.forEach(function (elem) {
                                for (var key in elem) {
                                    if (_.isNumber(elem[key])) {
                                        delete elem[key];
                                    }
                                }
                                if (elem.selected == true) {
                                    elem.selected = false;
                                }
                            }, this);
                        }
                    }, this);
                }*/

    /**
     * Remove from panel
     */

    /*  INDUSTRY CODE         this.removeFromSelectedList = function (index, subList) {
                   this.industriesInPanel.splice(index, 1);
                   this.IndustrySubList.forEach(function (element) {
                       if (subList.name == element.name) {
                           element.selected = false;
                       }
                   }, this);
               }*/

    /**
     * Delete extra characters
     */
    deleteExtraCharacters(element) {
        for(var key in element) {
            if(_.isNumber(element[key])) {
                delete element[key];
            }
        }
    }

    /**
     * Industry chosen
     */
    /*   INDUSTRY CODE         this.industrySubGroupChosen = function (selectedValues) {
                    var newElementToPanel = true;
                    selectedValues.forEach(function (element) {
                        //this.deleteExtraCharacters(element);
                        if (element.selected == true) {
                            for (var index = 0; index < this.industriesInPanel.length; index++) {
                                if (element.name == this.industriesInPanel[index].name) {
                                    newElementToPanel = false;
                                    break;
                                } else {
                                    newElementToPanel = true;
                                }
                            }
                            if (newElementToPanel) {
                                this.industriesInPanel.push(element);
                            }
                        }
                    }, this);
                    //this.companyManagement.company.industryList = this.SelectedIndustrySubList;
                }*/

    hasDataChanged() {
        this.confirmationAtStep = this.getCurrentStep();
        this.showConfirmationPopup = false;
        this.cleanContactList();

        if(this.confirmationAtStep == 3 && (this.TypeOfCompany == 'Client' || this.StorageService.get('SelectedHostCompany') != null || this.TypeOfCompany == 'BusinessUnit')) {
            this.confirmationAtStep = 5;
        }
        if(!(_.isEqual(this.savedCompanyManagement.company, this.companyManagement.company)) && this.confirmationAtStep == 1) {
            this.showConfirmationPopup = true;
        } else if(this.confirmationAtStep == 2 && !(_.isEqual(this.savedCompanyManagement.contactList, this.companyManagement.contactList))) {
            this.showConfirmationPopup = true;
        } else if(!_.isUndefined(this.originalStepThree) && this.confirmationAtStep == 3) {
            if(!(!_.isUndefined(this.selectedFourDotFiveAdmin[0]))) {
                this.showConfirmationPopup = true;
            } else if(!(_.isEqual(this.originalStepThree.userId, this.selectedFourDotFiveAdmin[0].userId))) {
                this.showConfirmationPopup = true;
            }
        } else if(!_.isUndefined(this.selectedFourDotFiveAdmin[0]) && this.confirmationAtStep == 3) {
            this.showConfirmationPopup = true;
        } else if(this.showRemoveButton) {
            if(this.form.companySecondaryContactPersonDetailsForm.$dirty && this.confirmationAtStep == 2 && !this.companyStepTwoSaved && !this.isCompanyDeleted) {
                this.showConfirmationPopup = true;
            }
            // } else if (this.confirmationAtStep == 2 && !this.companyStepTwoSaved) {
            //     this.showConfirmationPopup = true;
            // }
        } else if(this.confirmationAtStep == 5 && !(_.isEqual(this.companyManagement.licensePreferences, this.originalStepFive)) && !(_.isEqual(this.originalStepFive, this.preferences))) {
            // if (!(_.isEqual(this.originalStepFive, this.savedAssessmentWorkFlow))) {
            this.showConfirmationPopup = true;
            // }
        } else if(this.confirmationAtStep == 3 && !(_.isEqual(this.companyManagement.licensePreferences, this.originalStepFive)) && this.userDetails.company.companyType != 'StaffingCompany' && (this.userDetails.company.companyType != 'Corporation') && this.StorageService.get('companyDetails') === null) {
            this.showConfirmationPopup = true;
        } else if(this.errorOcurred && (!_.isEqual(this.originalStepOne, this.companyManagement.company)) && this.confirmationAtStep == 1) {
            this.showConfirmationPopup = true;
        }
        return this.showConfirmationPopup;
    }

    /**
     * Bridges from the native company-page-step-details component (22b-4d step 2).
     */
    onCompanyDetailsFormStateChange(formState) {
        this.form.companyDetailsForm = formState;
    }

    onDetailsStepAddressFieldChange(event) {
        this.changeAddressObject(event.type, event.value);
    }

    onDetailsStepZipcodeBlur(zipcode) {
        this.getZipCodeDetails(zipcode, 'zipchanged');
    }

    /**
     * Bridges from the native company-page-step-contact component (22b-4d step 3).
     * Phone validity is now tracked by the native
     * angular-four-dot-five-phone-number component instead of the legacy
     * isPrimaryContactWTValid/isSecondaryContactWTValid/etc functions, which
     * this component's form/phone state replaces for step 2's fields.
     */
    onContactStepFormStateChange(formState) {
        this.form.companyContactPersonDetailsForm = formState.companyContactPersonDetailsForm;
        this.form.companySecondaryContactPersonDetailsForm = formState.companySecondaryContactPersonDetailsForm;
        this.form.contactForm = formState.contactForm;
        this.isValidPrimaryWorkPhoneEntered = formState.isValidPrimaryWorkPhoneEntered;
        this.isValidPrimaryMobileEntered = formState.isValidPrimaryMobileEntered;
        this.isValidSecondaryWorkPhoneEntered = formState.isValidSecondaryWorkPhoneEntered;
        this.isValidSecondaryMobileEntered = formState.isValidSecondaryMobileEntered;
    }

    /**
     * Bridges from the native company-page-step-users component (22b-4d step 4).
     */
    onUsersStepFormStateChange(formState) {
        this.form.company4dot5AdminDetailsForm = formState;
    }

    /**
     * Bridges from the native company-page-step-license-scoring component (22b-4d step 6a).
     */
    onLicenseScoringFormStateChange(formState) {
        this.licenseScoringFormValid = formState.$valid;
    }

    /**
     * Bridges from the native company-page-step-license-assessments component (22b-4d step 6a2).
     */
    onAssessmentsFormStateChange(formState) {
        this.assessmentsFormValid = formState.$valid;
    }

    /**
     * Bridges from the native company-page-step-license-jobboards component (22b-4d step 6a3).
     */
    onJobBoardsFormStateChange(formState) {
        this.jobBoardsFormValid = formState.$valid;
    }

    /**
     * Step changes
     */
    getStepChanges(stepclicked) {
        this.saveButtonClicked = false;
        this.nextButtonClicked = false;
        if(stepclicked != 3 && (this.companyManagement.company.companyType == 'Client' || this.companyManagement.company.companyType == 'BusinessUnit')) {
            this.laststep = false;
        } else if(stepclicked != 4 && (this.companyManagement.company.companyType == 'StaffingCompany' || this.companyManagement.company.companyType == 'Corporation')) {
            this.laststep = false;
        }
    }

    changeAddressObject(type, obj) {
        this.saveButtonClicked = false;
        this.nextButtonClicked = false;
        switch(type) {
            case 'city':
                for(var p = 0; p < this.geoLocationscopy.length; p++) {
                    if(this.geoLocationscopy[p].city === obj) {
                        this.companyManagement.company.addressState = this.geoLocationscopy[p].state;
                        this.companyManagement.company.country = this.geoLocationscopy[p].country;
                        this.companyManagement.company.countryCode = this.geoLocationscopy[p].countryCode;
                        this.companyManagement.company.county = this.geoLocationscopy[p].county;
                        if(!_.isUndefined(this.geoLocationscopy[p].county) && (this.geoLocationscopy[p].county !== null)) {
                            this.isCountyExists = true;
                        } else {
                            this.isCountyExists = false;
                        }
                    }
                }
                break;
            case 'state':
                for(var p = 0; p < this.geoLocationscopy.length; p++) {
                    if(this.geoLocationscopy[p].state === obj) {
                        this.companyManagement.company.city = this.geoLocationscopy[p].city;
                        this.companyManagement.company.country = this.geoLocationscopy[p].country;
                        this.companyManagement.company.countryCode = this.geoLocationscopy[p].countryCode;
                        this.companyManagement.company.county = this.geoLocationscopy[p].county;
                        if(!_.isUndefined(this.geoLocationscopy[p].county) && (this.geoLocationscopy[p].county !== null)) {
                            this.isCountyExists = true;
                        } else {
                            this.isCountyExists = false;
                        }
                    }
                }
                break;
            case 'country':
                for(var p = 0; p < this.geoLocationscopy.length; p++) {
                    if(this.geoLocationscopy[p].country === obj) {
                        this.companyManagement.company.city = this.geoLocationscopy[p].city;
                        this.companyManagement.company.addressState = this.geoLocationscopy[p].state;
                        this.companyManagement.company.county = this.geoLocationscopy[p].county;
                        if(!_.isUndefined(this.geoLocationscopy[p].county) && (this.geoLocationscopy[p].county !== null)) {
                            this.isCountyExists = true;
                        } else {
                            this.isCountyExists = false;
                        }
                    }
                }
                break;
            case 'county':
                for(var p = 0; p < this.geoLocationscopy.length; p++) {
                    if(this.geoLocationscopy[p].county === obj) {
                        this.companyManagement.company.city = this.geoLocationscopy[p].city;
                        this.companyManagement.company.addressState = this.geoLocationscopy[p].state;
                        this.companyManagement.company.country = this.geoLocationscopy[p].country;
                        this.companyManagement.company.countryCode = this.geoLocationscopy[p].countryCode;
                    }
                }
                break;
        }
    }

    complexityRestrictionChange(userType) {
        if(userType == 'user') {
            var element = (document.getElementById('checkbox-warning-user') as any);
            if($('#checkbox-group-user :checkbox:checked').length == 0 && this.companyManagement.userConfiguration.passwordSettings[0].complexityRestrictionEnabled) {
                element.innerHTML = "<span class='text-danger'>Atleast one restriction has to be selected</span>";
            } else {
                element.innerHTML = "";
            }
        } else {
            var element = (document.getElementById('checkbox-warning-administrator') as any);
            if($('#checkbox-group-administrator :checkbox:checked').length == 0 && this.companyManagement.userConfiguration.passwordSettings[1].complexityRestrictionEnabled) {
                element.innerHTML = "<span class='text-danger'>Atleast one restriction has to be selected</span>";
            } else {
                element.innerHTML = "";
            }
        }
    }

    showAddAssessmentCredentialsForm() {
        this.newAssessmentCredential = {
            assessmentType: null,
            useAccountDetails: null,
            useAccountCredDetails: null,
            userName: '',
            key: ''
        };
        this.showAddAssessmentCredentialsFormFlag = true;
        this._setHasUnsavedAssessmentCredentials();
    }

    cancelAddAssessmentKey() {
        this.showAddAssessmentCredentialsFormFlag = false;
        this.newAssessmentCredential = {};
        this._setHasUnsavedAssessmentCredentials();
    }

    addAssessmentKey() {
        let companyId = "";
        if(this.TypeOfCompany == "BusinessUnit") {
            companyId = this.clientOrBuCreationInProgress ? this.newlyCreatedCompanyId : this.companyManagement.company.id; 
        } else {
            companyId = this.userDetails.company.companyId;
        }

        var alreadyPresent = false;
        var use4dot5Account;
        var use4dot5Credential;
        if(this.TypeOfCompany == 'Host') {
            use4dot5Account = false;
            use4dot5Credential = false;
        } else {
            if(this.newAssessmentCredential.useAccountDetails == '4dot5') {
                use4dot5Account = true;
            } else {
                use4dot5Account = false;
            }
            if(this.newAssessmentCredential.useAccountCredDetails == '4dot5') {
                use4dot5Credential = true;
            } else {
                use4dot5Credential = false;
            }
        }

        if(this.validateAssessmentCredsBeforeAdd(this.newAssessmentCredential.userName, this.newAssessmentCredential.key, use4dot5Account, use4dot5Credential)) {
            if(_.isNull(this.preferences.licenseAssessmentPreferences.assessmentCredentials)) {
                this.preferences.licenseAssessmentPreferences.assessmentCredentials = [];
            }
            this.preferences.licenseAssessmentPreferences.assessmentCredentials.forEach((val, key) => {
                if(this.newAssessmentCredential.userName == val.userName && this.newAssessmentCredential.assessmentType == val.assessmentType) {
                    alreadyPresent = true;
                    this.alertsAndNotificationsService.showAlert("Duplicate", "Username already exists!", "warning");
                }
            });
            if(!alreadyPresent) {
                let credentialDetails = {
                    userName: this.newAssessmentCredential.userName,
                    use4Dot5Account: use4dot5Account,
                    use4Dot5Credential: use4dot5Credential,
                    assessmentType: this.newAssessmentCredential.assessmentType
                }
                this.companyService.isAssessmentCredentialsValid(companyId, credentialDetails, (data) => {
                    if(data.credentialAccepted) {
                        this.newAssessmentCredential.use4Dot5Account = use4dot5Account;
                        this.newAssessmentCredential.use4Dot5Credential = use4dot5Credential;
                        this.preferences.licenseAssessmentPreferences.assessmentCredentials.push(this.newAssessmentCredential);
        
                        this.showAddAssessmentCredentialsFormFlag = false;
                        this.newAssessmentCredential = {};
                        this.assessmentOrJobBoardCredentialsUpdated = true;
                        this._setHasUnsavedAssessmentCredentials();
                    } else {
                        this.alertsAndNotificationsService.showAlert("Alert!", data.description, "warning"); 
                    }
                }, error => {
                    //do nothing
                });
            } else {
                this.alertsAndNotificationsService.showAlert("All details not provided!", "Please provide all details to add credentials.", "warning");
            }
        }
    }

    showEditAssessmentPreferencesForm(assessmentCredential) {
        assessmentCredential.oldValue = _.cloneDeep(assessmentCredential);
        if(assessmentCredential.use4Dot5Account == true) {
            assessmentCredential.useAccountDetails = "4dot5";
        } else {
            assessmentCredential.useAccountDetails = "My Own";
        }
        if(assessmentCredential.use4Dot5Credential == true) {
            assessmentCredential.useAccountCredDetails = "4dot5";
        } else {
            assessmentCredential.useAccountCredDetails = "My Own";
        }
        assessmentCredential.showEditForm = true;
        this._setHasUnsavedAssessmentCredentials();
    }

    cancelEditAssessmentPreferencesForm(assessmentCredential, index) {
        this.preferences.licenseAssessmentPreferences.assessmentCredentials[index] = _.cloneDeep(assessmentCredential.oldValue);
        assessmentCredential.showEditForm = false;
        this._setHasUnsavedAssessmentCredentials();
    }

    updateEditedAssessmentPreferences(assessmentCredential, currentCredentialIndex) {
        let companyId = "";
        if(this.TypeOfCompany == "BusinessUnit") {
            companyId = this.clientOrBuCreationInProgress ? this.newlyCreatedCompanyId : this.companyManagement.company.id; 
        } else {
            companyId = this.userDetails.company.companyId;
        }
        var alreadyPresent = false;
        var use4dot5Account;
        var use4dot5Credential;
        if(this.TypeOfCompany == 'Host') {
            use4dot5Account = false;
            use4dot5Credential = false;
        } else {
            if(assessmentCredential.useAccountDetails == '4dot5') {
                use4dot5Account = true;
            } else {
                use4dot5Account = false;
            }
            if(assessmentCredential.useAccountCredDetails == '4dot5') {
                use4dot5Credential = true;
            } else {
                use4dot5Credential = false;
            }
        }

        if(this.validateAssessmentCredsBeforeAdd(assessmentCredential.userName, assessmentCredential.key, use4dot5Account, use4dot5Credential)) {
            this.preferences.licenseAssessmentPreferences.assessmentCredentials.forEach((val, key) => {
                if(key != currentCredentialIndex && assessmentCredential.userName == val.userName && assessmentCredential.assessmentType == val.assessmentType) {
                    alreadyPresent = true;
                    this.alertsAndNotificationsService.showAlert("Duplicate", "Username already exists!", "warning");
                }
            });
            if(!alreadyPresent) {
                 
                let credentialDetails = {
                    userName: assessmentCredential.userName,
                    use4Dot5Account: use4dot5Account,
                    use4Dot5Credential: use4dot5Credential,
                    assessmentType: assessmentCredential.assessmentType
                }
                this.companyService.isAssessmentCredentialsValid(companyId, credentialDetails, (data) => {
                    if(data.credentialAccepted) {
                        assessmentCredential.use4Dot5Account = use4dot5Account;
                        assessmentCredential.use4Dot5Credential = use4dot5Credential;
                        assessmentCredential.showEditForm = false;
                        this.assessmentOrJobBoardCredentialsUpdated = true;
                        this._setHasUnsavedAssessmentCredentials();
                    } else {
                        this.alertsAndNotificationsService.showAlert("Alert!", data.description, "warning"); 
                    }
                }, error => {
                    //do nothing
                });                
            }
        } else {
            this.alertsAndNotificationsService.showAlert("All details not provided!", "Please provide all details to add credentials.", "warning");
        }
    }

    isBlank(str) {
        return (!str || /^\s*$/.test(str));
    }

    validateAssessmentCredsBeforeAdd(username, key, use4dot5Account, use4dot5Credential) {
        if(use4dot5Account && use4dot5Credential && this.isBlank(username) && this.isBlank(key)) {
            return true;
        } else if(use4dot5Account && !use4dot5Credential && !this.isBlank(username) && this.isBlank(key)) {
            return true;
        } else if(!use4dot5Account && !this.isBlank(username) && !this.isBlank(key)) {
            return true;
        } else {
            return false;
        }
    }

    changeAddAssessmentCredentialAccountType() {
        if(this.newAssessmentCredential.useAccountDetails == 'My Own') {
            this.newAssessmentCredential.useAccountCredDetails = 'My Own';
            (document.getElementById("addAssessmentCredentialUseAccountCredDetails") as any).disabled = true;
        } else {
            this.newAssessmentCredential.useAccountCredDetails = '';
            (document.getElementById("addAssessmentCredentialUseAccountCredDetails") as any).disabled = false;
        }
    }

    changeAddAssessmentCredentialAccountDetails() {
        this.newAssessmentCredential.userName = '';
        this.newAssessmentCredential.key = '';
    }

    changeEditAssessmentCredentialAccountType(assessmentCredential, index) {
        if(assessmentCredential.useAccountDetails == 'My Own') {
            assessmentCredential.useAccountCredDetails = 'My Own';
            (document.getElementById("editAssessmentCredentialUseAccountCredDetails-" + index) as any).disabled = true;
            assessmentCredential.userName = '';
            assessmentCredential.key = '';
        } else {
            assessmentCredential.useAccountCredDetails = '';
            (document.getElementById("editAssessmentCredentialUseAccountCredDetails-" + index) as any).disabled = false;
            assessmentCredential.key = '';
        }
    }

    changeEditAssessmentCredentialAccountDetails(assessmentCredential) {
        assessmentCredential.userName = '';
        assessmentCredential.key = '';
    }

    _setHasUnsavedAssessmentCredentials(){
        this.hasUnsavedAssessmentCredentials = this.showAddAssessmentCredentialsFormFlag || (this.preferences.licenseAssessmentPreferences.assessmentCredentials !== null && this.preferences.licenseAssessmentPreferences.assessmentCredentials.some(credential => credential.showEditForm));
    }

    jobBoardCredentialAdded(credential) {
        if(_.isNull(this.preferences.jobBoardCredentialList)) {
            this.preferences.jobBoardCredentialList = [];
        }
        this.preferences.jobBoardCredentialList.push(credential);
        this.assessmentOrJobBoardCredentialsUpdated = true;
        this._setHasUnsavedJobBoardCredentials();
    }

    showAddJobBoardKeyForm() {
        this.showCredentialTypeConfirmModal(() => {
            this.newJobBoardCredential = {
                source: {},
                password: null,
                jobBoardId: null,
                sourceType: null,
                userName: '',
                key: null,
                loginOptions: null,
                credentialsAddedWithKey: !this.addJobBoardByPasswordFlag
            };
        });

    }

    showCredentialTypeConfirmModal(successCallback) {
        // Ported to the native BsModalService-based choose-job-board-
        // credential-type modal (22b-4d step 6c) - see
        // CompanyPageChooseJobBoardCredentialTypeModalService/
        // CompanyPageChooseJobBoardCredentialTypeModalComponent.
        this.companyPageChooseJobBoardCredentialTypeModalService.show({
            addJobBoardByPasswordFlag: this.addJobBoardByPasswordFlag
        }, callbackObject => {
            if(callbackObject.callbackType == 'typeChange') {
                this.addJobBoardByPasswordFlag = callbackObject.entity.addJobBoardByPasswordFlag;
                this.showAddJobBoardCredentialsFormFlag = true;
                this._setHasUnsavedJobBoardCredentials();
                successCallback();
            }
        });
    }

    showEditJobBoardKeyForm(jobBoardCredential) {
        this.jobSourceType.forEach((val, key) => {
            if(jobBoardCredential.sourceType == val.sourceType) {
                jobBoardCredential.source = val;
            }
        });
        jobBoardCredential.oldValue = _.cloneDeep(jobBoardCredential);
        jobBoardCredential.showEditForm = true;
        this._setHasUnsavedJobBoardCredentials();
    }

    updateEditJobBoardKeyForm(jobBoardCredential, index) {
        if((!_.isNull(jobBoardCredential.source) && !_.isUndefined(jobBoardCredential.source.id)) && (!_.isUndefined(jobBoardCredential.userName) && (jobBoardCredential.userName != '')) && ((!jobBoardCredential.credentialsAddedWithKey && !_.isNull(jobBoardCredential.password) && !_.isUndefined(jobBoardCredential.password) && jobBoardCredential.password != '') || (jobBoardCredential.credentialsAddedWithKey && !_.isNull(jobBoardCredential.key) && !_.isUndefined(jobBoardCredential.key) && jobBoardCredential.key != ''))) {
            if((jobBoardCredential.oldValue.jobBoardId != jobBoardCredential.source.id) || (jobBoardCredential.oldValue.userName != jobBoardCredential.userName) || (jobBoardCredential.oldValue.password != jobBoardCredential.password) || (jobBoardCredential.oldValue.loginOptions != jobBoardCredential.loginOptions)) {
                if(this.isJobBoardSourceDuplicate(jobBoardCredential, index)) {
                    this.alertsAndNotificationsService.showAlert("Duplicate!", "Credentials already exist for this job board.", "warning");
                } else {
                    jobBoardCredential.jobBoardId = jobBoardCredential.source.id;
                    jobBoardCredential.sourceType = jobBoardCredential.source.sourceType;
                    jobBoardCredential.edited = true;
                    jobBoardCredential.showEditForm = false;
                    delete jobBoardCredential.source;
                    this._setHasUnsavedJobBoardCredentials();
                }
            } else {
                this.alertsAndNotificationsService.showAlert("No changes made!", "Nothing to update.<br>No changes were made to the credentials.", "warning");
            }
        } else {
            this.alertsAndNotificationsService.showAlert("All details not provided!", "Please provide all details to update credentials.", "warning");
        }
    }

    deleteJobBoardDetails(jobBoardCredential) {
        //var jobBoard = this.preferences.jobBoardCredentialList[index];
        this.alertsAndNotificationsService.showConfirm("Warning!", "Are you sure you want to delete " + jobBoardCredential.sourceType + "'s credentials ?", "warning", true, () => {
            setTimeout(() => {
                if(jobBoardCredential.editable) {
                    // existing saved credentials are being deleted.
                    jobBoardCredential.delete = true;
                } else {
                    // new credentials added are being deleted. remove them from the list
                    var index = 0;
                    if(!jobBoardCredential.credentialsAddedWithKey) {
                        index = _.findIndex(this.preferences.jobBoardCredentialList, { 'userName': jobBoardCredential.userName, 'password': jobBoardCredential.password });
                    } else {
                        index = _.findIndex(this.preferences.jobBoardCredentialList, { 'userName': jobBoardCredential.userName, 'key': jobBoardCredential.key });
                    }
                    this.preferences.jobBoardCredentialList.splice(index, 1);
                }
                this._setHasUnsavedJobBoardCredentials();
            }, 100);
        }, () => {});
    }

    cancelDeleteJobBoardKey(jobBoardCredential) {
        setTimeout(() => {
            jobBoardCredential.delete = false;
        }, 100);
    }

    cancelEditJobBoardKeyForm(index) {
        setTimeout(() => {
            this.preferences.jobBoardCredentialList[index] = _.cloneDeep(this.preferences.jobBoardCredentialList[index].oldValue);
            this.preferences.jobBoardCredentialList[index].password = null;
        }, 100);
        this._setHasUnsavedJobBoardCredentials();
    }

    jobBoardCredentialsAddSourceTypeChanged(index) {
        if(!_.isNull(this.newJobBoardCredential.source)) {
            this.newJobBoardCredential.jobBoardId = this.newJobBoardCredential.source.id;
            this.newJobBoardCredential.sourceType = this.newJobBoardCredential.source.sourceType;
            if(this.isJobBoardSourceDuplicate(this.newJobBoardCredential, index)) {
                var message = this.newJobBoardCredential.source.sourceType + " Credentials already exist.";
                this.alertsAndNotificationsService.showAlert("Duplicate", message, "warning");
                this.newJobBoardCredential.jobBoardId = "";
                this.newJobBoardCredential.sourceType = "";
                this.newJobBoardCredential.source.id = "";
                this.newJobBoardCredential.source.sourceType = "";

            } else {
                this.newJobBoardCredential.jobBoardId = this.newJobBoardCredential.source.id;
                this.newJobBoardCredential.sourceType = this.newJobBoardCredential.source.sourceType;
                if(this.newJobBoardCredential.sourceType !== 'Dice') {
                    this.newJobBoardCredential.loginOptions = null;
                }
            }
        } else {
            this.newJobBoardCredential.jobBoardId = "";
            this.newJobBoardCredential.sourceType = "";
        }
        this._setHasUnsavedJobBoardCredentials();
    }

    jobBoardCredentialsEditSourceTypeChanged(jobBoardCredential) {
        if(!_.isNull(jobBoardCredential.source)) {
            jobBoardCredential.jobBoardId = jobBoardCredential.source.id;
            jobBoardCredential.sourceType = jobBoardCredential.source.sourceType;
            if(jobBoardCredential.source.sourceType !== 'Dice') {
                jobBoardCredential.loginOptions = null;
            }

        } else {
            jobBoardCredential.jobBoardId = "";
            jobBoardCredential.sourceType = "";
        }
    }

    addJobBoardKey() {
        // if(this.form.addCredentialsForm.$invalid){
        if((!_.isNull(this.newJobBoardCredential.source) && !_.isUndefined(this.newJobBoardCredential.source.id)) && (!_.isUndefined(this.newJobBoardCredential.userName) && (this.newJobBoardCredential.userName != '')) && ((!this.newJobBoardCredential.credentialsAddedWithKey && !_.isNull(this.newJobBoardCredential.password) && !_.isUndefined(this.newJobBoardCredential.password) && this.newJobBoardCredential.password != '') || (this.newJobBoardCredential.credentialsAddedWithKey && !_.isNull(this.newJobBoardCredential.key) && !_.isUndefined(this.newJobBoardCredential.key) && this.newJobBoardCredential.key != ''))) {
            if(this.isJobBoardSourceDuplicate(this.newJobBoardCredential, -1)) {
                this.alertsAndNotificationsService.showAlert("Duplicate!", "Credentials already exist for this job board.", "warning");
            } else {
                var newJobBoardCred = _.cloneDeep(this.newJobBoardCredential);
                newJobBoardCred.jobBoardId = newJobBoardCred.source.id;
                newJobBoardCred.sourceType = newJobBoardCred.source.sourceType;
                delete newJobBoardCred.source;
                if(_.isNull(this.preferences.jobBoardCredentialList)) {
                    this.preferences.jobBoardCredentialList = [];
                }
                this.preferences.jobBoardCredentialList.push(newJobBoardCred);
                this.showAddJobBoardCredentialsFormFlag = false;
                this._setHasUnsavedJobBoardCredentials();
            }
        } else {
            this.alertsAndNotificationsService.showAlert("All details not provided!", "Please provide all details to add credentials.", "warning");
        }
    }

    isJobBoardSourceDuplicate(jobBoardCredential, currentCredentialIndex) {
        var isDuplicate = false;
        if(_.isNull(this.preferences.jobBoardCredentialList)) {
            return isDuplicate;
        }
        this.preferences.jobBoardCredentialList.forEach((val, key) => {
            if(key != currentCredentialIndex && val.sourceType == jobBoardCredential.source.sourceType) {
                isDuplicate = true;
            }
        });
        return isDuplicate;
    }

    cancelAddJobBoardKey() {
        setTimeout(() => {
            this.newJobBoardCredential = {};
            this.showAddJobBoardCredentialsFormFlag = false;
            this._setHasUnsavedJobBoardCredentials();
        }, 100);
    }

    _setHasUnsavedJobBoardCredentials(){
        this.hasUnsavedJobBoardCredentials = this.showAddJobBoardCredentialsFormFlag || (this.preferences.jobBoardCredentialList !== null && this.preferences.jobBoardCredentialList.some(credential => credential.showEditForm));
    }

    toggleAssessmentWorkFlowValues1(index, workflow) {
        if(workflow.selected) {
            workflow.selected = false;
        } else {
            workflow.selected = true;
        }
    }

    imageProctoringValueChanged() {
        if(!this.preferences.licenseAssessmentPreferences.imageProctoring) {
            this.preferences.licenseAssessmentPreferences.addProctoringImagesToReport = false;
        }
    }

    checkIfSelected(optionval) {
        if(this.preferences != null) {
            if(this.preferences.licenseAssessmentPreferences.accessTime == null) {
                return false;
            }

            if(this.preferences.licenseAssessmentPreferences.accessTime.indexOf(optionval.trim()) == -1) {
                return false
            } else {

                return true;
            }
        }
    }

    checkIfDisabled(optionval) {
        if(this.preferences != null) {
            if(this.preferences.licenseAssessmentPreferences.accessTimeDisabledValues == null) {
                return false;
            }

            if(this.preferences.licenseAssessmentPreferences.accessTimeDisabledValues.indexOf(optionval.trim()) == -1) {
                return false
            } else {
                return true;
            }
        }
    }

    deleteAssessmentUsername(index) {
        this.openDeleteAssessmentCredentialsModal(this.preferences.licenseAssessmentPreferences.assessmentCredentials[index], index);
    }

    openDeleteAssessmentCredentialsModal(assessmentCredentials, index) {
        let context = "";
        let buId = "";
        if(this.TypeOfCompany == "BusinessUnit") {
            context = "BU";
            buId = this.clientOrBuCreationInProgress ? this.newlyCreatedCompanyId : this.companyManagement.company.id; 
        } else {
            context = "company";
        }
        // Ported to the native BsModalService-based delete-assessment-
        // credential modal (22b-4d step 6c) - see
        // CompanyPageDeleteAssessmentCredentialModalService/
        // CompanyPageDeleteAssessmentCredentialModalComponent, which embeds
        // the already-native <app-delete-assessment-credential> component.
        this.companyPageDeleteAssessmentCredentialModalService.show({
            companyId: this.userDetails.company.companyId,
            buId: buId,
            testOwnerEmail: assessmentCredentials.userName,
            // Not populated by the legacy `$uibModal.open` call site either (no `assessmentId`
            // resolve was ever passed here) - preserved as always-undefined, matching legacy
            // runtime behavior, while satisfying the native modal service's typed signature.
            assessmentId: undefined,
            context: context,
            isDeleteCredential: true
        }, callbackObject => {
            if(callbackObject.callbackType == 'delete') {
                this.preferences.licenseAssessmentPreferences.assessmentCredentials.splice(index, 1);
                this.assessmentOrJobBoardCredentialsUpdated = true;
            }
        });
    }

    modifyJobBoard(jobBoardCred, idVal) {
        (document.getElementById("usernamejob") as any).value = jobBoardCred.userName;
        (document.getElementById("keyjob") as any).value = jobBoardCred.key;
        this.selectedjobWorkFlowQuestion = idVal;
        for(var source in this.jobSourceType) {
            if(this.jobSourceType[source].sourceType == jobBoardCred.sourceType) {
                this.source = this.jobSourceType[source];
            }
        }

        this.buttonWorkFlowText = "Modify";
    }

    choosePlan(planIndex) {
        var isPlanModify = this.plans[planIndex].enabled;
        var planChangeStatus = "";
        this.isPlanChosen = true;
        this.selectedPlanName = this.plans[planIndex].plan;
        this.selectedPlanTypeList = _.cloneDeep(this.plans[planIndex].planTypeList);
        if(!this.isActivePlanExists) {
            this.selectedPlan = this.getParsedSelectedPlan(this.plans[planIndex], planChangeStatus);
        } else if(this.savedCurrentActivePlan.planHeaderDto.planFeaturesCapabilityId !== this.plans[planIndex].id) {
            if(this.plans[planIndex].priority > this.savedCurrentActivePlanPriority) {
                planChangeStatus = "upgrade";
            }
            else {
                planChangeStatus = "downgrade";
            }
            this.selectedPlan = this.getParsedSelectedPlan(this.plans[planIndex], planChangeStatus);

            if(isPlanModify) {
                this.selectedPlan.planHeaderDto.id = this.savedCurrentActivePlan.planHeaderDto.id;
                this.selectedPlan.planDetailDto.id = this.savedCurrentActivePlan.planDetailDto.id;
                this.selectedPlan.planDetailDto.planHeaderId = this.savedCurrentActivePlan.planDetailDto.planHeaderId;
            } else {
                this.selectedPlan.planHeaderDto.id = '';
                this.selectedPlan.planDetailDto.id = '';
                this.selectedPlan.planDetailDto.planHeaderId = '';
                this.selectedPlan.planHeaderDto.timeZoneName = '';
            }
        } else {
            this.selectedPlan = _.cloneDeep(this.savedCurrentActivePlan);
        }
        this.modifyPlan(isPlanModify);
    }

    getParsedSelectedPlan(selectedPlan, planChangeStatus) {
        if(this.newlyCreatedCompanyId == null && this.companyDetailsUpdateMode) {
            this.newlyCreatedCompanyId = this.companyManagement.company.id;
        }
        var taAllocated: any = 0;
        var jbiAllocated: any = 0;
        if(this.selectedPlanName.toLowerCase() !== 'vendor only') {
            var taAllocated = selectedPlan.features.filter((feature) => {
                return feature.name == "Tech Assessments"
            })[0].limitValue;

            var jbiAllocated = selectedPlan.features.filter((feature) => {
                return feature.name == "Job Board Integration"
            })[0].limitValue;
        }

        var planStartDate = new Date();
        var planEndDate = new Date();
        var taUsed = 0;
        var jbiUsed = 0;

        if(planChangeStatus == "upgrade" && this.selectedPlanName.toLowerCase() !== 'trial') {
            planStartDate = moment(planStartDate).add(1, 'd').toDate();
            // taUsed = this.savedCurrentActivePlan.planDetailDto.taUsed;
            // jbiUsed = this.savedCurrentActivePlan.planDetailDto.jbiUsed;
        }
        else if(planChangeStatus == "downgrade") {
            planStartDate = moment(this.savedCurrentActivePlan.planHeaderDto.unformattedEndDate).add(1, 'd').toDate();
            // taUsed = this.savedCurrentActivePlan.planDetailDto.taUsed;
            // jbiUsed = this.savedCurrentActivePlan.planDetailDto.jbiUsed;
        }

        var planType = "";
        if(this.selectedPlanName.toLowerCase() == "trial") {
            planType = "Month to Month"
            planStartDate = this.fourDotFiveDateFormatPipe.transform(planStartDate);
            planEndDate = this.fourDotFiveDateFormatPipe.transform(moment(planStartDate).add(1, 'M').toDate());
        } else if(this.selectedPlanName.toLowerCase() == "vendor only") {
            planType = "Unlimited"
            planStartDate = this.fourDotFiveDateFormatPipe.transform(new Date());
            planEndDate = this.fourDotFiveDateFormatPipe.transform(new Date(2099, 11, 31, 0, 0, 0, 0));
        } else {
            planType = "6 Months"
            planStartDate = this.fourDotFiveDateFormatPipe.transform(planStartDate);
            planEndDate = this.fourDotFiveDateFormatPipe.transform(moment(planStartDate).add(6, 'M').toDate());
        }
        return {
            "planHeaderDto": {
                "id": "",
                "planFeaturesCapabilityId": selectedPlan.id,
                "companyId": this.newlyCreatedCompanyId,
                "planType": planType,
                "planStartDate": this.dateTimeUtilityService.getDateTimeInAPIFormat(planStartDate),
                "planEndDate": this.dateTimeUtilityService.getDateTimeInAPIFormat(new Date(planEndDate)),
                "planStartDateDisplay": planStartDate,
                "planEndDateDisplay": planEndDate,
                "unformattedStartDate": moment(planStartDate).toDate(),
                "unformattedEndDate": moment(planEndDate).toDate()
            },
            "planDetailDto": {
                "id": "",
                "planHeaderId": "",
                "taAllocated": taAllocated,
                "taUsed": taUsed,
                "taAdded": 0,
                "jbiAllocated": jbiAllocated,
                "jbiUsed": 0,
                "jbiAdded": jbiUsed
            }
        }
    }

    modifyPlan(isModify) {
        // Ported to the native BsModalService-based modify-plan modal (22b-4d
        // step 5) - see CompanyPageModifyPlanModalService/
        // CompanyPageModifyPlanModalComponent, which merge the legacy
        // modifyPlan/modifyPlanModal component pair into one native modal.
        const currentPlanEndDate = this.isActivePlanExists
            ? new Date(this.savedCurrentActivePlan.planHeaderDto.planEndDateDisplay)
            : new Date();

        this.companyPageModifyPlanModalService.show({
            selectedPlanName: this.selectedPlanName,
            planTypeList: this.selectedPlanTypeList,
            selectedPlan: this.selectedPlan,
            futurePlan: this.futurePlan,
            isModifyPlan: isModify,
            isFuturePlanExists: this.isFuturePlanExists,
            isActivePlanExists: this.isActivePlanExists,
            currentPlanEndDate: currentPlanEndDate
        }, callbackObject => {
            if(!callbackObject || callbackObject.callbackType == 'cancel') {
                this.selectedPlan = _.cloneDeep(this.savedCurrentActivePlan);
            } else if(callbackObject.callbackType == 'save') {
                this.getCurrentPlanDetails(this.selectedPlan.planHeaderDto.companyId, false);
            }
            this.detectChangesIfAlive();
        });
    }

    showUnsavedCredentialsWarning(message) {
        this.alertsAndNotificationsService.showAlert("Unsaved Data!", message, "warning");
    }

    uploadCompanyLogo(isSmallHeaderLogo) {
        var file;
        if(isSmallHeaderLogo) {
            file = this.smallHeaderLogo;
            this.uploadCompanySmallHeaderLogoInProgress = true;
            this.uploadCompanyHeaderLogoInProgress = false;
        } else {
            file = this.headerLogo;
            this.uploadCompanySmallHeaderLogoInProgress = false;
            this.uploadCompanyHeaderLogoInProgress = true;
        }

        var imageInfo = new FormData();
        imageInfo.append('file', file);
        imageInfo.append('isSmallHeaderLogo', isSmallHeaderLogo);

        this.companyService.uploadCompanyLogo(imageInfo, this.userDetails.company.companyId, (data) => {
            this.uploadCompanySmallHeaderLogoInProgress = false;
            this.uploadCompanyHeaderLogoInProgress = false;
            if(!isSmallHeaderLogo) {
                this.preferences.frontPageLogoS3Url = data.imageUrl;
                this.headerLogo = undefined;
            } else {
                this.preferences.smallHeaderLogoS3Url = data.imageUrl;
                this.smallHeaderLogo = undefined;
            }

        }, (error) => {
            this.uploadCompanyHeaderLogoInProgress = false;
            this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
        });
    }

    isSuperUser(role) {
        return this.userRoleService.isSuperUser(role);
    }

    
    //Handle navigating away when data is unsaved
    uiCanExit(transitions) {
        let toState = transitions.to().name;
        let toParams = transitions.params();
        if(this.hasDataChanged() && !this.isCompanyDeleted && this.isTransitionAbortRequired && toState !== "login") {
            transitions.abort();
            this.alertsAndNotificationsService.showConfirm("Warning!", "Unsaved data on the page will be lost. Do you still want to continue?", "warning", true, () => {
                this.isTransitionAbortRequired = false;
                this.clearData();
                this.errorOcurred = false;
                if(this.StorageService.get('clientOrBuToBeUpdated') != null) {
                    this.StorageService.remove('clientOrBuToBeUpdated');
                }
                // Handling data change when clicked on Switch company
                if(this.authService.isSwitchClicked) {
                    this.authService.setIsSwitchClicked(false);
                    // this.companyService.showAllCompanies();
                    this.uibModalService.showAllCompanies();
                } else {
                    this.$state.go(toState, toParams, { reload: true });
                }
            }, () => {
                if(this.authService.isSwitchClicked) {
                    this.authService.setIsSwitchClicked(false);
                }
            });
        } else {
            // If clicked on switch company without changing data
            if(this.authService.isSwitchClicked && !this.hasDataChanged()) {
                transitions.abort();
                setTimeout(() => {
                    // this.companyService.showAllCompanies();
                    this.uibModalService.showAllCompanies();
                }, 150);
                this.authService.setIsSwitchClicked(false);
            }
        }
      }

    runCandidateScore() {
        this.alertsAndNotificationsService.showConfirm("Run Candidate Score", "Are you sure you want to run the candidate score?", "warning", true, () => {
            let requestObject = new RunCandidateScore(this.userDetails.company.companyId, this.userDetails.id);
                requestObject.rerunType = "GLOBAL";
            this.resumeService.reRunResumeScore(requestObject, (data) => {
                this.alertsAndNotificationsService.showAlertWithCallBack("Candidate scoring in Progress", data.message, "success", () => {});
            }, (error) => {
                this.alertsAndNotificationsService.showBannerMessage(error.message, 'danger');
            });
        }, () => {
            //do nothing
        });
    }

    receiveFormalizedCommentForDescription(event) {
        this.companyManagement.company.description = event;
    }

    onVoiceTranscriptForDescription(transcript) {
        if (this.companyManagement.company.description) {
            this.companyManagement.company.description += ' ' + transcript;
        } else {
            this.companyManagement.company.description = transcript;
        }
    }

    receiveFormalizedCommentForBenefits(event) {
        this.skillsBenefits = event;
    }

    onVoiceTranscriptForBenefits(transcript) {
        if (this.skillsBenefits) {
            this.skillsBenefits += ' ' + transcript;
        } else {
            this.skillsBenefits = transcript;
        }
    }
}
